// bluaIQ — M3: Quotes & Pricing Module
// RFQ generation, supplier quote reader, price comparison

import { apiFetch } from '../utils/api'
import { formatDateOnly } from '../utils/dateOnly'
import { useState, useRef, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Upload, DollarSign, Loader2, FileText, Send, CheckCircle, AlertTriangle, Plus, X, Trash2, Eye, ChevronDown, ChevronRight, Info } from 'lucide-react'
import { formatMoneyValue } from '../utils/money'
import { isDemoProject } from '../utils/demoData'
import { parseLeadTimeDays } from '../utils/materialLeadTimes'
import { compareQuoteLines, unitDisplay, type ConvUnit, type ConversionBlockCode } from '../utils/quoteUnitConversion'
import ContactLinker, { type LinkedContact } from './ContactLinker'

// ── TYPES ─────────────────────────────────────────

interface QuoteItem {
  id: string
  supplierId: string
  materialId: string
  unitPrice: number
  quoteDate: string
  validUntil?: string
  paymentTerms?: string
  deliveryTerms?: string
  leadTimeDays?: number
  supplier?: { name: string }
  material?: { name: string; unit: string; category: string }
}

// Supplier quote *document* — new entity introduced in PR #66.
// The Quotes tab is now document-first: each row in `documentsQuery`
// is one card. The legacy QuoteItem table is still readable behind a
// collapsible "Legacy quote items" section so existing data stays
// accessible but does not dominate the primary view.
interface SupplierQuoteDoc {
  id: string
  userId: string
  projectId?: string | null
  supplierId?: string | null
  supplierName: string
  quoteNumber?: string | null
  quoteDate?: string | null
  validUntil?: string | null
  subtotal?: number | null
  paymentTerms?: string | null
  deliveryTerms?: string | null
  confidence?: number | null
  originalFilename?: string | null
  storagePath?: string | null
  status: string
  createdAt: string
  updatedAt: string
  supplier?: { id: string; name: string } | null
  _count?: { lines: number }
  // Contacts Linking Foundation — durable link to a project SUPPLIER contact.
  projectContactId?: string | null
  projectContact?: LinkedContact | null
}

interface SupplierQuoteDocLine {
  id: string
  supplierQuoteId: string
  rawDescription: string
  normalizedName?: string | null
  comparisonKey: string
  quantity?: number | null
  unit?: string | null
  unitPrice: number
  totalPrice?: number | null
  brand?: string | null
  notes?: string | null
  lineOrder: number
  materialId?: string | null
  takeoffItemId?: string | null
  createdAt: string
}

interface SupplierQuoteDocDetail extends SupplierQuoteDoc {
  lines: SupplierQuoteDocLine[]
}

// Compare-Prices matrix shape (PR #68). Mirrors the server-side
// response from GET /api/projects/:id/quotes/comparison, which is
// produced by buildSupplierQuoteComparisonMatrix(). The shape stays
// stable across renders; never accept any narrower union here.
type ComparisonRowStatus = 'BEST' | 'TIE' | 'ONLY_QUOTE' | 'COMPARABLE'

interface ComparisonMatrixDoc {
  id: string
  supplierName: string
  quoteNumber: string | null
  quoteDate: string | null
  subtotal: number | null
  lineCount: number
}

interface ComparisonMatrixCell {
  supplierQuoteId: string
  supplierName: string
  unitPrice: number
  totalPrice: number | null
  quantity: number | null
  duplicateCount: number
  isBest: boolean
  isTie: boolean
}

// Lightweight family-sibling preview emitted by the matrix builder
// (PR #71 follow-up: Variant Review). The drawer renders one row
// per sibling using these fields. Other fields (cells, worstPrice,
// etc.) are intentionally NOT in the preview to keep the payload
// small.
interface ComparisonMatrixRowPreview {
  comparisonKey: string
  displayName: string
  unit: string | null
  status: ComparisonRowStatus
  bestPrice: number
  winnerLabels: string[]
  variantNames: string[]
}

interface ComparisonMatrixRow {
  comparisonKey: string
  displayName: string
  unit: string | null
  cells: ComparisonMatrixCell[]
  bestPrice: number
  worstPrice: number
  status: ComparisonRowStatus
  winnerLabels: string[]
  // PR #69 — populated server-side; old responses may omit them so
  // we default to safe values in the renderer.
  variantCount?: number
  variantNames?: string[]
  // True when this row was split out of a grouped exact bucket because
  // the user chose "Keep separate" on the grouped-variants drawer. The
  // matrix shows a "Separated by user" badge instead of "Grouped variants".
  separatedByUser?: boolean
  // True when a line in this row was marked a compatible substitute /
  // acceptable alternative — the matrix shows a "Substitute saved" badge.
  // Does NOT merge as same spec; comparable count is unchanged.
  substituteSaved?: boolean
  // NOTE: Ignore is a pure hide — the backend may still send an `ignored`
  // flag, but the FE intentionally does NOT render any badge for it.
  // PR #69 follow-up (family matching). Multiple exact-match rows
  // sharing the same `familyKey` are surfaced with a "Variants /
  // review" badge so the user can spot same-family / different-
  // spec alternatives without the matrix silently merging them.
  familyKey?: string
  familyVariantCount?: number
  // PR #71 follow-up (Variant Review). When the user clicks the
  // family badge we open a drawer that lists every sibling row in
  // the family. These two fields let us render the drawer entirely
  // from the cached matrix response — no second round-trip needed.
  familyRowKeys?: string[]
  familyRowsPreview?: ComparisonMatrixRowPreview[]
  // PR #73 follow-up: persistent SupplierQuoteLine.id values that
  // contributed to THIS runtime row (post-grouping). Use this for
  // LINE_SET override payloads instead of indexing the legacy
  // `lineIdsByComparisonKey` map by the write-time comparisonKey
  // — the two only line up when no runtime re-grouping happened.
  lineIds?: string[]
}

// Persisted user decision about a Compare-Prices comparison group
// (PR-NEW / Phase A). Stored in the QuoteComparisonOverride table.
// Matrix grouping is NOT yet altered by these in Phase A — they
// are surfaced read-back for the drawer so the user can see saved
// decisions; Phase B applies them to the matrix output.
type VariantDecisionType =
  | 'CONFIRM_SAME_PRODUCT'
  | 'USE_AS_SAME_SPEC'
  | 'KEEP_SEPARATE'
  | 'NOT_COMPARABLE'
  | 'ACCEPTABLE_ALTERNATIVE'
  | 'MARK_AS_SUBSTITUTE'
  | 'NEEDS_CONFIRMATION'
  | 'IGNORE'

type VariantDecisionSourceType = 'EXACT_GROUP' | 'FAMILY_GROUP' | 'LINE_SET'

// User-facing bilingual label for a saved decision. NEVER expose the raw
// internal code (e.g. "MARK_AS_SUBSTITUTE") in the UI.
function variantDecisionSavedLabel(decision: VariantDecisionType, isEs: boolean): string {
  const en: Record<VariantDecisionType, string> = {
    CONFIRM_SAME_PRODUCT: 'Same spec',
    USE_AS_SAME_SPEC: 'Same spec',
    KEEP_SEPARATE: 'Kept separate',
    NOT_COMPARABLE: 'Not comparable',
    ACCEPTABLE_ALTERNATIVE: 'Substitute saved',
    MARK_AS_SUBSTITUTE: 'Substitute saved',
    NEEDS_CONFIRMATION: 'Needs review',
    IGNORE: 'Ignored',
  }
  const es: Record<VariantDecisionType, string> = {
    CONFIRM_SAME_PRODUCT: 'Misma especificación',
    USE_AS_SAME_SPEC: 'Misma especificación',
    KEEP_SEPARATE: 'Mantener separado',
    NOT_COMPARABLE: 'No comparable',
    ACCEPTABLE_ALTERNATIVE: 'Sustituto guardado',
    MARK_AS_SUBSTITUTE: 'Sustituto guardado',
    NEEDS_CONFIRMATION: 'Requiere revisión',
    IGNORE: 'Ignorado',
  }
  return (isEs ? es : en)[decision]
}

interface QuoteComparisonOverride {
  id: string
  decisionType: VariantDecisionType
  sourceType: VariantDecisionSourceType
  targetKey: string | null
  canonicalKey: string | null
  lineIds: string[]
  note: string | null
  createdAt: string
  updatedAt: string
}

// Production-bug fix: non-material quote-level charges (tax,
// freight, fees, discounts, totals, deposit, payment) are
// classified server-side and routed to `excludedCharges` instead
// of into product rows. The UI renders a small secondary section
// below the matrix so accounting is visible without polluting the
// product comparison grid.
type ExcludedChargeReason =
  | 'TAX'
  | 'SHIPPING'
  | 'FEE'
  | 'DISCOUNT'
  | 'TOTAL_OR_SUBTOTAL'
  | 'NON_MATERIAL_CHARGE'

interface ExcludedCharge {
  supplierQuoteId: string
  supplierName: string
  description: string
  amount: number
  reason: ExcludedChargeReason
}

// Cross-key equivalence candidates ("Possible matches / Posibles
// equivalencias"). Suggestions only — confirming one writes a
// CONFIRM_SAME_PRODUCT override that Phase B applies to the matrix.
interface CandidateAttributeChip {
  key: string
  value: string
}
interface EquivalenceCandidateLine {
  id: string
  supplierName: string
  description: string
  unit?: string | null
  quantity?: number | null
  unitPrice?: number | null
  groupKey: string
  /** Extracted, display-ready attribute chips from the material engine. */
  attributes?: CandidateAttributeChip[]
}
// Material family + reason codes mirror the server engine
// (quoteEquivalenceCandidates.ts). Labels are resolved to EN/ES in the UI.
type MaterialFamilyCode =
  | 'LUMBER' | 'SHEATHING' | 'SIDING' | 'TRIM' | 'FLASHING' | 'TAPE_WRB'
  | 'CAULK' | 'FASTENER' | 'ELECTRICAL' | 'PLUMBING' | 'DOOR_WINDOW_HARDWARE' | 'OTHER'
type CandidateReasonCode =
  | 'SAME_FAMILY' | 'SAME_DIMENSION' | 'SAME_BRAND' | 'SAME_SIZE'
  | 'MISSING_ATTRIBUTE' | 'SPEC_DIFFERENCE' | 'UNIT_BASIS_DIFFERS'
// Construction relationship codes (mirror quoteMaterialRelationship.ts, PR-A).
type MaterialRelationshipCode =
  | 'DIRECT_EQUIVALENT' | 'COMPATIBLE_SUBSTITUTE' | 'UPGRADE'
  | 'DOWNGRADE_RISK' | 'NOT_COMPARABLE' | 'NEEDS_CONFIRMATION'
type RelationshipReasonCode =
  | 'SAME_DIMENSION' | 'SAME_SPECIES' | 'SAME_GRADE' | 'SAME_TREATMENT'
  | 'DIFFERENT_WIDTH' | 'DIFFERENT_LENGTH' | 'DIFFERENT_SPECIES' | 'DIFFERENT_GRADE'
  | 'TREATED_VS_UNTREATED' | 'ZIP_VS_OSB_LOWER_SPEC' | 'FINGER_JOINT_VARIANT'
  | 'SEALANT_TYPE_DIFFERS' | 'FASTENER_COATING_DIFFERS' | 'TAPE_WIDTH_DIFFERS'
  | 'USE_CASE_DIFFERS' | 'MISSING_SPEC'
type RiskNoteCode =
  | 'LOWER_SPEC' | 'DIFFERENT_SYSTEM' | 'TREATMENT_MISMATCH' | 'GRADE_MISMATCH'
  | 'SPECIES_MISMATCH' | 'INCOMPLETE_SPEC' | null
type RecommendationCode =
  | 'SAFE_TO_COMPARE' | 'CONFIRM_BEFORE_SUBSTITUTE' | 'VERIFY_WITH_PLANS' | 'KEEP_SEPARATE' | null
interface EquivalenceCandidate {
  candidateId: string
  signature: string
  label: string
  familyCode?: MaterialFamilyCode
  confidence: 'HIGH' | 'MEDIUM' | 'REVIEW'
  reasons?: CandidateReasonCode[]
  // Relationship layer (PR-A backend).
  relationship?: MaterialRelationshipCode
  relationshipReasons?: RelationshipReasonCode[]
  risk?: RiskNoteCode
  recommendation?: RecommendationCode
  lineIds: string[]
  lines: EquivalenceCandidateLine[]
}

interface ComparisonMatrix {
  basis: 'UPLOADED_QUOTES_ONLY'
  documents: ComparisonMatrixDoc[]
  rows: ComparisonMatrixRow[]
  // PR-NEW additions on the comparison response (additive only).
  candidates?: EquivalenceCandidate[]
  lineIdsByComparisonKey?: Record<string, string[]>
  overrides?: QuoteComparisonOverride[]
  excludedCharges?: ExcludedCharge[]
  summary: {
    documentCount: number
    productGroupCount: number
    comparableCount: number
    onlyQuoteCount: number
    tieCount: number
    excludedChargeCount?: number
  }
}

interface QuoteReadPreview {
  supplierName?: string
  quoteDate?: string
  validUntil?: string
  quoteNumber?: string
  paymentTerms?: string
  deliveryTerms?: string
  subtotal?: number
  confidence: number
  // PR-NEW: multi-page PDF batching surfaces. When the server-side
  // quote reader processes a multi-chunk PDF and some chunks fail
  // or are skipped because the internal 45s time budget ran out,
  // it returns `partial: true` and a `warnings` list. The FE then
  // renders a clearly-labeled "partial — review required" banner
  // and the save button changes copy so the user explicitly
  // acknowledges they're saving a partial quote. Undefined =
  // single-call path (image upload or PDF ≤4 pages) = complete.
  partial?: boolean
  warnings?: string[]
  pagesProcessed?: number
  pagesTotal?: number
  items: Array<{
    description: string
    quantity?: number
    unit?: string
    unitPrice: number
    totalPrice?: number
    brand?: string
    notes?: string
    matchSuggestion?: string
  }>
}

interface SupplierOption {
  id: string
  name: string
}

// ── DEMO DATA ───────────────────────────────────

const DEMO_QUOTES: QuoteItem[] = [
  { id: 'q1', supplierId: 's1', materialId: 'm1', unitPrice: 4.29, quoteDate: '2026-03-01', validUntil: '2026-04-01', paymentTerms: 'Net 30', deliveryTerms: '3-5 business days',
    supplier: { name: '84 Lumber' }, material: { name: '2x4x8 KD SPF Stud', unit: 'pc', category: 'FRAMING' } },
  { id: 'q2', supplierId: 's3', materialId: 'm1', unitPrice: 4.49, quoteDate: '2026-03-02', validUntil: '2026-03-30', paymentTerms: 'Net 15', deliveryTerms: '2-3 business days',
    supplier: { name: 'Carter Lumber' }, material: { name: '2x4x8 KD SPF Stud', unit: 'pc', category: 'FRAMING' } },
  { id: 'q3', supplierId: 's4', materialId: 'm1', unitPrice: 4.79, quoteDate: '2026-03-03', paymentTerms: 'COD', deliveryTerms: 'Same day pickup',
    supplier: { name: 'Home Depot Pro' }, material: { name: '2x4x8 KD SPF Stud', unit: 'pc', category: 'FRAMING' } },
  { id: 'q4', supplierId: 's1', materialId: 'm2', unitPrice: 14.99, quoteDate: '2026-03-01', validUntil: '2026-04-01', paymentTerms: 'Net 30',
    supplier: { name: '84 Lumber' }, material: { name: 'OSB 7/16 4x8 Sheathing', unit: 'sheet', category: 'FRAMING' } },
  { id: 'q5', supplierId: 's3', materialId: 'm2', unitPrice: 15.49, quoteDate: '2026-03-02', paymentTerms: 'Net 15',
    supplier: { name: 'Carter Lumber' }, material: { name: 'OSB 7/16 4x8 Sheathing', unit: 'sheet', category: 'FRAMING' } },
  { id: 'q6', supplierId: 's5', materialId: 'm3', unitPrice: 112.50, quoteDate: '2026-03-05', validUntil: '2026-04-05', paymentTerms: 'Net 30', deliveryTerms: 'Free delivery over $2000',
    supplier: { name: 'Beacon Roofing' }, material: { name: 'OC Duration Shingles (Onyx Black)', unit: 'sq', category: 'ROOFING' } },
  { id: 'q7', supplierId: 's2', materialId: 'm4', unitPrice: 54.99, quoteDate: '2026-03-04', paymentTerms: 'Net 30',
    supplier: { name: 'HD Supply' }, material: { name: 'R-38 Fiberglass Batt 24"', unit: '8-pack', category: 'INSULATION' } },
  { id: 'q8', supplierId: 's4', materialId: 'm4', unitPrice: 57.99, quoteDate: '2026-03-06', paymentTerms: 'COD',
    supplier: { name: 'Home Depot Pro' }, material: { name: 'R-38 Fiberglass Batt 24"', unit: '8-pack', category: 'INSULATION' } },
]

// ── PHASE 1A — Compare Prices honesty taxonomy ────────────────────
//
// Replaces the previous 4-pill render (BEST / TIE / ONLY_QUOTE /
// COMPARABLE) with a 6-label honest taxonomy that refuses to call
// a row "best" when the comparison has any structural ambiguity.
//
// Rules (from the audit task):
//   • BEST_COMPARABLE only when ALL are true:
//       - row has 2+ cells (so there's something to compare)
//       - no merged-raw-lines ambiguity (variantCount <= 1)
//       - no same-family-spec-differs ambiguity (familyVariantCount <= 1)
//       - no low-extraction-confidence source (every contributing
//         SupplierQuote has confidence >= COMPARE_PRICES_LOW_CONFIDENCE_THRESHOLD)
//       - no cross-row unit mismatch within the same familyKey
//   • NOT_COMPARABLE — same-family sibling rows have different units
//     (strongest signal: you literally can't compare across units)
//   • ONLY_AVAILABLE — exactly one cell in the row
//   • TIED — value-based tie across multiple cells
//   • NEEDS_REVIEW — comparable in principle, but at least one of the
//     ambiguity reasons fires (variant merge, family alternative,
//     low confidence)
//   • COMPARABLE — fallback for multi-cell rows with no unique winner
//     AND no ambiguity reasons (rare; mostly when all cells tie at
//     the same price)
//
// No data invention: every reason is sourced directly from the
// already-fetched matrix fields + the per-document `confidence` the
// BE persists on SupplierQuote. No new API calls, no schema changes.

export const COMPARE_PRICES_LOW_CONFIDENCE_THRESHOLD = 0.5

export type HonestRowStatus =
  | 'BEST_COMPARABLE'
  | 'ONLY_AVAILABLE'
  | 'TIED'
  | 'COMPARABLE'
  | 'NEEDS_REVIEW'
  | 'NOT_COMPARABLE'

export type HonestRowReason =
  | 'ONLY_ONE_SUPPLIER'
  | 'SAME_FAMILY_SPEC_DIFFERS'
  | 'MERGED_RAW_LINES'
  | 'UNIT_OR_SPEC_NEEDS_VERIFICATION'
  | 'LOW_EXTRACTION_CONFIDENCE'

export interface HonestStatusDecision {
  status: HonestRowStatus
  reasons: HonestRowReason[]
}

export interface HonestStatusContext {
  /** Set of SupplierQuote.id values whose `confidence` < threshold. */
  lowConfidenceDocIds: Set<string>
  /**
   * Map of familyKey → all matrix rows that share it. Used for the
   * cross-row unit-mismatch check. Rows whose familyKey is missing
   * still get their own entry (single-element array) so callers can
   * use a single Map for every row.
   */
  rowsByFamily: Map<string, ComparisonMatrixRow[]>
}

export function deriveHonestRowStatus(
  row: ComparisonMatrixRow,
  context: HonestStatusContext,
): HonestStatusDecision {
  const reasons: HonestRowReason[] = []

  if (row.cells.length === 1) {
    reasons.push('ONLY_ONE_SUPPLIER')
  }
  if ((row.familyVariantCount ?? 1) > 1) {
    reasons.push('SAME_FAMILY_SPEC_DIFFERS')
  }
  if ((row.variantCount ?? 1) > 1) {
    reasons.push('MERGED_RAW_LINES')
  }
  if (row.cells.some((c) => context.lowConfidenceDocIds.has(c.supplierQuoteId))) {
    reasons.push('LOW_EXTRACTION_CONFIDENCE')
  }

  // Cross-row unit check: a sibling row in the same familyKey with a
  // different unit means the matrix has split this product across
  // unit families. The user cannot directly compare prices across
  // those rows, so the strongest honest signal is NOT_COMPARABLE.
  const family = row.familyKey ? context.rowsByFamily.get(row.familyKey) ?? [] : []
  const hasSiblingWithDifferentUnit = family.some(
    (sibling) =>
      sibling.comparisonKey !== row.comparisonKey &&
      (sibling.unit ?? null) !== (row.unit ?? null),
  )
  if (hasSiblingWithDifferentUnit) {
    reasons.push('UNIT_OR_SPEC_NEEDS_VERIFICATION')
  }

  // Map to honest status. Order matters — strongest signal first.
  //
  // Phase 1B reorder: NEEDS_REVIEW is now checked BEFORE
  // ONLY_AVAILABLE so that a row which is both single-cell AND
  // has family/variant/low-confidence ambiguity surfaces as
  // "Needs Review" rather than "Only Available Price." The pill
  // is the user-visible signal — when both apply, ambiguity is
  // the more actionable framing (per the Phase 1B audit). The
  // ONLY_ONE_SUPPLIER reason still appears in the tooltip via
  // honestReasonCopy so the user sees both facts; only the pill
  // headline changes.
  if (reasons.includes('UNIT_OR_SPEC_NEEDS_VERIFICATION')) {
    return { status: 'NOT_COMPARABLE', reasons }
  }
  if (
    reasons.includes('SAME_FAMILY_SPEC_DIFFERS') ||
    reasons.includes('MERGED_RAW_LINES') ||
    reasons.includes('LOW_EXTRACTION_CONFIDENCE')
  ) {
    return { status: 'NEEDS_REVIEW', reasons }
  }
  if (row.status === 'ONLY_QUOTE' || row.cells.length === 1) {
    return { status: 'ONLY_AVAILABLE', reasons }
  }
  if (row.status === 'TIE') {
    return { status: 'TIED', reasons }
  }
  if (row.status === 'BEST') {
    return { status: 'BEST_COMPARABLE', reasons }
  }
  return { status: 'COMPARABLE', reasons }
}

export function honestStatusLabel(status: HonestRowStatus, isEs: boolean): string {
  switch (status) {
    case 'BEST_COMPARABLE': return isEs ? 'Mejor Precio Comparable' : 'Best Comparable Price'
    case 'ONLY_AVAILABLE':  return isEs ? 'Único Precio Disponible' : 'Only Available Price'
    case 'TIED':            return isEs ? 'Empate' : 'Tied'
    case 'COMPARABLE':      return isEs ? 'Comparable' : 'Comparable'
    case 'NEEDS_REVIEW':    return isEs ? 'Requiere Revisión' : 'Needs Review'
    case 'NOT_COMPARABLE':  return isEs ? 'No Comparable' : 'Not Comparable'
  }
}

/**
 * Phase 1B short labels — used ONLY by the desktop comparison
 * table pill where horizontal space is tight and the long honest
 * label ("Only Available Price" repeated dozens of times) becomes
 * visually noisy. The full `honestStatusLabel` is still used by
 * the tooltip and the mobile card pill (which has a full row of
 * vertical space and can afford the longer copy).
 *
 * The same `HonestRowStatus` decision drives both helpers, so a
 * short label and a long label always agree.
 */
export function honestStatusShortLabel(status: HonestRowStatus, isEs: boolean): string {
  switch (status) {
    case 'BEST_COMPARABLE': return isEs ? 'Mejor' : 'Best'
    case 'ONLY_AVAILABLE':  return isEs ? '1 cotización' : '1 quote'
    case 'TIED':            return isEs ? 'Empate' : 'Tied'
    case 'COMPARABLE':      return isEs ? 'Comparable' : 'Comparable'
    case 'NEEDS_REVIEW':    return isEs ? 'Revisar' : 'Review'
    case 'NOT_COMPARABLE':  return isEs ? 'No comparable' : 'No match'
  }
}

// Map a supplier-request-draft line reason CODE (emitted by
// materialRequestDraftBuilder) to bilingual text. Previously the builder
// stored English prose, which leaked into the Spanish draft. Unknown/legacy
// values (older drafts that stored prose) are shown verbatim as a fallback.
// Display-only unit localization (QA): in Spanish, show each-like units (ea /
// each / pc / piece / unit) as the compact "pza". Never mutates the stored unit.
export function displayUnit(unit: string | null | undefined, isEs: boolean): string {
  const raw = (unit ?? '').trim()
  if (!isEs || !raw) return raw
  const u = raw.toLowerCase().replace(/[^a-z]/g, '')
  if (['ea', 'each', 'pc', 'pcs', 'piece', 'pieces', 'unit', 'units', 'item', 'items', 'pza', 'pieza'].includes(u)) return 'pza'
  return raw
}

// Legacy drafts stored `reason` as English PROSE instead of a code. Map the
// known prose back to a code so the Spanish UI never shows raw English (QA #3).
function normalizeDraftReasonCode(code: string): string {
  const t = (code || '').trim().toLowerCase()
  if (/^only one supplier quoted/.test(t)) return 'ONLY_ONE_SUPPLIER'
  if (/tied|pick one|empat/.test(t)) return 'TIE_PICK_ONE'
  if (/same family|review variants|familia/.test(t)) return 'FAMILY_REVIEW'
  if (/kept separate|separad/.test(t)) return 'KEPT_SEPARATE'
  if (/acceptable alternative|substitute|sustituto/.test(t)) return 'ACCEPTABLE_ALTERNATIVE'
  if (/baseline but not quoted|missing from baseline/.test(t)) return 'MISSING_FROM_BASELINE'
  if (/status requires/.test(t)) return 'STATUS_REVIEW'
  return code
}

// User-facing bilingual REASON each draft line needs review. Short and clear —
// the recommended action (draftRecommendedAction) tells the user what to do.
export function draftReasonLabel(rawCode: string, isEs: boolean): string {
  const code = normalizeDraftReasonCode(rawCode)
  switch (code) {
    case 'ONLY_ONE_SUPPLIER':
      return isEs ? 'Solo un proveedor cotizó este producto' : 'Only one supplier quoted this product'
    case 'TIE_PICK_ONE':
      return isEs ? 'Varios proveedores empatados — elige uno' : 'Multiple suppliers tied — pick one'
    case 'ACCEPTABLE_ALTERNATIVE':
      return isEs ? 'Sustituto guardado' : 'Substitute saved'
    case 'FAMILY_REVIEW':
      return isEs ? 'Requiere revisión de especificación' : 'Spec review required'
    case 'KEPT_SEPARATE':
      return isEs ? 'Separado por usuario' : 'Kept separate by user'
    case 'NOT_COMPARABLE':
      return isEs ? 'No comparable' : 'Not comparable'
    case 'IGNORED':
      return isEs ? 'Ignorado' : 'Ignored'
    case 'UNIT_CONVERSION_REQUIRED':
      return isEs ? 'Requiere conversión de unidad' : 'Unit conversion required'
    case 'PACKAGE_REVIEW':
      return isEs ? 'Requiere revisión de empaque' : 'Package review required'
    case 'QUALITY_SPEC_REVIEW':
      return isEs ? 'Requiere revisión de calidad/especificación' : 'Quality/spec review required'
    case 'MISSING_FROM_BASELINE':
      return isEs ? 'En el takeoff del proyecto pero ningún proveedor lo cotizó' : 'In the project takeoff baseline but not quoted by any supplier'
    case 'STATUS_REVIEW':
      return isEs ? 'El estado requiere revisión del usuario' : 'Status requires user review'
    default:
      return code // legacy prose fallback
  }
}

// Recommended next action for a needs-review line — what the user should do.
export function draftRecommendedAction(code: string, isEs: boolean): string {
  switch (code) {
    case 'ACCEPTABLE_ALTERNATIVE':
    case 'ONLY_ONE_SUPPLIER':
      return isEs ? 'Incluir en solicitud' : 'Include in request'
    case 'TIE_PICK_ONE':
    case 'FAMILY_REVIEW':
    case 'UNIT_CONVERSION_REQUIRED':
    case 'PACKAGE_REVIEW':
    case 'QUALITY_SPEC_REVIEW':
      return isEs ? 'Revisar en Comparar precios' : 'Review in Compare Prices'
    case 'KEPT_SEPARATE':
      return isEs ? 'Mantener separado' : 'Keep separate'
    case 'NOT_COMPARABLE':
    case 'IGNORED':
      return isEs ? 'Excluir de solicitud' : 'Exclude from request'
    default:
      return isEs ? 'Revisar antes de incluir' : 'Review before including'
  }
}

export function honestReasonCopy(reason: HonestRowReason, isEs: boolean): string {
  switch (reason) {
    case 'ONLY_ONE_SUPPLIER':
      return isEs ? 'Solo un proveedor cotizó este producto' : 'Only one supplier quoted this item'
    case 'SAME_FAMILY_SPEC_DIFFERS':
      return isEs ? 'Misma familia, especificación distinta — revisar' : 'Same family, spec differs — review'
    case 'MERGED_RAW_LINES':
      return isEs ? 'Varias líneas unidas — revisar' : 'Multiple raw lines merged — review'
    case 'UNIT_OR_SPEC_NEEDS_VERIFICATION':
      return isEs ? 'Unidad/especificación requiere verificación' : 'Unit/spec needs verification'
    case 'LOW_EXTRACTION_CONFIDENCE':
      return isEs ? 'Confianza baja — verificar' : 'Low extraction confidence — verify'
  }
}

/** Pill colors for the honest status. Stored as a function so the
 *  smoke test can pin the mapping. */
export function honestStatusPillClass(status: HonestRowStatus): string {
  switch (status) {
    case 'BEST_COMPARABLE': return 'bg-[#ECFDF5] text-[#047857]'
    case 'TIED':            return 'bg-[#FEF3C7] text-[#92400E]'
    case 'NEEDS_REVIEW':    return 'bg-[#FEF3C7] text-[#92400E]'
    case 'NOT_COMPARABLE':  return 'bg-[#FEE2E2] text-[#991B1B]'
    case 'ONLY_AVAILABLE':  return 'bg-[#F3F4F6] text-[#6B7280]'
    case 'COMPARABLE':      return 'bg-[#F3F4F6] text-[#6B7280]'
  }
}

/**
 * Phase 1B — composed tooltip for the desktop status pill.
 * Format: "{full bilingual label} — {first reason copy}". If the
 * decision has no reasons (clean BEST_COMPARABLE / TIED /
 * COMPARABLE) the full label is returned alone. Mobile cards do
 * not use this composer — they already render the full label as
 * the pill and the reasons inline below the price.
 */
export function honestStatusTooltip(
  decision: HonestStatusDecision,
  isEs: boolean,
): string {
  const full = honestStatusLabel(decision.status, isEs)
  if (decision.reasons.length === 0) return full
  const firstReason = honestReasonCopy(decision.reasons[0], isEs)
  return `${full} — ${firstReason}`
}

/**
 * Phase 1B — per-cell provenance tooltip. Composes a multi-line
 * tooltip string from ALREADY-FETCHED data:
 *   • supplier name + optional quote number (line 1)
 *   • quote date if available (line 2)
 *   • source filename if available (line 3)
 *   • duplicate-line note when duplicateCount > 1 (line 4)
 *
 * No fetch, no drawer, no new endpoint. The doc lookup uses the
 * `documents` query already powering the column-header confidence
 * + missing-items badges in Phase 1A.
 *
 * Returns an empty string when no provenance fields are present
 * so callers can skip the `title=` attribute entirely (avoids
 * rendering a literal "undefined" on hover for sparse data).
 */
export function buildCellProvenanceTooltip(input: {
  supplierName: string
  duplicateCount: number
  doc: {
    quoteNumber?: string | null
    quoteDate?: string | null
    originalFilename?: string | null
  } | undefined
  isEs: boolean
}): string {
  const { supplierName, duplicateCount, doc, isEs } = input
  const lines: string[] = []

  const header = doc?.quoteNumber
    ? `${supplierName} · #${doc.quoteNumber}`
    : supplierName
  if (header) lines.push(header)

  if (doc?.quoteDate) {
    try {
      lines.push(
        formatDateOnly(doc.quoteDate, isEs ? 'es-MX' : 'en-US', {
          day: 'numeric', month: 'short', year: 'numeric',
        }),
      )
    } catch {
      // Bad date string — skip the line silently rather than
      // showing a parse-error string.
    }
  }

  if (doc?.originalFilename) {
    lines.push(
      isEs
        ? `Fuente: ${doc.originalFilename}`
        : `Source: ${doc.originalFilename}`,
    )
  }

  if (duplicateCount > 1) {
    lines.push(
      isEs
        ? `${duplicateCount} líneas originales unidas`
        : `${duplicateCount} raw lines merged`,
    )
  }

  return lines.join('\n')
}

// ── COMPONENT ─────────────────────────────────────

export default function QuotesModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  // ───────────────────────────────────────────────────────────────
  // PRODUCT RULE — demo project vs real project (read-only vs editable)
  //
  // bluaIQ supports two paths:
  //
  //   1) DEMO PROJECT (read-only exploration)
  //      - Lets a prospective user explore the product without
  //        creating a real project.
  //      - Demo data may be shown only when gated by `isDemoProject`.
  //      - Demo projects MUST NOT attempt real uploads, real saves,
  //        real quote parsing, real email sending, or any real
  //        backend mutation unless it is explicitly designed as a
  //        safe demo-only simulation.
  //      - When the user tries a real action inside a demo project,
  //        we show an honest bilingual CTA:
  //          EN: This action is available in saved projects.
  //              Create a real project to use it.
  //          ES: Esta acción está disponible en proyectos guardados.
  //              Crea un proyecto real para usarla.
  //        (The Quotes-specific copy below is the supplier-quote
  //        variant of this CTA.)
  //
  //   2) REAL PROJECT (persistent editable workflow)
  //      - Persists to Postgres via the existing `Project` model.
  //      - Can upload documents, upload supplier quotes, create
  //        contacts, create material orders, generate emails,
  //        and use Project Chat with real project data.
  //
  // Why this matters HERE:
  //   `demo-frisco-01` and `demo-mckinney-02` are FE-only constants
  //   in `src/utils/demoData.ts`. They are NEVER inserted into the
  //   database (`src/server/seed.ts` creates real projects with
  //   auto-generated CUIDs). The list query at line ~323 already
  //   falls back to DEMO_QUOTES when `isDemoProject(projectId)` is
  //   true.
  //
  //   The supplier-quote UPLOAD path used to fire
  //     POST /api/projects/${projectId}/quotes/read
  //   against the backend regardless of demo status. The backend
  //   correctly returned a 404 because the demo project does not
  //   exist in Postgres, and the FE rendered a confusing
  //     "Error al leer la cotización" / "Error 404"
  //   banner. We now gate the upload affordance AND the mutation
  //   itself behind `isDemo` so demo projects render the honest
  //   saved-project CTA instead, and the network call never fires.
  //
  //   This pattern (single `isDemo` const + gated UI + defensive
  //   mutation short-circuit) is the canonical shape for any future
  //   "real action" surface that should be inert in demo mode.
  // ───────────────────────────────────────────────────────────────
  const isDemo = isDemoProject(projectId)
  const [uploading, setUploading] = useState(false)
  const [activeTab, setActiveTab] = useState<'quotes' | 'compare' | 'rfq'>('quotes')
  const [quotePreview, setQuotePreview] = useState<QuoteReadPreview | null>(null)
  // Multi-file upload queue (#1 follow-up). Selecting several PDFs queues them;
  // each is read into a preview and must be explicitly Saved. The "N unsaved"
  // indicator counts the active preview + everything still queued so nothing is
  // silently lost on reload.
  const [fileQueue, setFileQueue] = useState<File[]>([])

  // Save-document flow (PR #67). Supplier mapping is OPTIONAL — when
  // selectedSupplierId is null we send supplierName from the AI
  // preview. There is no per-line material mapping requirement; the
  // legacy materialId path stays alive only for the collapsed legacy
  // section, not for new saves.
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null)
  const [importMessage, setImportMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)

  // Detail drawer + legacy fold state.
  const [openDocumentId, setOpenDocumentId] = useState<string | null>(null)
  const [showLegacyQuotes, setShowLegacyQuotes] = useState(false)

  // Variant Review drawer state (PR #71 follow-up). When the user
  // clicks one of the variant badges on the matrix we capture the
  // row's comparisonKey + the badge kind so the drawer can render
  // the right set of siblings. The drawer is purely read-only —
  // no decisions are persisted yet. A follow-up PR can introduce
  // user-confirmed merge/split overrides.
  const [variantReview, setVariantReview] = useState<{
    rowComparisonKey: string
    kind: 'grouped-exact' | 'family-variant'
  } | null>(null)

  const { data: apiQuotes } = useQuery<QuoteItem[]>({
    queryKey: ['quotes', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/quotes`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })
  const quotes = Array.isArray(apiQuotes) ? apiQuotes : (isDemo ? DEMO_QUOTES : [])

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      // Defensive guard: demo projects must never reach the network.
      // The UI affordance below already replaces the upload dropzone
      // with an honest saved-project notice for demo projects, but if
      // a future refactor re-exposes the input (or a test mutates the
      // mutation directly) this short-circuit ensures we never POST
      // to /api/projects/demo-…/quotes/read and never render a fake
      // "upload succeeded" state.
      if (isDemo) {
        throw new Error('DEMO_PROJECT_UPLOAD_NOT_SUPPORTED')
      }
      const token = await getToken()
      setUploading(true)
      const formData = new FormData()
      formData.append('file', file)
      const r = await apiFetch(`/api/projects/${projectId}/quotes/read`, { method: 'POST', headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }, body: formData })
      if (!r.ok) {
        // PR-NEW: parse the structured error body emitted by the
        // backend (PR for quote-upload 502). The backend now returns
        // a stable `code` we can key off in the FE error mapper —
        // the message itself stays a fallback for older deploys and
        // for bare proxy 502s (no JSON body).
        const body = await r.json().catch(() => ({} as Record<string, unknown>))
        const code = typeof (body as any).code === 'string' ? (body as any).code : null
        const requestId = typeof (body as any).requestId === 'string' ? (body as any).requestId : null
        const message = typeof (body as any).error === 'string' ? (body as any).error : `Error ${r.status}`
        const err: Error & { code?: string | null; httpStatus?: number; requestId?: string | null } = new Error(message)
        err.code = code
        err.httpStatus = r.status
        err.requestId = requestId
        throw err
      }
      const result = await r.json() as QuoteReadPreview
      if (!Array.isArray(result.items)) {
        throw new Error('AI returned unexpected format')
      }
      return result
    },
    onSuccess: (result) => {
      setQuotePreview(result)
      setUploading(false)
      setImportMessage(null)
      // Reset supplier selection so a fresh upload starts clean.
      // No per-line material mapping state to reset under the
      // document-save flow.
      setSelectedSupplierId(null)
    },
    onError: () => setUploading(false)
  })

  // Auto-advance the multi-file queue: whenever there is no active preview and
  // nothing is being read, pull the next queued file in for review. This drives
  // both the initial selection (queue all files → read the first) and the
  // post-save advance (preview cleared → read the next). A failed read drops
  // that file (surfaced by the upload error banner) and advances to the next.
  useEffect(() => {
    if (quotePreview || uploading || uploadMutation.isPending || fileQueue.length === 0) return
    const [next, ...rest] = fileQueue
    setFileQueue(rest)
    uploadMutation.mutate(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotePreview, uploading, uploadMutation.isPending, fileQueue])

  // Total quotes selected but not yet saved (active preview + still queued).
  const unsavedQuoteCount = (quotePreview ? 1 : 0) + fileQueue.length

  const detectedLeadTimeDays = parseLeadTimeDays(quotePreview?.deliveryTerms)

  // Suppliers list powers the optional supplier resolver. Note: no
  // materials query and no per-line material-mapping mutations live
  // under the document-save flow. Saving a quote document does not
  // create or require any Material rows.
  const { data: suppliers } = useQuery<SupplierOption[]>({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const token = await getToken()
      const r = await apiFetch('/api/suppliers', { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } })
      if (!r.ok) throw new Error('Error loading suppliers')
      return r.json()
    },
    retry: false,
  })

  // Pre-select supplier when the detected name matches an existing
  // row (case-insensitive). Done as an effect so it re-runs when
  // suppliers load after the preview.
  useEffect(() => {
    if (!quotePreview?.supplierName || !suppliers || selectedSupplierId) return
    const detected = quotePreview.supplierName.trim().toLowerCase()
    const match = suppliers.find((s) => s.name.trim().toLowerCase() === detected)
    if (match) setSelectedSupplierId(match.id)
  }, [quotePreview?.supplierName, suppliers, selectedSupplierId])

  const createSupplierMutation = useMutation({
    mutationFn: async (name: string) => {
      const token = await getToken()
      const r = await apiFetch('/api/suppliers', {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        throw new Error(body.error || `Error ${r.status}`)
      }
      return r.json() as Promise<SupplierOption>
    },
    onSuccess: (created) => {
      queryClient.setQueryData<SupplierOption[]>(['suppliers'], (prev) => [...(prev || []), created])
      setSelectedSupplierId(created.id)
    },
  })

  // Document list — the new primary view of the Quotes tab.
  const { data: documents } = useQuery<SupplierQuoteDoc[]>({
    queryKey: ['quote-documents', projectId],
    queryFn: async () => {
      const token = await getToken()
      const r = await apiFetch(`/api/projects/${projectId}/quotes/documents`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      if (!r.ok) throw new Error('Error loading quote documents')
      return r.json()
    },
    retry: false,
  })

  // Document detail — lazily fetched when a card is opened. Cached
  // per document id so collapsing/reopening is instant.
  const { data: openDocument } = useQuery<SupplierQuoteDocDetail>({
    queryKey: ['quote-document', openDocumentId],
    enabled: !!openDocumentId,
    queryFn: async () => {
      const token = await getToken()
      const r = await apiFetch(`/api/quotes/documents/${openDocumentId}`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      if (!r.ok) throw new Error('Error loading quote document')
      return r.json()
    },
    retry: false,
  })

  // Compare-Prices matrix (PR #68) — server-built from the project's
  // SupplierQuote / SupplierQuoteLine rows. Pure aggregation, no
  // Material / catalog dependency. The query is enabled only when
  // the Compare tab is active so we don't pre-fetch on every page
  // load.
  const { data: comparison, isLoading: comparisonLoading } = useQuery<ComparisonMatrix>({
    queryKey: ['quote-comparison', projectId],
    enabled: !!projectId && activeTab === 'compare',
    queryFn: async () => {
      const token = await getToken()
      const r = await apiFetch(`/api/projects/${projectId}/quotes/comparison`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      if (!r.ok) throw new Error('Error loading comparison')
      return r.json()
    },
    retry: false,
  })

  // Relationship-aware candidate decision (PR-B). The decision IS the persisted
  // decisionType. Only USE_AS_SAME_SPEC merges into a same-spec comparison row
  // (Phase B, via canonicalKey); MARK_AS_SUBSTITUTE / KEEP_SEPARATE /
  // NOT_COMPARABLE / NEEDS_CONFIRMATION / IGNORE persist but never merge into
  // same-spec pricing. Refreshes the comparison so Comparable (2+) recalculates.
  // No silent auto-merge; no alert().
  type CandidateDecision =
    | 'USE_AS_SAME_SPEC' | 'MARK_AS_SUBSTITUTE' | 'KEEP_SEPARATE'
    | 'NOT_COMPARABLE' | 'NEEDS_CONFIRMATION' | 'IGNORE'
  const [candidateError, setCandidateError] = useState<string | null>(null)
  // Decision-first card (QA): technical chips + advanced actions stay collapsed
  // under "View details" until the user opens them.
  const [expandedCandidates, setExpandedCandidates] = useState<Set<string>>(new Set())
  const toggleCandidateDetails = (id: string) =>
    setExpandedCandidates((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  const candidateDecision = useMutation({
    mutationFn: async ({ candidate, decision }: { candidate: EquivalenceCandidate; decision: CandidateDecision }) => {
      const token = await getToken()
      const merges = decision === 'USE_AS_SAME_SPEC'
      const res = await apiFetch(`/api/projects/${projectId}/quotes/comparison/overrides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          decisionType: decision,
          sourceType: 'LINE_SET',
          lineIds: candidate.lineIds,
          // Only a same-spec decision carries a canonical key (→ Phase B merge).
          canonicalKey: merges ? candidate.candidateId : null,
          note: null,
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    onMutate: () => setCandidateError(null),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['quote-comparison', projectId] }) },
    onError: () => setCandidateError(isEs ? 'No se pudo guardar la decisión. Inténtalo de nuevo.' : 'Could not save the decision. Please try again.'),
  })

  // Bilingual maps for the material-equivalence engine codes (family, reasons,
  // attribute keys). Kept next to the candidate UI so EN/ES stay in sync.
  const familyLabel = (code?: MaterialFamilyCode): string => {
    const en: Record<string, string> = {
      LUMBER: 'Lumber / studs / beams', SHEATHING: 'Sheathing / panels', SIDING: 'Siding',
      TRIM: 'Trim / fascia / soffit', FLASHING: 'Flashing', TAPE_WRB: 'Tape / weather barrier',
      CAULK: 'Caulk / sealant', FASTENER: 'Fasteners / connectors', ELECTRICAL: 'Electrical',
      PLUMBING: 'Plumbing', DOOR_WINDOW_HARDWARE: 'Doors / windows / hardware', OTHER: 'Other',
    }
    const es: Record<string, string> = {
      LUMBER: 'Madera / montantes / vigas', SHEATHING: 'Paneles estructurales', SIDING: 'Revestimiento',
      TRIM: 'Moldura / fascia / sofito', FLASHING: 'Tapajuntas', TAPE_WRB: 'Cinta / barrera',
      CAULK: 'Sellador', FASTENER: 'Sujetadores / conectores', ELECTRICAL: 'Eléctrico',
      PLUMBING: 'Plomería', DOOR_WINDOW_HARDWARE: 'Puertas / ventanas / herrajes', OTHER: 'Otro',
    }
    return (isEs ? es : en)[code || 'OTHER'] || (code || '')
  }
  const reasonLabel = (code: CandidateReasonCode): string => {
    const en: Record<CandidateReasonCode, string> = {
      SAME_FAMILY: 'Same material family', SAME_DIMENSION: 'Same dimensions', SAME_BRAND: 'Same brand',
      SAME_SIZE: 'Same size', MISSING_ATTRIBUTE: 'Some attributes missing',
      SPEC_DIFFERENCE: 'Possible spec / quality difference', UNIT_BASIS_DIFFERS: 'Unit basis differs',
    }
    const es: Record<CandidateReasonCode, string> = {
      SAME_FAMILY: 'Misma familia de material', SAME_DIMENSION: 'Mismas dimensiones', SAME_BRAND: 'Misma marca',
      SAME_SIZE: 'Mismo tamaño', MISSING_ATTRIBUTE: 'Faltan algunos atributos',
      SPEC_DIFFERENCE: 'Posible diferencia de especificación / calidad', UNIT_BASIS_DIFFERS: 'Difiere la unidad de medida',
    }
    return (isEs ? es : en)[code] || code
  }
  const attrKeyLabel = (key: string): string => {
    const en: Record<string, string> = {
      thickness: 'thickness', width: 'width', length: 'length', sheetSize: 'sheet', species: 'species',
      grade: 'grade', treatment: 'treatment', material: 'material', profile: 'profile', texture: 'texture',
      finish: 'finish', hzZone: 'HZ zone', brand: 'brand', type: 'type', color: 'color', size: 'size',
      rating: 'rating', gauge: 'gauge', coating: 'coating',
    }
    const es: Record<string, string> = {
      thickness: 'grosor', width: 'ancho', length: 'largo', sheetSize: 'panel', species: 'especie',
      grade: 'grado', treatment: 'tratamiento', material: 'material', profile: 'perfil', texture: 'textura',
      finish: 'acabado', hzZone: 'zona HZ', brand: 'marca', type: 'tipo', color: 'color', size: 'tamaño',
      rating: 'clasificación', gauge: 'calibre', coating: 'recubrimiento',
    }
    return (isEs ? es : en)[key] || key
  }
  const confidenceLabel = (c: EquivalenceCandidate['confidence']): string =>
    c === 'HIGH' ? (isEs ? 'Confianza alta' : 'High confidence')
      : c === 'MEDIUM' ? (isEs ? 'Confianza media' : 'Medium confidence')
        : (isEs ? 'Requiere revisión' : 'Requires review')

  // ── Construction relationship bilingual maps (PR-B) ──
  const relationshipLabel = (code?: MaterialRelationshipCode): string => {
    const en: Record<MaterialRelationshipCode, string> = {
      DIRECT_EQUIVALENT: 'Direct equivalent', COMPATIBLE_SUBSTITUTE: 'Compatible substitute',
      UPGRADE: 'Upgrade', DOWNGRADE_RISK: 'Downgrade / risk', NOT_COMPARABLE: 'Not comparable',
      NEEDS_CONFIRMATION: 'Needs contractor/plan confirmation',
    }
    const es: Record<MaterialRelationshipCode, string> = {
      DIRECT_EQUIVALENT: 'Equivalente directo', COMPATIBLE_SUBSTITUTE: 'Sustituto compatible',
      UPGRADE: 'Mejora de especificación', DOWNGRADE_RISK: 'Menor especificación / riesgo',
      NOT_COMPARABLE: 'No comparable', NEEDS_CONFIRMATION: 'Requiere confirmación del contratista o del plano',
    }
    return code ? (isEs ? es : en)[code] : ''
  }
  // "Same spec / Misma especificación" — the safe-to-compare framing for direct equivalents.
  const sameSpecLabel = (): string => (isEs ? 'Misma especificación' : 'Same spec')
  const relationshipExplainer = (code?: MaterialRelationshipCode): string => {
    const en: Record<MaterialRelationshipCode, string> = {
      DIRECT_EQUIVALENT: 'Same spec: safe to compare directly.',
      COMPATIBLE_SUBSTITUTE: 'Alternative: similar use, but confirm before substituting.',
      UPGRADE: 'Upgrade: better spec, compare carefully.',
      DOWNGRADE_RISK: 'Downgrade/risk: lower or different spec.',
      NOT_COMPARABLE: 'Not comparable: different dimension/material/use.',
      NEEDS_CONFIRMATION: 'Needs confirmation: verify with contractor, supplier, or plans.',
    }
    const es: Record<MaterialRelationshipCode, string> = {
      DIRECT_EQUIVALENT: 'Misma especificación: seguro para comparar directamente.',
      COMPATIBLE_SUBSTITUTE: 'Alternativa: uso similar, pero confirma antes de sustituir.',
      UPGRADE: 'Mejora: mejor especificación, comparar con cuidado.',
      DOWNGRADE_RISK: 'Menor especificación / riesgo: especificación inferior o diferente.',
      NOT_COMPARABLE: 'No comparable: diferente dimensión, material o uso.',
      NEEDS_CONFIRMATION: 'Requiere confirmación: verifica con contratista, proveedor o planos.',
    }
    return code ? (isEs ? es : en)[code] : ''
  }
  const relationshipReasonLabel = (code: RelationshipReasonCode): string => {
    const en: Record<RelationshipReasonCode, string> = {
      SAME_DIMENSION: 'Same dimension', SAME_SPECIES: 'Same species', SAME_GRADE: 'Same grade',
      SAME_TREATMENT: 'Same treatment', DIFFERENT_WIDTH: 'Different width', DIFFERENT_LENGTH: 'Different length',
      DIFFERENT_SPECIES: 'Different species', DIFFERENT_GRADE: 'Different grade',
      TREATED_VS_UNTREATED: 'Treated vs untreated', ZIP_VS_OSB_LOWER_SPEC: 'ZIP vs OSB lower spec',
      FINGER_JOINT_VARIANT: 'Finger-joint variant', SEALANT_TYPE_DIFFERS: 'Sealant type differs',
      FASTENER_COATING_DIFFERS: 'Fastener coating differs', TAPE_WIDTH_DIFFERS: 'Tape width differs',
      USE_CASE_DIFFERS: 'Use case differs', MISSING_SPEC: 'Missing spec',
    }
    const es: Record<RelationshipReasonCode, string> = {
      SAME_DIMENSION: 'Misma dimensión', SAME_SPECIES: 'Misma especie', SAME_GRADE: 'Mismo grado',
      SAME_TREATMENT: 'Mismo tratamiento', DIFFERENT_WIDTH: 'Diferente ancho', DIFFERENT_LENGTH: 'Diferente largo',
      DIFFERENT_SPECIES: 'Diferente especie', DIFFERENT_GRADE: 'Diferente grado',
      TREATED_VS_UNTREATED: 'Tratado vs no tratado', ZIP_VS_OSB_LOWER_SPEC: 'ZIP vs OSB de menor especificación',
      FINGER_JOINT_VARIANT: 'Variante finger-joint', SEALANT_TYPE_DIFFERS: 'Diferente tipo de sellador',
      FASTENER_COATING_DIFFERS: 'Diferente recubrimiento del sujetador', TAPE_WIDTH_DIFFERS: 'Diferente ancho de cinta',
      USE_CASE_DIFFERS: 'Diferente uso', MISSING_SPEC: 'Especificación faltante',
    }
    return (isEs ? es : en)[code] || code
  }
  const riskLabel = (code: RiskNoteCode): string => {
    if (!code) return ''
    const en: Record<string, string> = {
      LOWER_SPEC: 'Lower spec', DIFFERENT_SYSTEM: 'Different building system', TREATMENT_MISMATCH: 'Treatment mismatch',
      GRADE_MISMATCH: 'Grade mismatch', SPECIES_MISMATCH: 'Species mismatch', INCOMPLETE_SPEC: 'Incomplete spec',
    }
    const es: Record<string, string> = {
      LOWER_SPEC: 'Especificación inferior', DIFFERENT_SYSTEM: 'Sistema constructivo diferente', TREATMENT_MISMATCH: 'Tratamiento distinto',
      GRADE_MISMATCH: 'Grado distinto', SPECIES_MISMATCH: 'Especie distinta', INCOMPLETE_SPEC: 'Especificación incompleta',
    }
    return (isEs ? es : en)[code] || ''
  }
  const recommendationLabel = (code: RecommendationCode): string => {
    if (!code) return ''
    const en: Record<string, string> = {
      SAFE_TO_COMPARE: 'Safe to compare', CONFIRM_BEFORE_SUBSTITUTE: 'Confirm before substituting',
      VERIFY_WITH_PLANS: 'Verify with contractor / supplier / plans', KEEP_SEPARATE: 'Keep separate',
    }
    const es: Record<string, string> = {
      SAFE_TO_COMPARE: 'Seguro para comparar', CONFIRM_BEFORE_SUBSTITUTE: 'Confirma antes de sustituir',
      VERIFY_WITH_PLANS: 'Verifica con contratista / proveedor / planos', KEEP_SEPARATE: 'Mantener separado',
    }
    return (isEs ? es : en)[code] || ''
  }
  // Decision-action labels + the plain-language consequence of each click.
  const decisionActionLabel = (code: CandidateDecision): string => {
    const en: Record<CandidateDecision, string> = {
      USE_AS_SAME_SPEC: 'Use as same spec', MARK_AS_SUBSTITUTE: 'Mark as substitute',
      KEEP_SEPARATE: 'Keep separate', NOT_COMPARABLE: 'Not comparable',
      NEEDS_CONFIRMATION: 'Needs review', IGNORE: 'Ignore',
    }
    const es: Record<CandidateDecision, string> = {
      USE_AS_SAME_SPEC: 'Usar como misma especificación', MARK_AS_SUBSTITUTE: 'Marcar como sustituto',
      KEEP_SEPARATE: 'Mantener separado', NOT_COMPARABLE: 'No comparable',
      NEEDS_CONFIRMATION: 'Requiere revisión', IGNORE: 'Ignorar',
    }
    return (isEs ? es : en)[code]
  }
  // "Compare as direct equivalent" is the same merge action with direct-equivalent framing.
  const compareAsDirectLabel = (): string => (isEs ? 'Comparar como equivalente directo' : 'Compare as direct equivalent')
  const decisionConsequence = (code: CandidateDecision): string => {
    const en: Record<CandidateDecision, string> = {
      USE_AS_SAME_SPEC: 'groups into the same comparable row.',
      MARK_AS_SUBSTITUTE: 'saves as an alternative, but does not merge as same spec.',
      KEEP_SEPARATE: 'does not group, but keeps both products.',
      NOT_COMPARABLE: 'rejects the equivalence.',
      NEEDS_CONFIRMATION: 'saves the relationship as pending confirmation.',
      IGNORE: 'hides the suggestion without changing the comparison.',
    }
    const es: Record<CandidateDecision, string> = {
      USE_AS_SAME_SPEC: 'agrupa en la misma fila comparable.',
      MARK_AS_SUBSTITUTE: 'guarda como alternativa, pero no agrupa como misma especificación.',
      KEEP_SEPARATE: 'no agrupa, pero conserva ambos productos.',
      NOT_COMPARABLE: 'rechaza la equivalencia.',
      NEEDS_CONFIRMATION: 'guarda la relación como pendiente de confirmación.',
      IGNORE: 'oculta la sugerencia sin cambiar la comparación.',
    }
    return (isEs ? es : en)[code]
  }
  // Structured field labels for the candidate card (QA follow-up) so the user
  // sees WHY a relationship is recommended, not just a confidence pill.
  const fieldLabel = (key:
    | 'relationship' | 'confidence' | 'reasons' | 'recommendation' | 'risk' | 'specDiffs'): string => {
    const en = {
      relationship: 'Relationship', confidence: 'Confidence', reasons: 'Reasons',
      recommendation: 'Recommendation', risk: 'Risk note', specDiffs: 'Spec differences',
    }
    const es = {
      relationship: 'Relación', confidence: 'Confianza', reasons: 'Razones',
      recommendation: 'Recomendación', risk: 'Nota de riesgo', specDiffs: 'Diferencias de especificación',
    }
    return (isEs ? es : en)[key]
  }

  // ── Decision-first card helpers (QA) ──
  // Normalize a unit to a comparison basis. "each" units are directly
  // comparable; MBF / box / bundle / case / roll / LF / SF / SQ need conversion
  // before a price comparison is meaningful.
  const normalizeCardUnit = (unit?: string | null): string => {
    const u = (unit || '').toLowerCase().replace(/[^a-z]/g, '')
    if (['ea', 'each', 'pc', 'pcs', 'piece', 'unit', 'item', ''].includes(u)) return 'ea'
    return u
  }
  const isCountableUnit = (unit?: string | null): boolean => normalizeCardUnit(unit) === 'ea'
  // Quantity wording: "11 each / 11 piezas" for countable; raw unit otherwise.
  const quantityWithUnit = (qty: number, unit?: string | null): string =>
    isCountableUnit(unit) ? `${qty} ${isEs ? 'piezas' : 'each'}` : `${qty} ${(unit || '').trim()}`.trim()

  // Bilingual display of a normalized unit ("each" → "pieza" in ES).
  const unitDisplayBilingual = (u: ConvUnit): string => {
    const en = unitDisplay(u)
    if (!isEs) return en
    if (u === 'EA') return 'pieza'
    return en // MBF/LF/SF/box/case/bundle/roll/sheet read the same in ES context
  }
  const eachWord = () => (isEs ? 'pieza' : 'each')
  // Why a unit conversion is blocked — exact missing datum, bilingual.
  const conversionReasonLabel = (code: ConversionBlockCode | undefined): string => {
    const en: Record<ConversionBlockCode, string> = {
      MISSING_PRICE: 'price is missing',
      MISSING_LUMBER_DIMENSIONS: 'full dimensions are missing to convert MBF to each',
      MISSING_PACKAGE_QTY: 'package quantity is missing',
      MISSING_ROLL_LENGTH: 'roll length/coverage is missing',
      MISSING_COVERAGE: 'coverage per piece is missing',
      MISSING_PIECE_LENGTH: 'piece length is missing',
      MISSING_SHEET_DIMENSIONS: 'sheet dimensions are missing',
      UNIT_NOT_CONVERTIBLE: 'this unit cannot be converted automatically',
    }
    const es: Record<ConversionBlockCode, string> = {
      MISSING_PRICE: 'falta el precio',
      MISSING_LUMBER_DIMENSIONS: 'falta dimensión completa para convertir MBF a pieza',
      MISSING_PACKAGE_QTY: 'falta cantidad por paquete',
      MISSING_ROLL_LENGTH: 'falta largo/cobertura del rollo',
      MISSING_COVERAGE: 'falta cobertura por pieza',
      MISSING_PIECE_LENGTH: 'falta largo de pieza',
      MISSING_SHEET_DIMENSIONS: 'faltan dimensiones de la lámina',
      UNIT_NOT_CONVERTIBLE: 'no se puede convertir esta unidad automáticamente',
    }
    return (isEs ? es : en)[code || 'UNIT_NOT_CONVERTIBLE']
  }

  // Reason-specific Review badge (QA #5) — never just "Review".
  type ReviewReason = 'UNIT' | 'SPEC' | 'CONVERSION' | 'PACKAGE' | 'QUALITY'
  const reviewReasonLabel = (r: ReviewReason): string => {
    const en: Record<ReviewReason, string> = {
      UNIT: 'Review unit', SPEC: 'Review specification', CONVERSION: 'Review conversion',
      PACKAGE: 'Review package', QUALITY: 'Review quality/spec',
    }
    const es: Record<ReviewReason, string> = {
      UNIT: 'Revisar unidad', SPEC: 'Revisar especificación', CONVERSION: 'Revisar conversión',
      PACKAGE: 'Revisar empaque', QUALITY: 'Revisar calidad/spec',
    }
    return (isEs ? es : en)[r]
  }
  // Derive the specific reason a candidate needs review, or null when it's clean.
  const deriveReviewReason = (
    blocked: ConversionBlockCode | undefined,
    relation: MaterialRelationshipCode | undefined,
    unitsDiffer: boolean,
  ): ReviewReason | null => {
    if (blocked === 'MISSING_PACKAGE_QTY') return 'PACKAGE'
    if (blocked && blocked !== 'MISSING_PRICE') return 'CONVERSION'
    if (relation === 'DOWNGRADE_RISK') return 'QUALITY'
    if (relation === 'NEEDS_CONFIRMATION') return 'SPEC'
    if (unitsDiffer) return 'UNIT'
    return null
  }

  // Plain-language risk level derived from the relationship + unit comparability.
  const riskLevelLabel = (relation: MaterialRelationshipCode | undefined, unitsComparable: boolean): string => {
    if (!unitsComparable) return isEs ? 'Requiere conversión' : 'Requires conversion'
    if (relation === 'NEEDS_CONFIRMATION') return isEs ? 'Requiere confirmación de plano/contratista' : 'Requires plan/contractor confirmation'
    if (relation === 'DIRECT_EQUIVALENT') return isEs ? 'Bajo' : 'Low'
    if (relation === 'UPGRADE' || relation === 'COMPATIBLE_SUBSTITUTE') return isEs ? 'Medio' : 'Medium'
    return isEs ? 'Alto' : 'High' // DOWNGRADE_RISK / NOT_COMPARABLE
  }
  // Recommendation sentence: safe path vs caution path.
  const recommendationSentence = (relation: MaterialRelationshipCode | undefined, bestSupplier: string | null, unitsComparable: boolean): string => {
    const risky = !unitsComparable || relation === 'DOWNGRADE_RISK' || relation === 'NOT_COMPARABLE' || relation === 'NEEDS_CONFIRMATION'
    if (risky) {
      return isEs
        ? 'Recomendación: no elegir solo por precio; confirmar especificación/unidad antes de aprobar.'
        : 'Recommendation: do not choose only by price; confirm spec/unit before approving.'
    }
    const who = bestSupplier || (isEs ? 'el proveedor sugerido' : 'the suggested supplier')
    return isEs
      ? `Recomendación: usar ${who} si disponibilidad y delivery están bien.`
      : `Recommendation: use ${who} if availability and delivery are acceptable.`
  }

  // Phase 1A — Compare Prices honesty context.
  //
  // lowConfidenceDocIds is sourced from the existing `documents` query
  // (the SupplierQuote list), not from the comparison response (which
  // doesn't carry confidence). A document is "low confidence" when
  // SupplierQuote.confidence < COMPARE_PRICES_LOW_CONFIDENCE_THRESHOLD.
  //
  // rowsByFamily groups every matrix row by its `familyKey` so the
  // honest-status derivation can detect cross-row unit mismatches
  // (the only condition that escalates a row to "Not Comparable").
  const lowConfidenceDocIds = useMemo<Set<string>>(() => {
    const ids = new Set<string>()
    if (!documents) return ids
    for (const d of documents) {
      if (typeof d.confidence === 'number' && d.confidence < COMPARE_PRICES_LOW_CONFIDENCE_THRESHOLD) {
        ids.add(d.id)
      }
    }
    return ids
  }, [documents])

  const rowsByFamily = useMemo<Map<string, ComparisonMatrixRow[]>>(() => {
    const m = new Map<string, ComparisonMatrixRow[]>()
    if (!comparison) return m
    for (const row of comparison.rows) {
      const key = row.familyKey ?? row.comparisonKey
      const bucket = m.get(key) ?? []
      bucket.push(row)
      m.set(key, bucket)
    }
    return m
  }, [comparison])

  // Per-supplier missing-item rollup: how many product rows in the
  // matrix this document did NOT contribute a cell to. Used for the
  // "Missing N of M / Faltan N de M" header hint. Derived from
  // already-fetched matrix data — no new query.
  const missingCountsByDocId = useMemo<Map<string, { missing: number; total: number }>>(() => {
    const m = new Map<string, { missing: number; total: number }>()
    if (!comparison) return m
    const totalRows = comparison.rows.length
    for (const doc of comparison.documents) {
      let present = 0
      for (const row of comparison.rows) {
        if (row.cells.some((c) => c.supplierQuoteId === doc.id)) present++
      }
      m.set(doc.id, { missing: totalRows - present, total: totalRows })
    }
    return m
  }, [comparison])

  // Phase 1B — per-doc lookup keyed by SupplierQuote.id, fed by
  // the existing `documents` query. Used by buildCellProvenanceTooltip
  // to render the per-cell provenance hover (supplier · quote # ·
  // date · source filename · duplicate-merge note). No new fetch.
  const docsById = useMemo<Map<string, SupplierQuoteDoc>>(() => {
    const m = new Map<string, SupplierQuoteDoc>()
    if (!documents) return m
    for (const d of documents) m.set(d.id, d)
    return m
  }, [documents])

  // Build the document-save payload from the AI preview. Skipped
  // entries: lines without rawDescription, or with non-finite /
  // negative unitPrice. Material mapping is NOT involved — these
  // lines persist as raw quote lines, not catalog references.
  const documentSaveItems = useMemo(() => {
    if (!quotePreview) return []
    return quotePreview.items
      .map((item) => {
        const rawDescription = (item.description || '').trim()
        if (!rawDescription) return null
        const unitPrice = typeof item.unitPrice === 'number' ? item.unitPrice : NaN
        if (!Number.isFinite(unitPrice) || unitPrice < 0) return null
        return {
          rawDescription,
          normalizedName: item.matchSuggestion?.trim() || undefined,
          matchSuggestion: item.matchSuggestion?.trim() || undefined,
          quantity: typeof item.quantity === 'number' && Number.isFinite(item.quantity) ? item.quantity : undefined,
          unit: item.unit?.trim() || undefined,
          unitPrice,
          totalPrice: typeof item.totalPrice === 'number' && Number.isFinite(item.totalPrice) ? item.totalPrice : undefined,
          brand: item.brand?.trim() || undefined,
          notes: item.notes?.trim() || undefined,
          rawLine: item,
        }
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)
  }, [quotePreview])

  // Save the AI preview as a SupplierQuote document. Supplier mapping
  // is OPTIONAL — when the user has not picked an existing supplierId
  // we ship supplierName from the preview and let the server use it.
  const saveDocumentMutation = useMutation({
    mutationFn: async () => {
      if (!quotePreview) throw new Error('NO_PREVIEW')
      const supplierName = quotePreview.supplierName?.trim() || ''
      if (!selectedSupplierId && !supplierName) throw new Error('SUPPLIER_REQUIRED')
      if (documentSaveItems.length === 0) throw new Error('NO_VALID_ITEMS')
      const token = await getToken()
      const r = await apiFetch(`/api/projects/${projectId}/quotes/document`, {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplierId: selectedSupplierId || undefined,
          supplierName: supplierName || undefined,
          quoteNumber: quotePreview.quoteNumber || undefined,
          quoteDate: quotePreview.quoteDate || undefined,
          validUntil: quotePreview.validUntil || undefined,
          subtotal: typeof quotePreview.subtotal === 'number' ? quotePreview.subtotal : undefined,
          paymentTerms: quotePreview.paymentTerms || undefined,
          deliveryTerms: quotePreview.deliveryTerms || undefined,
          confidence: typeof quotePreview.confidence === 'number' ? quotePreview.confidence : undefined,
          rawAiResponse: quotePreview,
          items: documentSaveItems,
        }),
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        throw new Error(body.error || `Error ${r.status}`)
      }
      return (await r.json()) as SupplierQuoteDoc
    },
    onSuccess: (doc) => {
      // Refresh the document list + Compare-Prices matrix so a freshly saved
      // quote appears immediately (no manual reload), and keep the legacy
      // quotes query coherent for the collapsed legacy section.
      queryClient.invalidateQueries({ queryKey: ['quote-documents', projectId] })
      queryClient.invalidateQueries({ queryKey: ['quote-comparison', projectId] })
      queryClient.invalidateQueries({ queryKey: ['quotes', projectId] })
      setQuotePreview(null)
      setSelectedSupplierId(null)
      if (fileRef.current) fileRef.current.value = ''
      setImportMessage({
        kind: 'success',
        text: isEs
          ? `Cotización guardada (${doc._count?.lines ?? documentSaveItems.length} líneas).`
          : `Quote saved (${doc._count?.lines ?? documentSaveItems.length} lines).`,
      })
    },
    onError: (err: any) => {
      setImportMessage({
        kind: 'error',
        text: err instanceof Error ? err.message : (isEs ? 'Error al guardar' : 'Error saving quote'),
      })
    },
  })

  // Delete a saved supplier-quote document.
  const deleteDocumentMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken()
      const r = await apiFetch(`/api/quotes/documents/${id}`, {
        method: 'DELETE',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        throw new Error(body.error || `Error ${r.status}`)
      }
      return id
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['quote-documents', projectId] })
      if (openDocumentId === id) setOpenDocumentId(null)
      setImportMessage({
        kind: 'success',
        text: isEs ? 'Cotización eliminada.' : 'Quote deleted.',
      })
    },
    onError: (err: any) => {
      setImportMessage({
        kind: 'error',
        text: err instanceof Error ? err.message : (isEs ? 'Error al eliminar' : 'Error deleting quote'),
      })
    },
  })

  const tabs = [
    { key: 'quotes' as const, label: t('Quotes') },
    { key: 'compare' as const, label: t('Compare Prices') },
    { key: 'rfq' as const, label: t('Request Quote') },
  ]

  // PR #69 follow-up — top-level wrapper widened so the Compare-
  // Prices matrix has room to render multiple supplier columns
  // without forcing page-level horizontal scroll. Page-level
  // overflow is still avoided by the inner card's overflow-x-auto
  // + max-w-full container.
  return (
    <div className="p-5 max-w-7xl mx-auto space-y-4">
      <div>
        <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{t('Quotes & Pricing')}</h2>
        {/* Phase 1B — header count fix.
            The previous sub-line read from the legacy QuoteItem
            array (`quotes?.length`) which is mostly empty after
            PR #66 migrated the module to SupplierQuote documents.
            That produced a top-of-page "0 quotes on file" while
            the Compare Prices KPI correctly reported "7 quote
            documents". Phase 1B reads from the same SupplierQuote
            sources the rest of the page already uses:
              • documents.length (the saved SupplierQuote[] query)
              • comparison.summary.productGroupCount (matrix)
            Both are already-fetched; no new API call. Defensive
            defaults (?? 0) so an unresolved query renders 0
            instead of crashing. */}
        <p
          className="text-[11px] font-[600] text-[#9E9890] mt-0.5"
          data-testid="quotes-page-header-counts"
        >
          {(() => {
            const documentCount = documents?.length ?? comparison?.summary?.documentCount ?? 0
            // Product groups are computed lazily by the Compare-Prices matrix
            // (the comparison query is enabled only on that tab). When it has
            // not loaded yet, show a hint instead of a misleading "0 groups".
            const groupsKnown = !!comparison?.summary
            const productGroupCount = comparison?.summary?.productGroupCount ?? 0
            const docsPart = isEs ? `${documentCount} cotizaciones subidas` : `${documentCount} quote documents uploaded`
            const groupsPart = groupsKnown
              ? (isEs ? `${productGroupCount} grupos de productos detectados` : `${productGroupCount} product groups detected`)
              : (isEs ? 'Abre Comparar precios para calcular grupos de productos' : 'Open Compare Prices to calculate product groups')
            return `${docsPart} · ${groupsPart}`
          })()}
        </p>
      </div>

      {/* Import success / error banner. Lives at the module level
          (NOT inside the quotePreview panel) so it stays visible after
          a successful save clears the preview and the UI switches to
          the Compare tab. Cleared automatically on the next upload via
          uploadMutation.onSuccess. */}
      {importMessage && (
        <div
          className={`text-[11px] font-[700] rounded-lg px-3 py-2 ${
            importMessage.kind === 'success'
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {importMessage.text}
        </div>
      )}

      {/* Unsaved-quotes indicator (#1 follow-up). Counts the active preview +
          queued files so the user knows exactly how many quotes still need an
          explicit Save before they persist — nothing is silently lost on
          reload. */}
      {unsavedQuoteCount > 0 && (
        <div
          data-testid="quotes-unsaved-count"
          className="flex items-center gap-2 text-[11px] font-[700] rounded-lg px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800"
        >
          <Loader2 size={13} className={uploading ? 'animate-spin' : 'hidden'} />
          {isEs
            ? `${unsavedQuoteCount} cotización(es) sin guardar — guarda cada una para conservarla tras recargar.`
            : `${unsavedQuoteCount} unsaved quote(s) — save each one to keep it after reload.`}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-[#F2F0EB] rounded-full p-1">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 text-[13px] font-[800] py-2 rounded-full transition-colors ${activeTab === tab.key ? 'bg-white text-[#1C1A17] shadow-sm' : 'text-[#9E9890]'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Upload quote
          Demo branch: demo projects are FE-only constants and have no
          backing database row, so POST /api/projects/:id/quotes/read
          would 404. Render an honest bilingual saved-project notice
          instead of the file input — no fake success, no broken-looking
          "Error 404", no network call. The existing DEMO_QUOTES list
          rendering below stays unchanged so the demo experience still
          shows preloaded sample data.
          Real branch: unchanged from before this PR. */}
      {activeTab === 'quotes' && (
        <>
          {isDemo ? (
            <div
              className="border-2 border-dashed border-[#ECEAE4] rounded-xl p-6 text-center bg-[#FAFAF8]"
              data-testid="quotes-upload-demo-notice"
            >
              <Upload size={28} className="text-[#9E9890] mx-auto mb-2" />
              <p className="text-[12px] font-[700] text-[#1C1A17] max-w-md mx-auto leading-snug">
                {isEs
                  ? 'La carga de cotizaciones está disponible para proyectos guardados. Crea o abre un proyecto real para subir cotizaciones de proveedores.'
                  : 'Quote upload is available for saved projects. Create or open a real project to upload supplier quotes.'}
              </p>
            </div>
          ) : (
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-[#ECEAE4] rounded-xl p-6 text-center cursor-pointer hover:border-[#06539B] transition-all"
            >
              {/* Multi-file: selecting several PDFs queues them all; the
                  effect reads them one at a time for review + explicit Save. */}
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,image/*"
                multiple
                onChange={e => {
                  const files = Array.from(e.target.files || [])
                  if (files.length > 0) setFileQueue(prev => [...prev, ...files])
                }}
                className="hidden"
              />
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 size={28} className="text-[#06539B] animate-spin" />
                  <span className="text-[12px] font-[700]">
                    {fileQueue.length > 0
                      ? (isEs ? `Leyendo cotización… (${fileQueue.length} en cola)` : `AI reading quote… (${fileQueue.length} queued)`)
                      : t('AI reading quote...')}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload size={28} className="text-[#9E9890]" />
                  <span className="text-[12px] font-[700]">{t('Upload supplier quote')}</span>
                  <span className="text-[10px] font-[600] text-[#9E9890]">
                    {isEs ? 'PDF, JPG, PNG — puedes seleccionar varios' : 'PDF, JPG, PNG — select multiple'}
                  </span>
                </div>
              )}
            </div>
          )}

          {uploadMutation.isError && (() => {
            // PR-NEW: map by structured `code` from the backend
            // first, then fall back to raw-message heuristics for
            // older deploys + bare proxy 502s (no JSON body). We
            // never dump the raw AI payload or long server stacks —
            // those stay in the server logs. The bilingual copy
            // below is verbatim from the spec.
            const err = uploadMutation.error as Error & { code?: string | null; httpStatus?: number; requestId?: string | null }
            const raw = err instanceof Error ? err.message : ''
            const code: string | null = (err && typeof err.code === 'string') ? err.code : null
            const httpStatus: number | undefined = (err && typeof err.httpStatus === 'number') ? err.httpStatus : undefined
            const requestId: string | null = (err && typeof err.requestId === 'string') ? err.requestId : null

            // QUOTE_READER_TIMEOUT — backend bounded timeout or
            // bare proxy 502/504 (no body) collapses to this same
            // friendly message because semantically both mean the
            // upstream did not respond in time.
            const isTimeoutLike = code === 'QUOTE_READER_TIMEOUT'
              || (!code && httpStatus === 504)
              || (!code && httpStatus === 502)
              || (!code && /^Error 5(02|04)$/.test(raw))

            let detail: string
            if (isTimeoutLike) {
              // QA finding: the previous wording told the user to upload
              // a clearer file, which was misleading when the source PDF
              // is already perfectly readable but happens to be long /
              // multi-page. The reader times out on those files because
              // the single-call upstream-AI path doesn't batch pages (a
              // separate follow-up will introduce splitPdfIntoChunks;
              // this PR is copy-only). The new copy points the user at
              // the real remediation: retry, or split the PDF into
              // shorter files.
              detail = isEs
                ? 'El procesamiento de esta cotización tomó demasiado tiempo. Puede ser un PDF largo o con muchas páginas. Intenta de nuevo o divídelo en archivos más cortos.'
                : 'Reading this quote took too long. The file may be long or have many pages. Try again, or split it into shorter files.'
            } else if (code === 'QUOTE_READER_UPSTREAM') {
              detail = isEs
                ? 'Nuestro servicio de IA está temporalmente saturado. Intenta de nuevo en un momento.'
                : 'Our AI service is temporarily busy. Try again in a moment.'
            } else if (code === 'AI_RATE_LIMITED') {
              detail = isEs
                ? 'El servicio de IA está limitado temporalmente. Intenta de nuevo en unos minutos.'
                : 'The AI service is temporarily rate limited. Try again in a few minutes.'
            } else if (code === 'QUOTE_FILE_TOO_LARGE') {
              detail = isEs
                ? 'El archivo es demasiado grande. Sube una cotización más pequeña.'
                : 'The file is too large. Upload a smaller quote file.'
            } else if (
              code === 'QUOTE_READER_UNREADABLE' ||
              raw.includes('QUOTE_READER_UNREADABLE') ||
              raw.startsWith('Claude returned invalid quote response') ||
              raw.includes('AI returned unexpected format')
            ) {
              detail = isEs
                ? 'No se pudo leer esta cotización. Sube un PDF con texto, una imagen o un archivo más claro.'
                : 'This quote could not be read. Upload a searchable PDF, image, or clearer file.'
            } else if (code === 'AI_LIMIT_REACHED' || raw.includes('AI_LIMIT_REACHED')) {
              detail = isEs
                ? 'Límite de análisis IA del mes alcanzado.'
                : 'Monthly AI scan limit reached.'
            } else {
              // Fallback: trim any leftover technical detail to a short
              // snippet so we never render raw JSON to the user.
              detail = raw.length > 120 ? raw.slice(0, 120) + '…' : (raw || (isEs ? 'Error desconocido' : 'Unknown error'))
            }
            return (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center" data-testid="quotes-upload-error-banner">
                <AlertTriangle size={20} className="text-red-500 mx-auto mb-1" />
                <p className="text-[12px] font-[700] text-red-700">
                  {isEs ? 'Error al leer la cotización' : 'Error reading quote'}
                </p>
                <p className="text-[10px] font-[600] text-red-500 mt-1">
                  {detail}
                </p>
                {requestId && (
                  <p className="text-[9px] font-[600] text-red-400 mt-1.5" data-testid="quotes-upload-error-requestid">
                    {isEs ? 'ID de la solicitud' : 'Request ID'}: {requestId}
                  </p>
                )}
              </div>
            )
          })()}

          {quotePreview && (
            <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
              {/* PR-NEW: partial-read banner. Fires when the server-side
                  multi-chunk quote reader couldn't process every page
                  (chunk failure, time-budget exhaustion). The save flow
                  stays enabled so the user can still capture what was
                  read — but the save button copy below changes to
                  "Save partial quote" so the action is explicit. */}
              {quotePreview.partial && (
                <div
                  className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-900"
                  data-testid="quotes-preview-partial-banner"
                >
                  <p className="text-[11px] font-[800]">
                    {isEs
                      ? 'Esta cotización se leyó parcialmente. Revisa las partidas antes de guardarla.'
                      : 'This quote was read partially. Review the line items before saving.'}
                  </p>
                  {typeof quotePreview.pagesProcessed === 'number' && typeof quotePreview.pagesTotal === 'number' && (
                    <p className="text-[10px] font-[600] text-amber-800 mt-0.5">
                      {isEs
                        ? `Páginas procesadas: ${quotePreview.pagesProcessed} de ${quotePreview.pagesTotal}.`
                        : `Pages processed: ${quotePreview.pagesProcessed} of ${quotePreview.pagesTotal}.`}
                    </p>
                  )}
                </div>
              )}

              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-[13px] font-[800] text-[#1C1A17]">
                    {isEs ? 'Vista previa de cotización leída por AI' : 'AI quote preview'}
                  </h4>
                  <p className="text-[10px] font-[600] text-[#9E9890] mt-0.5">
                    {isEs
                      ? 'Esto confirma lo que se detectó antes de importarlo al sistema completo.'
                      : 'This confirms what was detected before a full import flow is added.'}
                  </p>
                </div>
                <span className={`text-[9px] font-[800] px-2 py-1 rounded-full ${
                  quotePreview.confidence >= 0.8
                    ? 'bg-emerald-50 text-emerald-700'
                    : quotePreview.confidence >= 0.5
                      ? 'bg-amber-50 text-amber-800'
                      : 'bg-red-50 text-red-700'
                }`}>
                  {isEs ? 'Confianza' : 'Confidence'} {Math.round((quotePreview.confidence || 0) * 100)}%
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
                <div className="rounded-lg border border-[#ECEAE4] bg-[#FAFAF8] p-3 space-y-1.5">
                  <div><span className="font-[800] text-[#1C1A17]">{isEs ? 'Proveedor:' : 'Supplier:'}</span> <span className="text-[#4B5563]">{quotePreview.supplierName || '—'}</span></div>
                  <div><span className="font-[800] text-[#1C1A17]">{isEs ? 'Fecha:' : 'Date:'}</span> <span className="text-[#4B5563]">{quotePreview.quoteDate || '—'}</span></div>
                  <div><span className="font-[800] text-[#1C1A17]">{isEs ? 'Válida hasta:' : 'Valid until:'}</span> <span className="text-[#4B5563]">{quotePreview.validUntil || '—'}</span></div>
                  <div><span className="font-[800] text-[#1C1A17]">{isEs ? 'Pago:' : 'Payment:'}</span> <span className="text-[#4B5563]">{quotePreview.paymentTerms || '—'}</span></div>
                </div>

                <div className="rounded-lg border border-[#ECEAE4] bg-[#FAFAF8] p-3 space-y-2">
                  <div><span className="font-[800] text-[#1C1A17]">{isEs ? 'Entrega:' : 'Delivery:'}</span> <span className="text-[#4B5563]">{quotePreview.deliveryTerms || '—'}</span></div>
                  {detectedLeadTimeDays ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-[10px] font-[700] text-emerald-800">
                      {isEs
                        ? `Lead time detectado: ${detectedLeadTimeDays} días. Esto puede usarse para recordatorios de pedido.`
                        : `Detected lead time: ${detectedLeadTimeDays} days. This can be used for order reminders.`}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-[10px] font-[700] text-amber-800">
                      {isEs
                        ? 'No se detectó un lead time claro; el sistema usaría el default de categoría.'
                        : 'No clear lead time was detected; the system would use the category default.'}
                    </div>
                  )}
                  {typeof quotePreview.subtotal === 'number' && (
                    <div><span className="font-[800] text-[#1C1A17]">{isEs ? 'Subtotal:' : 'Subtotal:'}</span> <span className="text-[#4B5563]">{formatMoneyValue(quotePreview.subtotal ?? null, isEs)}</span></div>
                  )}
                </div>
              </div>

              {/* Supplier resolution — OPTIONAL under the document-save
                  flow. When the user picks an existing supplier we send
                  supplierId; otherwise the AI's supplierName ships as
                  free text and the server stores it directly. No
                  inline "Create supplier" form here — we keep the
                  one-click "Create detected supplier" shortcut for
                  power users but never require it. */}
              <div className="rounded-lg border border-[#ECEAE4] p-3 space-y-2">
                <div className="text-[11px] font-[800] text-[#1C1A17]">
                  {isEs ? 'Proveedor (opcional)' : 'Supplier (optional)'}
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedSupplierId || ''}
                    onChange={(e) => setSelectedSupplierId(e.target.value || null)}
                    className="flex-1 border border-[#ECEAE4] rounded-lg px-2 py-1.5 text-[12px] font-[600] bg-white"
                  >
                    <option value="">
                      {quotePreview.supplierName
                        ? (isEs
                            ? `Usar nombre detectado: ${quotePreview.supplierName}`
                            : `Use detected name: ${quotePreview.supplierName}`)
                        : (isEs ? 'Sin proveedor seleccionado' : 'No supplier selected')}
                    </option>
                    {(suppliers || []).map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                {!selectedSupplierId &&
                 quotePreview.supplierName &&
                 suppliers &&
                 !suppliers.some(
                   (s) => s.name.trim().toLowerCase() === quotePreview.supplierName!.trim().toLowerCase(),
                 ) && (
                  <button
                    onClick={() => createSupplierMutation.mutate(quotePreview.supplierName!.trim())}
                    disabled={createSupplierMutation.isPending}
                    className="w-full text-left text-[11px] font-[800] bg-[#EFF6FF] hover:bg-[#DBEAFE] text-[#06539B] border border-[#BFDBFE] rounded-lg px-3 py-2 disabled:opacity-40"
                  >
                    {createSupplierMutation.isPending
                      ? (isEs ? 'Creando proveedor…' : 'Creating supplier…')
                      : (isEs
                          ? `Crear proveedor detectado: ${quotePreview.supplierName} (opcional)`
                          : `Create detected supplier: ${quotePreview.supplierName} (optional)`)}
                  </button>
                )}
                {createSupplierMutation.isError && (
                  <div className="text-[10px] font-[700] text-red-600">
                    {createSupplierMutation.error instanceof Error ? createSupplierMutation.error.message : 'Error'}
                  </div>
                )}
              </div>

              {/* Detected lines — read-only summary. No per-line
                  Include / Match-to-material / Create-material UI
                  under the document-save flow. */}
              <div className="rounded-lg border border-[#ECEAE4] overflow-hidden">
                <div className="px-3 py-2 bg-[#FAFAF8] border-b border-[#ECEAE4] text-[11px] font-[800] text-[#1C1A17] flex items-center justify-between">
                  <span>{isEs ? 'Líneas detectadas' : 'Detected lines'}</span>
                  <span className="text-[10px] font-[700] text-[#9E9890]">
                    {documentSaveItems.length}/{quotePreview.items.length}
                  </span>
                </div>
                <div className="divide-y divide-[#F2F0EB] max-h-72 overflow-y-auto">
                  {(quotePreview.items || []).map((item, idx) => (
                    <div key={`${item.description}-${idx}`} className="px-3 py-2 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-[700] text-[#1C1A17] truncate">{item.description || '—'}</div>
                        {item.matchSuggestion && (
                          <div className="text-[9px] font-[700] text-[#0F766E] mt-0.5">
                            {isEs ? 'Normalizado:' : 'Normalized:'} {item.matchSuggestion}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[11px] font-[800] text-[#1C1A17]">{formatMoneyValue(item.unitPrice ?? null, isEs)}</div>
                        {item.unit && <div className="text-[9px] font-[600] text-[#9E9890]">/{item.unit}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Save quote document — Save button enabled when:
                  - supplierName OR supplierId exists
                  - documentSaveItems.length > 0
                  No per-line catalog gates here. */}
              {(() => {
                const supplierName = quotePreview.supplierName?.trim() || ''
                const hasSupplier = !!selectedSupplierId || !!supplierName
                const readyCount = documentSaveItems.length
                return (
                  <div className="flex items-center justify-between gap-3 pt-1">
                    <div className="space-y-0.5 text-[10px] font-[700]">
                      <div className={hasSupplier ? 'text-emerald-700' : 'text-[#9E9890]'}>
                        {isEs ? 'Proveedor:' : 'Supplier:'}{' '}
                        {hasSupplier
                          ? (selectedSupplierId
                              ? (isEs ? 'seleccionado del catálogo' : 'selected from catalog')
                              : (isEs ? `usando nombre detectado: ${supplierName}` : `using detected name: ${supplierName}`))
                          : (isEs ? 'falta nombre o selección' : 'name or selection required')}
                      </div>
                      <div className={readyCount > 0 ? 'text-emerald-700' : 'text-[#9E9890]'}>
                        {isEs ? 'Líneas:' : 'Lines:'}{' '}
                        {readyCount > 0
                          ? (isEs ? `${readyCount} listas para guardar.` : `${readyCount} ready to save.`)
                          : (isEs
                              ? 'No se detectaron partidas válidas. Intenta OCR/revisión manual o ingresa las líneas manualmente.'
                              : 'No valid line items detected. Try OCR/manual review or enter lines manually.')}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {/* Skip — discard this preview without saving and advance
                          to the next queued quote (so a bad parse never blocks
                          the queue). */}
                      <button
                        onClick={() => { setQuotePreview(null); setSelectedSupplierId(null) }}
                        disabled={saveDocumentMutation.isPending}
                        className="text-[11px] font-[800] text-[#6B6660] border border-[#CECAC4] px-3 py-2 rounded-full disabled:opacity-40"
                        data-testid="quotes-skip-button"
                      >
                        {fileQueue.length > 0
                          ? (isEs ? 'Omitir' : 'Skip')
                          : (isEs ? 'Descartar' : 'Discard')}
                      </button>
                      <button
                        onClick={() => saveDocumentMutation.mutate()}
                        disabled={
                          saveDocumentMutation.isPending ||
                          !hasSupplier ||
                          readyCount === 0
                        }
                        className="flex items-center gap-2 bg-[#06539B] text-white text-[12px] font-[800] px-4 py-2 rounded-full disabled:opacity-40"
                        data-testid={quotePreview?.partial ? 'quotes-save-partial-button' : 'quotes-save-button'}
                      >
                        {saveDocumentMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                        {saveDocumentMutation.isPending
                          ? (isEs ? 'Guardando...' : 'Saving...')
                          : quotePreview?.partial
                            ? (isEs ? 'Guardar cotización parcial' : 'Save partial quote')
                            : (isEs ? 'Guardar cotización' : 'Save quote')}
                      </button>
                    </div>
                  </div>
                )
              })()}
            </div>
          )}

          {/* Financial Trust P0 (PR-F5): AI-extraction + not-connected honesty note */}
          <div
            data-testid="quotes-trust-note"
            className="flex items-start gap-2 rounded-lg border border-[#ECEAE4] bg-[#FAFAF8] px-3 py-2.5"
          >
            <Info size={13} className="text-[#9E9890] mt-0.5 shrink-0" />
            <p className="text-[10px] font-[600] text-[#6B6660] leading-relaxed">
              {isEs
                ? 'Los montos de cotizaciones son extraídos por IA de los documentos cargados. Verifica con los originales antes de usarlos. Las cotizaciones aún no están conectadas al Presupuesto ni al Calendario de Pagos.'
                : 'Quote amounts are AI-extracted from uploaded documents. Verify against originals before use. Quotes are not yet connected to Budget or Payment schedule.'}
            </p>
          </div>

          {/* Saved quote documents — primary view. One card per
              SupplierQuote row from GET /api/projects/:id/quotes/documents. */}
          <div className="space-y-2">
            <div className="text-[11px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">
              {isEs ? 'Cotizaciones guardadas' : 'Saved quote documents'}
            </div>
            {(documents || []).length === 0 ? (
              <div className="text-center py-6 rounded-xl border border-dashed border-[#ECEAE4] bg-[#FAFAF8]">
                <p className="text-[11px] font-[700] text-[#9E9890]">
                  {isEs ? 'Sube una cotización para empezar.' : 'Upload a quote to get started.'}
                </p>
              </div>
            ) : (
              (documents || []).map((doc) => {
                const expanded = openDocumentId === doc.id
                return (
                  <div key={doc.id} className="bg-white border border-[#ECEAE4] rounded-xl">
                    <div className="flex items-center gap-3 p-3">
                      <FileText size={16} className="text-[#06539B]" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-[800] text-[#1C1A17] truncate">
                          {doc.supplier?.name || doc.supplierName}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5 text-[10px] text-[#9E9890]">
                          {doc.quoteNumber && (
                            <span className="font-[700] text-[#1C1A17]">#{doc.quoteNumber}</span>
                          )}
                          {doc.quoteDate && (
                            <span>{formatDateOnly(doc.quoteDate, isEs ? 'es-MX' : 'en-US', { year: 'numeric', month: 'numeric', day: 'numeric' })}</span>
                          )}
                          <span>·</span>
                          <span>{doc._count?.lines ?? 0} {isEs ? 'líneas' : 'lines'}</span>
                          {typeof doc.subtotal === 'number' && (
                            <>
                              <span>·</span>
                              <span className="font-[700] text-[#1C1A17]">{formatMoneyValue(doc.subtotal, isEs)}</span>
                            </>
                          )}
                          <span className="text-[9px] font-[800] px-1.5 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280]">
                            {doc.status}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => setOpenDocumentId(expanded ? null : doc.id)}
                        aria-label={isEs ? 'Ver' : 'View'}
                        className="flex items-center gap-1 text-[10px] font-[800] border border-[#ECEAE4] text-[#1C1A17] px-2 py-1.5 rounded-full hover:bg-[#FAFAF8]"
                      >
                        <Eye size={12} /> {isEs ? 'Ver' : 'View'}
                      </button>
                      <button
                        onClick={() => {
                          const confirmText = isEs
                            ? `¿Eliminar cotización de ${doc.supplier?.name || doc.supplierName}?`
                            : `Delete quote from ${doc.supplier?.name || doc.supplierName}?`
                          if (window.confirm(confirmText)) deleteDocumentMutation.mutate(doc.id)
                        }}
                        aria-label={isEs ? 'Eliminar' : 'Delete'}
                        disabled={deleteDocumentMutation.isPending}
                        className="flex items-center gap-1 text-[10px] font-[800] border border-[#FECACA] text-[#B91C1C] px-2 py-1.5 rounded-full hover:bg-[#FEF2F2] disabled:opacity-40"
                      >
                        <Trash2 size={12} /> {isEs ? 'Eliminar' : 'Delete'}
                      </button>
                    </div>

                    {/* Contacts Linking Foundation — link this quote to a project
                        SUPPLIER contact (distinct from the contractor linking on
                        Bids/Contracts). Only project-scoped quotes can be linked;
                        a quote-only doc (no projectId) shows no linker. Creates no
                        contact silently and no downstream writes. */}
                    {projectId && doc.projectId && (
                      <div className="px-3 pb-3">
                        <div className="text-[10px] font-[800] uppercase tracking-wide text-[#9E9890] mb-1">
                          {isEs ? 'Contacto de proveedor' : 'Supplier contact'}
                        </div>
                        <ContactLinker
                          projectId={projectId}
                          entity="quote"
                          entityId={doc.id}
                          linkedContact={doc.projectContact ?? null}
                          roleFilter="SUPPLIER"
                          detected={{ name: doc.supplier?.name || doc.supplierName }}
                          isEs={isEs}
                          invalidateKeys={[['quote-documents', projectId]]}
                        />
                      </div>
                    )}

                    {/* Inline detail drawer — shows the document's
                        SupplierQuoteLine rows (rawDescription / qty /
                        unit / unitPrice / totalPrice / brand / notes). */}
                    {expanded && (
                      <div className="border-t border-[#ECEAE4] p-3 space-y-2 bg-[#FAFAF8]">
                        {!openDocument || openDocument.id !== doc.id ? (
                          <div className="text-[10px] font-[700] text-[#9E9890] flex items-center gap-2">
                            <Loader2 size={12} className="animate-spin" />
                            {isEs ? 'Cargando líneas…' : 'Loading lines…'}
                          </div>
                        ) : (
                          <div className="rounded-lg border border-[#ECEAE4] overflow-hidden bg-white">
                            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[#F2F0EB] text-[9px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">
                              <div className="col-span-6">{isEs ? 'Descripción' : 'Description'}</div>
                              <div className="col-span-2 text-right">{isEs ? 'Cantidad' : 'Qty'}</div>
                              <div className="col-span-2 text-right">{isEs ? 'P. unit.' : 'Unit price'}</div>
                              <div className="col-span-2 text-right">{isEs ? 'Total' : 'Total'}</div>
                            </div>
                            <div className="divide-y divide-[#F2F0EB]">
                              {openDocument.lines.map((line) => (
                                <div key={line.id} className="grid grid-cols-12 gap-2 px-3 py-2 text-[11px] items-start">
                                  <div className="col-span-6">
                                    <div className="font-[700] text-[#1C1A17]">{line.rawDescription}</div>
                                    {line.brand && (
                                      <div className="text-[9px] font-[600] text-[#9E9890]">{line.brand}</div>
                                    )}
                                    {line.notes && (
                                      <div className="text-[9px] font-[600] text-[#9E9890] mt-0.5">{line.notes}</div>
                                    )}
                                  </div>
                                  <div className="col-span-2 text-right font-[700] text-[#1C1A17]">
                                    {line.quantity ?? '—'}{line.unit ? ` ${line.unit}` : ''}
                                  </div>
                                  <div className="col-span-2 text-right font-[800] text-[#1C1A17]">
                                    {formatMoneyValue(line.unitPrice, isEs)}
                                  </div>
                                  <div className="col-span-2 text-right text-[#4B5563]">
                                    {typeof line.totalPrice === 'number' ? formatMoneyValue(line.totalPrice, isEs) : '—'}
                                  </div>
                                </div>
                              ))}
                              {openDocument.lines.length === 0 && (
                                <div className="px-3 py-3 text-[10px] font-[700] text-[#9E9890] text-center">
                                  {isEs ? 'Sin líneas.' : 'No lines.'}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>

          {/* Legacy QuoteItem rows — collapsed by default. Kept so
              existing data stays accessible but does not dominate the
              primary document-first view. */}
          {quotes && quotes.length > 0 && (
            <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
              <button
                onClick={() => setShowLegacyQuotes((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 bg-[#FAFAF8] hover:bg-[#F2F0EB] transition-colors"
              >
                <div className="flex items-center gap-2 text-[11px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">
                  {showLegacyQuotes ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  {isEs ? 'Líneas de cotización legacy' : 'Legacy quote items'}
                  <span className="text-[9px] font-[700] text-[#9E9890]">({quotes.length})</span>
                </div>
                <span className="text-[9px] font-[700] text-[#9E9890]">
                  {isEs ? 'datos previos' : 'pre-document data'}
                </span>
              </button>
              {showLegacyQuotes && (
                <div className="divide-y divide-[#F2F0EB]">
                  {quotes.map((q) => (
                    <div key={q.id} className="flex items-center gap-3 p-3">
                      <DollarSign size={14} className="text-[#22C55E]" />
                      <div className="flex-1 min-w-0">
                        <span className="text-[12px] font-[700] text-[#1C1A17]">{q.material?.name}</span>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
                          <span className="text-[10px] text-[#9E9890]">{q.supplier?.name}</span>
                          {q.leadTimeDays ? (
                            <span className="text-[9px] font-[800] text-[#0F766E] bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                              {q.leadTimeDays} {isEs ? 'días lead time' : 'days lead time'}
                            </span>
                          ) : q.deliveryTerms ? (
                            <span className="text-[9px] font-[700] text-[#9E9890]">{q.deliveryTerms}</span>
                          ) : null}
                        </div>
                      </div>
                      <span className="text-[12px] font-[800] text-[#1C1A17]">{formatMoneyValue(q.unitPrice ?? null, isEs)}/{q.material?.unit}</span>
                      <span className="text-[9px] font-[600] text-[#9E9890]">
                        {formatDateOnly(q.quoteDate, isEs ? 'es-MX' : 'en-US', { year: 'numeric', month: 'numeric', day: 'numeric' })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Price comparison — document-based matrix (PR #68).
          Rows = SupplierQuoteLine.comparisonKey groups.
          Columns = SupplierQuote documents (one column per saved
          document, so the same supplier with two quote documents
          gets two columns).
          Winner highlighting is value-based; ties and only-quote
          rows are first-class statuses. Quote-only mode is a
          permanent, first-class comparison basis — not a stopgap. */}
      {activeTab === 'compare' && (
        <div className="space-y-3">
          {/* Persistent basis label — Phase 1B copy refresh.
              Title + body now use the verbatim Phase 1B spec copy
              so the banner explicitly says "this is not a full
              project material list," consistent with the broader
              product honesty stance. Structural layout unchanged. */}
          <div
            className="rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] px-4 py-3"
            data-testid="quotes-compare-basis-banner"
          >
            <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#06539B]">
              {isEs ? 'Base de comparación' : 'Comparison basis'}
            </div>
            <p className="mt-1 text-[12px] font-[700] text-[#1C1A17]">
              {isEs ? 'Solo cotizaciones subidas.' : 'Uploaded quotes only.'}
            </p>
            <p className="mt-1 text-[10px] font-[600] text-[#4B5563]">
              {isEs
                ? 'Esta comparación usa únicamente los documentos de cotización que subiste. No representa la lista completa de materiales del proyecto.'
                : 'This comparison uses only the quote documents you uploaded. It is not a full project material list.'}
            </p>
          </div>

          {comparisonLoading && !comparison ? (
            <div className="rounded-xl border border-[#ECEAE4] bg-white px-4 py-6 text-center">
              <div className="text-[11px] font-[700] text-[#9E9890] flex items-center justify-center gap-2">
                <Loader2 size={14} className="animate-spin" />
                {isEs ? 'Cargando comparación…' : 'Loading comparison…'}
              </div>
            </div>
          ) : !comparison || comparison.documents.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#ECEAE4] bg-[#FAFAF8] px-4 py-12 text-center">
              <p className="text-[12px] font-[700] text-[#9E9890]">
                {isEs
                  ? 'Sube y guarda cotizaciones para comparar precios.'
                  : 'Upload and save quote documents to compare prices.'}
              </p>
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <SummaryCard
                  label={isEs ? 'Cotizaciones' : 'Quote documents'}
                  value={comparison.summary.documentCount}
                />
                <SummaryCard
                  label={isEs ? 'Productos' : 'Product groups'}
                  value={comparison.summary.productGroupCount}
                />
                <SummaryCard
                  label={isEs ? 'Comparables (2+)' : 'Comparable (2+)'}
                  value={comparison.summary.comparableCount}
                />
                <SummaryCard
                  label={isEs ? 'Única cotización' : 'Only quote'}
                  value={comparison.summary.onlyQuoteCount}
                />
                <SummaryCard
                  label={isEs ? 'Empates' : 'Ties'}
                  value={comparison.summary.tieCount}
                />
              </div>

              {/* Possible matches / Posibles equivalencias — cross-key
                  equivalence candidates the user can confirm. Suggestions
                  only: confirming re-groups the matrix (Phase B) and bumps
                  Comparable (2+); rejecting just records the decision. */}
              {Array.isArray(comparison.candidates) && comparison.candidates.length > 0 && (
                <div data-testid="equivalence-candidates" className="rounded-xl border border-[#D8E6F4] bg-[#F2F7FC] p-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-[12px] font-[800] text-[#06539B]">
                      {isEs ? 'Posibles equivalencias' : 'Possible matches'}
                    </span>
                    <span className="text-[10px] font-[600] text-[#6B7280]">
                      {isEs
                        ? 'NATAMI clasifica si son la misma especificación, sustituto, mejora, riesgo o no comparable. Confirma solo cuando quieras aplicar esa relación.'
                        : 'NATAMI classifies whether these are same spec, substitute, upgrade, risk, or not comparable. Confirm only when you want to apply that relationship.'}
                    </span>
                  </div>
                  {candidateError && (
                    <div data-testid="candidate-error" className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[11px] font-[700] text-red-800">
                      {candidateError}
                    </div>
                  )}
                  {comparison.candidates.map((cand) => {
                    const pending = candidateDecision.isPending && candidateDecision.variables?.candidate.candidateId === cand.candidateId
                    // Confidence badge color: HIGH green, MEDIUM blue, REVIEW amber.
                    const conf = cand.confidence
                    const confClass = conf === 'HIGH'
                      ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                      : conf === 'MEDIUM'
                        ? 'text-[#06539B] bg-[#EAF2FB] border-[#CFE0F3]'
                        : 'text-amber-700 bg-amber-50 border-amber-200'
                    // Only a HIGH exact equivalence offers a one-tap confirm as
                    // the primary action; MEDIUM/REVIEW lead with review framing.
                    return (
                      (() => {
                        const relation = cand.relationship
                        const isDirect = relation === 'DIRECT_EQUIVALENT'
                        const isNotComparable = relation === 'NOT_COMPARABLE'
                        const isNeedsConf = relation === 'NEEDS_CONFIRMATION'
                        const relChip =
                          isDirect ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                            : relation === 'UPGRADE' ? 'text-[#06539B] bg-[#EAF2FB] border-[#CFE0F3]'
                              : relation === 'COMPATIBLE_SUBSTITUTE' ? 'text-indigo-700 bg-indigo-50 border-indigo-200'
                                : relation === 'DOWNGRADE_RISK' ? 'text-red-700 bg-red-50 border-red-200'
                                  : relation === 'NEEDS_CONFIRMATION' ? 'text-amber-700 bg-amber-50 border-amber-200'
                                    : 'text-[#6B6660] bg-[#F2F0EB] border-[#E5E2DC]'
                        const relReasons = cand.relationshipReasons ?? []
                        const baseReasons = cand.reasons ?? []
                        // Decision-first computation. The conversion engine
                        // normalizes every priced line to a comparable per-each
                        // price (MBF→each, box/case/bundle→each, …) so savings
                        // rank on a like-for-like basis. Source data is NEVER
                        // mutated — converted prices are for comparison only.
                        const cmp = compareQuoteLines(cand.lines)
                        const cmpBest = cmp.best
                        const comparedAgainst = cmp.comparedAgainst
                        const allComparable = cmp.allComparable
                        const savingsPerEach = cmp.savingsPerEach
                        const materialName = cmpBest?.description || cand.lines[0]?.description || familyLabel(cand.familyCode)
                        const bestQty = cmpBest?.quantity ?? null
                        const bestLineTotal = bestQty != null && cmpBest?.originalUnitPrice != null ? bestQty * cmpBest.originalUnitPrice : null
                        const unitsLabel = cmp.originalUnits.map(unitDisplayBilingual).join(isEs ? ' vs ' : ' vs ')
                        const isExpanded = expandedCandidates.has(cand.candidateId)
                        // Accept recommendation (QA #6): only DIRECT merges. DOWNGRADE_RISK
                        // and NEEDS_CONFIRMATION persist as review; substitute/upgrade as
                        // alternative; not-comparable rejects. None but DIRECT merge.
                        const primaryDecision: CandidateDecision = isDirect ? 'USE_AS_SAME_SPEC'
                          : isNotComparable ? 'NOT_COMPARABLE'
                          : (isNeedsConf || relation === 'DOWNGRADE_RISK') ? 'NEEDS_CONFIRMATION'
                          : 'MARK_AS_SUBSTITUTE'
                        const reviewReason = deriveReviewReason(cmp.blockedReasonCode, relation, cmp.originalUnits.length > 1)
                        // Executive guidance (QA #2/#3) for 3+ comparable options.
                        const showExecSummary = cmp.comparableCount >= 3 && cmp.bestByPrice && cmp.bestBySpec && cmp.bestOverallValue
                        return (
                      <div key={cand.candidateId} data-testid="candidate-card" className="rounded-lg border border-[#ECEAE4] bg-white p-3 space-y-2">
                        {/* 1. Normalized material name. */}
                        <div data-testid="candidate-material-name" className="text-[13px] font-[900] text-[#1C1A17] truncate">{materialName}</div>
                        {/* Status chips (visual). */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {relation && (
                            <span data-testid="candidate-relationship" className={`text-[9px] font-[800] uppercase tracking-[0.5px] border rounded-full px-2 py-0.5 ${relChip}`}>
                              {isDirect ? sameSpecLabel() : relationshipLabel(relation)}
                            </span>
                          )}
                          <span data-testid="candidate-confidence" className={`text-[9px] font-[800] uppercase tracking-[0.5px] border rounded-full px-2 py-0.5 ${confClass}`}>
                            {confidenceLabel(conf)}
                          </span>
                          {reviewReason && (
                            <span data-testid="candidate-review-reason" title={conversionReasonLabel(cmp.blockedReasonCode)} className="text-[9px] font-[800] uppercase tracking-[0.5px] border border-amber-200 bg-amber-50 text-amber-700 rounded-full px-2 py-0.5">
                              {reviewReasonLabel(reviewReason)}
                            </span>
                          )}
                        </div>
                        {/* 5/6. Explicit labeled Quality/spec status + Risk level. */}
                        <div className="space-y-0.5 text-[9.5px] font-[600] text-[#4B5563]">
                          <div data-testid="candidate-quality-status">
                            <span className="font-[800] text-[#1C1A17]">{isEs ? 'Estado de especificación' : 'Quality/spec status'}:</span>{' '}
                            {isDirect ? sameSpecLabel() : (relation ? relationshipLabel(relation) : (isEs ? 'Sin clasificar' : 'Unclassified'))}
                          </div>
                          <div data-testid="candidate-risk-level">
                            <span className="font-[800] text-[#1C1A17]">{isEs ? 'Nivel de riesgo' : 'Risk level'}:</span>{' '}{riskLevelLabel(relation, allComparable)}
                          </div>
                        </div>
                        {/* 2. Suggested best option (uses the comparable price). */}
                        {cmpBest && (
                          <div data-testid="candidate-best-option" className="rounded-md bg-[#F2F7FC] border border-[#D8E6F4] px-2.5 py-1.5">
                            <div className="text-[9px] font-[800] uppercase tracking-[0.4px] text-[#06539B]">{isEs ? 'Mejor opción recomendada' : 'Suggested best option'}</div>
                            <div className="text-[11px] font-[800] text-[#1C1A17]">
                              {cmpBest.supplierName}
                              {cmpBest.comparableUnitPrice != null
                                ? ` · $${cmpBest.comparableUnitPrice.toFixed(2)}/${eachWord()}`
                                : cmpBest.originalUnitPrice != null ? ` · $${cmpBest.originalUnitPrice.toFixed(2)}/${cmpBest.originalUnitLabel}` : ''}
                            </div>
                            <div className="text-[9px] font-[600] text-[#6B7280]">
                              {isDirect
                                ? (isEs ? 'Misma especificación, precio comparable más bajo.' : 'Same spec, lowest comparable price.')
                                : (isEs ? 'Precio comparable más bajo entre proveedores.' : 'Lowest comparable price among suppliers.')}
                            </div>
                          </div>
                        )}
                        {/* Executive guidance when there are 3+ comparable options (QA #3). */}
                        {showExecSummary && (
                          <div data-testid="candidate-exec-summary" className="rounded-md bg-[#F7FAFF] border border-[#E1ECF7] px-2.5 py-1.5 text-[9.5px] font-[600] text-[#4B5563] space-y-0.5">
                            <div className="text-[9px] font-[800] uppercase tracking-[0.4px] text-[#06539B]">{isEs ? 'Resumen' : 'Summary'}</div>
                            <div><span className="font-[800] text-[#1C1A17]">{isEs ? 'Mejor por precio' : 'Best by price'}:</span> {cmp.bestByPrice!.supplierName}{cmp.bestByPrice!.comparableUnitPrice != null ? ` · $${cmp.bestByPrice!.comparableUnitPrice.toFixed(2)}/${eachWord()}` : ''}</div>
                            <div><span className="font-[800] text-[#1C1A17]">{isEs ? 'Mejor por calidad/spec' : 'Best by quality/spec'}:</span> {cmp.bestBySpec!.supplierName}</div>
                            <div><span className="font-[800] text-[#1C1A17]">{isEs ? 'Mejor valor general' : 'Best overall value'}:</span> {cmp.bestOverallValue!.supplierName}</div>
                          </div>
                        )}
                        {/* 3. Comparable price — explicit unit math + conversion. */}
                        <div data-testid="candidate-comparable-price" className="text-[9.5px] font-[600] text-[#4B5563]">
                          <div className="font-[800] text-[#1C1A17]">{isEs ? 'Precio comparable' : 'Comparable price'}</div>
                          {allComparable && cmpBest ? (
                            <>
                              {bestQty != null && <div>{isEs ? 'Cantidad' : 'Quantity'}: {quantityWithUnit(bestQty, cmpBest.originalUnitLabel)}</div>}
                              {cmpBest.comparableUnitPrice != null && <div>{isEs ? 'Precio unitario' : 'Unit price'}: ${cmpBest.comparableUnitPrice.toFixed(2)} {eachWord()}</div>}
                              {bestLineTotal != null && <div>{isEs ? 'Total de línea' : 'Line total'}: ${bestLineTotal.toFixed(2)}</div>}
                              {cmpBest.status === 'converted' && cmpBest.originalUnitPrice != null && (
                                <div data-testid="candidate-converted-price" className="mt-0.5 rounded bg-[#F7FAFF] border border-[#E1ECF7] px-1.5 py-1 space-y-0.5">
                                  <div>{isEs ? 'Precio original' : 'Original price'}: ${cmpBest.originalUnitPrice.toFixed(2)} {isEs ? 'por' : 'per'} {unitDisplayBilingual(cmpBest.originalUnit)}</div>
                                  <div>{isEs ? 'Precio convertido' : 'Converted price'}: ${cmpBest.comparableUnitPrice!.toFixed(2)} {eachWord()}</div>
                                  {comparedAgainst?.comparableUnitPrice != null && (
                                    <div>{isEs ? 'Comparado contra' : 'Compared against'}: ${comparedAgainst.comparableUnitPrice.toFixed(2)} {eachWord()}</div>
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <div data-testid="candidate-unit-conversion" className="text-amber-700 space-y-0.5">
                              <div>{isEs ? 'Unidad' : 'Unit'}: {unitsLabel}</div>
                              <div>{isEs ? 'Conversión' : 'Conversion'}: {isEs ? 'pendiente' : 'pending'}</div>
                              <div>{isEs ? 'Motivo' : 'Reason'}: {conversionReasonLabel(cmp.blockedReasonCode)}</div>
                              <div>{isEs ? 'Ahorro' : 'Savings'}: {isEs ? 'no calculado' : 'not calculated'}</div>
                            </div>
                          )}
                        </div>
                        {/* 4. Estimated savings (from comparable prices). */}
                        <div data-testid="candidate-savings" className="text-[9.5px] font-[700] text-emerald-700">
                          {allComparable && savingsPerEach != null ? (
                            <>{isEs ? 'Ahorro estimado' : 'Estimated savings'}: ${savingsPerEach.toFixed(2)} {isEs ? `por ${eachWord()}` : `per ${eachWord()}`}</>
                          ) : (
                            <span className="text-[#9E9890] font-[600]">
                              {isEs ? 'Ahorro no calculado: las unidades no son comparables sin conversión.' : 'Savings not calculated: units are not comparable without conversion.'}
                            </span>
                          )}
                        </div>
                        {/* 7. Recommendation sentence. */}
                        <div data-testid="candidate-recommendation-sentence" className="text-[9.5px] font-[700] text-[#06539B]">
                          {recommendationSentence(relation, cmpBest?.supplierName ?? null, allComparable)}
                        </div>
                        {/* 8. Primary action + View details. */}
                        <div className="flex gap-2 flex-wrap items-center">
                          <button
                            data-testid="candidate-primary-action"
                            disabled={pending}
                            title={decisionConsequence(primaryDecision)}
                            onClick={() => candidateDecision.mutate({ candidate: cand, decision: primaryDecision })}
                            className="flex-1 min-w-[140px] rounded-full bg-[#06539B] text-white text-[10px] font-[800] py-1.5 disabled:opacity-50"
                          >
                            {pending ? '…' : (isDirect ? decisionActionLabel('USE_AS_SAME_SPEC') : (isEs ? 'Aceptar recomendación' : 'Accept recommendation'))}
                          </button>
                          <button
                            data-testid="candidate-view-details"
                            onClick={() => toggleCandidateDetails(cand.candidateId)}
                            className="rounded-full border border-[#CECAC4] text-[#6B6660] text-[10px] font-[800] px-3 py-1.5"
                          >
                            {isExpanded ? (isEs ? 'Ocultar detalle' : 'Hide details') : (isEs ? 'Ver detalle' : 'View details')}
                          </button>
                        </div>
                        {/* Collapsed technical details — only when expanded. */}
                        {isExpanded && (
                          <div data-testid="candidate-details" className="border-t border-[#F2F0EB] pt-2 space-y-2">
                            {relation && (
                              <div data-testid="candidate-relationship-explainer" className="text-[9.5px] font-[600] text-[#6B7280]">
                                {relationshipExplainer(relation)}
                              </div>
                            )}
                            <div className="space-y-0.5 text-[9.5px] font-[600] text-[#4B5563]">
                              {relation && (
                                <div data-testid="candidate-field-relationship">
                                  <span className="font-[800] text-[#1C1A17]">{fieldLabel('relationship')}:</span>{' '}{isDirect ? sameSpecLabel() : relationshipLabel(relation)}
                                </div>
                              )}
                              <div data-testid="candidate-field-confidence">
                                <span className="font-[800] text-[#1C1A17]">{fieldLabel('confidence')}:</span>{' '}{confidenceLabel(conf)}
                              </div>
                            </div>
                            {/* Original supplier + item names + all attributes. */}
                            <div className="space-y-1.5">
                              {cand.lines.map((ln) => (
                                <div key={ln.id} className="space-y-0.5">
                                  <div className="flex items-center justify-between gap-2 text-[10px] font-[600] text-[#4B5563]">
                                    <span className="truncate">
                                      <span className="font-[800] text-[#1C1A17]">{ln.supplierName}</span> — {ln.description}
                                    </span>
                                    <span className="shrink-0 text-[#9E9890]">
                                      {ln.quantity != null ? `${ln.quantity} ` : ''}{ln.unit || ''}
                                      {typeof ln.unitPrice === 'number' ? ` · $${ln.unitPrice.toFixed(2)}` : ''}
                                    </span>
                                  </div>
                                  {Array.isArray(ln.attributes) && ln.attributes.length > 0 && (
                                    <div data-testid="candidate-attributes" className="flex flex-wrap gap-1">
                                      {ln.attributes.map((at) => (
                                        <span key={at.key} className="text-[8.5px] font-[700] text-[#6B7280] bg-[#F2F0EB] rounded px-1.5 py-0.5">
                                          {attrKeyLabel(at.key)}: {at.value}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            {baseReasons.length > 0 && (
                              <div data-testid="candidate-reasons" className="text-[9.5px] font-[600] text-[#4B5563]">
                                <span className="font-[800] text-[#1C1A17]">{fieldLabel('reasons')}:</span>
                                <span className="flex flex-wrap gap-1 mt-0.5">
                                  {baseReasons.map((r) => (
                                    <span key={r} className="text-[8.5px] font-[700] text-[#4B5563] bg-white border border-[#E5E2DC] rounded-full px-2 py-0.5">{reasonLabel(r)}</span>
                                  ))}
                                </span>
                              </div>
                            )}
                            {relReasons.length > 0 && (
                              <div data-testid="candidate-spec-differences" className="text-[9.5px] font-[600] text-[#4B5563]">
                                <span className="font-[800] text-[#1C1A17]">{fieldLabel('specDiffs')}:</span>
                                <span className="flex flex-wrap gap-1 mt-0.5">
                                  {relReasons.map((r) => (
                                    <span key={`rel-${r}`} className="text-[8.5px] font-[700] text-[#4B5563] bg-white border border-[#E5E2DC] rounded-full px-2 py-0.5">{relationshipReasonLabel(r)}</span>
                                  ))}
                                </span>
                              </div>
                            )}
                            {cand.risk && (
                              <div data-testid="candidate-risk" className="text-[9px] font-[800] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                                <span className="uppercase">{fieldLabel('risk')}:</span> ⚠ {riskLabel(cand.risk)}
                              </div>
                            )}
                            {cand.recommendation && (
                              <div data-testid="candidate-recommendation" className="text-[9.5px] font-[700] text-[#06539B]">
                                <span className="font-[800]">{fieldLabel('recommendation')}:</span> {recommendationLabel(cand.recommendation)}
                              </div>
                            )}
                            {/* Relationship review intro (QA #1) — plain language, no internal codes. */}
                            <div data-testid="candidate-review-intro" className="text-[9.5px] font-[600] text-[#4B5563]">
                              {isEs
                                ? 'Elige si estos materiales deben compararse juntos, tratarse como sustituto, mantenerse separados o ignorarse.'
                                : 'Choose whether these materials should be compared together, treated as a substitute, kept separate, or ignored.'}
                            </div>
                            {/* Advanced actions — all bilingual user-facing labels. Only Use-as-same-spec merges. */}
                            <div className="flex gap-2 flex-wrap">
                              <button data-testid="candidate-use-same-spec" disabled={pending} title={decisionConsequence('USE_AS_SAME_SPEC')}
                                onClick={() => candidateDecision.mutate({ candidate: cand, decision: 'USE_AS_SAME_SPEC' })}
                                className="rounded-full border border-[#CECAC4] text-[#6B6660] text-[10px] font-[800] px-3 py-1.5 disabled:opacity-50">
                                {pending ? '…' : (isDirect ? decisionActionLabel('USE_AS_SAME_SPEC') : compareAsDirectLabel())}
                              </button>
                              <button data-testid="candidate-mark-substitute" disabled={pending} title={decisionConsequence('MARK_AS_SUBSTITUTE')}
                                onClick={() => candidateDecision.mutate({ candidate: cand, decision: 'MARK_AS_SUBSTITUTE' })}
                                className="rounded-full border border-indigo-300 text-indigo-700 text-[10px] font-[800] px-3 py-1.5 disabled:opacity-50">
                                {pending ? '…' : decisionActionLabel('MARK_AS_SUBSTITUTE')}
                              </button>
                              <button data-testid="candidate-keep-separate" disabled={pending} title={decisionConsequence('KEEP_SEPARATE')}
                                onClick={() => candidateDecision.mutate({ candidate: cand, decision: 'KEEP_SEPARATE' })}
                                className="rounded-full border border-[#CECAC4] text-[#6B6660] text-[10px] font-[800] px-3 py-1.5 disabled:opacity-50">
                                {pending ? '…' : decisionActionLabel('KEEP_SEPARATE')}
                              </button>
                              <button data-testid="candidate-not-comparable" disabled={pending} title={decisionConsequence('NOT_COMPARABLE')}
                                onClick={() => candidateDecision.mutate({ candidate: cand, decision: 'NOT_COMPARABLE' })}
                                className="rounded-full border border-[#CECAC4] text-[#6B6660] text-[10px] font-[800] px-3 py-1.5 disabled:opacity-50">
                                {pending ? '…' : decisionActionLabel('NOT_COMPARABLE')}
                              </button>
                              <button data-testid="candidate-needs-review" disabled={pending} title={decisionConsequence('NEEDS_CONFIRMATION')}
                                onClick={() => candidateDecision.mutate({ candidate: cand, decision: 'NEEDS_CONFIRMATION' })}
                                className="rounded-full border border-amber-300 text-amber-700 text-[10px] font-[800] px-3 py-1.5 disabled:opacity-50">
                                {pending ? '…' : decisionActionLabel('NEEDS_CONFIRMATION')}
                              </button>
                              <button data-testid="candidate-ignore" disabled={pending} title={decisionConsequence('IGNORE')}
                                onClick={() => candidateDecision.mutate({ candidate: cand, decision: 'IGNORE' })}
                                className="rounded-full border border-[#CECAC4] text-[#9E9890] text-[10px] font-[800] px-3 py-1.5 disabled:opacity-50">
                                {pending ? '…' : decisionActionLabel('IGNORE')}
                              </button>
                            </div>
                            <div data-testid="candidate-action-help" className="text-[8.5px] font-[600] text-[#9E9890] space-y-0.5 pt-0.5">
                              {(['USE_AS_SAME_SPEC', 'MARK_AS_SUBSTITUTE', 'KEEP_SEPARATE', 'NOT_COMPARABLE', 'IGNORE', 'NEEDS_CONFIRMATION'] as CandidateDecision[]).map((d) => (
                                <div key={d}><span className="font-[800] text-[#6B6660]">{decisionActionLabel(d)}:</span> {decisionConsequence(d)}</div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                        )
                      })()
                    )
                  })}
                </div>
              )}

              {/* Matrix — PR #69 layout fixes:
                  · Horizontal scroll is CONTAINED inside this card
                    (no page-level overflow).
                  · Product column is STICKY on the left (position:
                    sticky; left: 0; z-index above the scrolling body)
                    so the row identity stays visible while scrolling
                    across many supplier columns.
                  · table-layout: fixed + explicit widths so columns
                    don't stretch unpredictably with long supplier
                    names or product descriptions.
                  · Supplier header uses a compact 2-line layout with
                    truncation + a title tooltip for full name.
                  · Product names truncate with a title tooltip; the
                    optional "Grouped variants" badge surfaces when
                    PR #69 collapsed multiple raw descriptions into
                    one row. */}
              {/* Phase 1A — mobile-only stacked card view. Renders
                  below `md` and is hidden at md+. Each card surfaces
                  the same row data the desktop table shows (product
                  name, unit, best price, honest status pill, per-
                  supplier price list) without horizontal scroll.
                  Reuses the same deriveHonestRowStatus decision +
                  bilingual status / reason copy as the table, so
                  there is no semantic divergence between the two
                  breakpoints. */}
              <div
                className="md:hidden bg-white border border-[#ECEAE4] rounded-xl divide-y divide-[#F2F0EB]"
                data-testid="quotes-compare-mobile-cards"
              >
                {comparison.rows.length === 0 ? (
                  <div className="px-3 py-6 text-center text-[10px] font-[700] text-[#9E9890]">
                    {isEs ? 'Aún no hay líneas comparables.' : 'No comparable lines yet.'}
                  </div>
                ) : (
                  comparison.rows.map((row) => {
                    const decision = deriveHonestRowStatus(row, {
                      lowConfidenceDocIds,
                      rowsByFamily,
                    })
                    const quotedCount = row.cells.length
                    const totalDocs = comparison.documents.length
                    const reasonLines = decision.reasons.map((r) => honestReasonCopy(r, isEs))
                    return (
                      <div key={`mobile-${row.comparisonKey}`} className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] font-[800] text-[#1C1A17] truncate" title={row.displayName}>
                              {row.displayName}
                            </div>
                            <div className="text-[10px] font-[700] text-[#9E9890]">
                              {row.unit || '—'}
                            </div>
                          </div>
                          <span
                            className={`text-[9px] font-[800] px-1.5 py-0.5 rounded-full whitespace-nowrap ${honestStatusPillClass(decision.status)}`}
                            data-testid="quotes-compare-mobile-status-pill"
                          >
                            {honestStatusLabel(decision.status, isEs)}
                          </span>
                        </div>
                        <div className="mt-2 flex items-baseline justify-between gap-2">
                          <div className={`text-[16px] font-[900] tracking-[-0.5px] ${
                            decision.status === 'BEST_COMPARABLE' ? 'text-[#047857]' : 'text-[#1C1A17]'
                          }`}>
                            ${row.bestPrice.toFixed(2)}
                          </div>
                          <div className="text-[10px] font-[700] text-[#9E9890]">
                            {isEs
                              ? `${quotedCount} de ${totalDocs} cotizados`
                              : `${quotedCount} of ${totalDocs} quoted`}
                          </div>
                        </div>
                        {reasonLines.length > 0 && (
                          <div className="mt-1 text-[10px] font-[600] text-[#6B7280] leading-snug">
                            {reasonLines.join(' · ')}
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
              </div>

              {/* Phase 1A — desktop comparison table. Hidden below
                  `md` so mobile users get the stacked card view
                  immediately above this wrapper. Existing
                  horizontal-scroll + sticky-left Product column +
                  fixed table-layout all preserved at md+. */}
              <div className="hidden md:block bg-white border border-[#ECEAE4] rounded-xl overflow-x-auto max-w-full" data-testid="quotes-compare-table">
                <table
                  className="text-[11px] w-full"
                  style={{ tableLayout: 'fixed', minWidth: 720 }}
                >
                  <colgroup>
                    <col style={{ width: 240 }} />
                    <col style={{ width: 56 }} />
                    {comparison.documents.map((doc) => (
                      <col key={doc.id} style={{ width: 112 }} />
                    ))}
                    {/* Phase 1B — small visual rebalance.
                        Best column gains 8px (96 → 104) because it's
                        the headline aggregate. Status column drops
                        12px (92 → 80) because Phase 1B's short pill
                        labels ("Best", "1 quote", "Review", etc.)
                        fit comfortably in 80px; the freed 4px goes
                        back to the supplier scroll area. */}
                    <col style={{ width: 104 }} />
                    <col style={{ width: 80 }} />
                  </colgroup>
                  <thead className="bg-[#FAFAF8] border-b border-[#ECEAE4]">
                    <tr>
                      <th
                        className="text-left px-3 py-2 font-[800] text-[#1C1A17] bg-[#FAFAF8] sticky left-0 z-20 shadow-[2px_0_0_0_#ECEAE4]"
                      >
                        {isEs ? 'Producto' : 'Product'}
                      </th>
                      <th className="text-left px-2 py-2 font-[800] text-[#9E9890]">
                        {isEs ? 'Unidad' : 'Unit'}
                      </th>
                      {comparison.documents.map((doc) => {
                        const isLowConfidence = lowConfidenceDocIds.has(doc.id)
                        const missing = missingCountsByDocId.get(doc.id)
                        return (
                          <th
                            key={doc.id}
                            className="text-right px-2 py-2 font-[800] text-[#1C1A17]"
                            title={`${doc.supplierName}${doc.quoteNumber ? ` · #${doc.quoteNumber}` : ''}${doc.quoteDate ? ` · ${formatDateOnly(doc.quoteDate, isEs ? 'es-MX' : 'en-US', { year: 'numeric', month: 'numeric', day: 'numeric' })}` : ''}`}
                          >
                            <div className="truncate" title={doc.supplierName}>
                              {doc.supplierName}
                            </div>
                            {(doc.quoteNumber || doc.quoteDate) && (
                              <div className="text-[9px] font-[700] text-[#9E9890] truncate">
                                {doc.quoteNumber ? `#${doc.quoteNumber}` : ''}
                                {doc.quoteNumber && doc.quoteDate ? ' · ' : ''}
                                {doc.quoteDate ? formatDateOnly(doc.quoteDate, isEs ? 'es-MX' : 'en-US', { year: 'numeric', month: 'numeric', day: 'numeric' }) : ''}
                              </div>
                            )}
                            {/* Phase 1A — per-supplier confidence hint.
                                Renders only when the SupplierQuote's
                                persisted confidence is below the
                                threshold. */}
                            {isLowConfidence && (
                              <div
                                className="text-[8px] font-[800] uppercase tracking-[0.4px] text-[#92400E] mt-0.5"
                                data-testid="quotes-compare-doc-low-confidence"
                              >
                                {isEs ? 'Confianza baja' : 'Low confidence'}
                              </div>
                            )}
                            {/* Phase 1A — per-supplier missing-item
                                count. Only when missing > 0. */}
                            {missing && missing.missing > 0 && (
                              <div
                                className="text-[8px] font-[700] text-[#9E9890] mt-0.5"
                                data-testid="quotes-compare-doc-missing"
                              >
                                {isEs
                                  ? `Faltan ${missing.missing} de ${missing.total}`
                                  : `Missing ${missing.missing} of ${missing.total}`}
                              </div>
                            )}
                          </th>
                        )
                      })}
                      <th className="text-right px-2 py-2 font-[800] text-[#1C1A17]">
                        {isEs ? 'Mejor' : 'Best'}
                      </th>
                      <th className="text-right px-2 py-2 font-[800] text-[#9E9890]">
                        {isEs ? 'Estado' : 'Status'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F2F0EB]">
                    {comparison.rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={comparison.documents.length + 4}
                          className="px-3 py-6 text-center text-[10px] font-[700] text-[#9E9890]"
                        >
                          {isEs
                            ? 'Aún no hay líneas comparables.'
                            : 'No comparable lines yet.'}
                        </td>
                      </tr>
                    ) : (
                      comparison.rows.map((row) => {
                        const cellsByDoc = new Map(row.cells.map((c) => [c.supplierQuoteId, c]))
                        // Phase 1A — derive the honest status FIRST.
                        // Every downstream pill, inline winner line,
                        // and "meaningful delta" gate consults the
                        // honest decision instead of the raw matrix
                        // status, so "Best Comparable Price" is the
                        // ONLY surface that uses the green/winner
                        // styling. Anything ambiguous degrades to
                        // Needs Review or Not Comparable.
                        const decision = deriveHonestRowStatus(row, {
                          lowConfidenceDocIds,
                          rowsByFamily,
                        })
                        const isHonestBest = decision.status === 'BEST_COMPARABLE'
                        const isHonestTied = decision.status === 'TIED'
                        const meaningfulDelta =
                          isHonestBest && row.worstPrice > row.bestPrice
                        const variantCount = row.variantCount ?? 0
                        const variantTooltip =
                          row.variantNames && row.variantNames.length > 0
                            ? row.variantNames.join('\n')
                            : ''
                        // Family-matching annotation (post-PR #69
                        // follow-up). When the same product family
                        // has more than one exact-match row in the
                        // matrix, surface a per-row "Variants /
                        // review" badge so the user can spot same-
                        // family / different-spec alternatives. We
                        // never silently merge — the rows stay
                        // distinct; only the badge appears.
                        const familyVariantCount = row.familyVariantCount ?? 1
                        const isFamilyAlternative = familyVariantCount > 1
                        const familyTooltip = isEs
                          ? 'Misma familia, especificación distinta'
                          : 'Same family, spec differs'
                        // Phase 1A — honest reason tooltip, built
                        // from the existing matrix + document data.
                        // Each reason maps to a bilingual one-liner.
                        // Phase 1B — the desktop pill now shows a
                        // short label (e.g. "Best", "1 quote",
                        // "Review") and the hover tooltip composes
                        // the full bilingual label with the first
                        // reason. Mobile keeps the long label.
                        const pillTooltip = honestStatusTooltip(decision, isEs)
                        return (
                          <tr key={row.comparisonKey}>
                            <td className="px-3 py-2 font-[700] text-[#1C1A17] bg-white sticky left-0 z-10 shadow-[2px_0_0_0_#ECEAE4]">
                              <div className="truncate" title={row.displayName}>
                                {row.displayName}
                              </div>
                              {variantCount > 1 && (
                                <button
                                  type="button"
                                  className="inline-block mt-0.5 text-[9px] font-[800] px-1.5 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8] hover:bg-[#DBEAFE] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1D4ED8] cursor-pointer"
                                  title={variantTooltip || (isEs ? 'Revisar variantes' : 'Review variants')}
                                  aria-label={isEs ? 'Revisar variantes agrupadas' : 'Review grouped variants'}
                                  onClick={() =>
                                    setVariantReview({
                                      rowComparisonKey: row.comparisonKey,
                                      kind: 'grouped-exact',
                                    })
                                  }
                                >
                                  {isEs ? 'Variantes agrupadas' : 'Grouped variants'} ({variantCount})
                                </button>
                              )}
                              {row.separatedByUser && (
                                <span
                                  data-testid="row-separated-by-user"
                                  className="inline-block mt-0.5 text-[9px] font-[800] px-1.5 py-0.5 rounded-full bg-[#F2F0EB] text-[#6B6660]"
                                  title={isEs ? 'Separado por decisión del usuario' : 'Separated by your decision'}
                                >
                                  {isEs ? 'Separado por usuario' : 'Separated by user'}
                                </span>
                              )}
                              {row.substituteSaved && (
                                <span
                                  data-testid="row-substitute-saved"
                                  className="inline-block mt-0.5 ml-1 text-[9px] font-[800] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200"
                                  title={isEs ? 'Guardado como sustituto/alternativa (no se agrupa como misma especificación)' : 'Saved as a substitute/alternative (not merged as same spec)'}
                                >
                                  {isEs ? 'Sustituto guardado' : 'Substitute saved'}
                                </span>
                              )}
                              {/* Ignore is a PURE hide (QA): the suggestion is
                                  resolved/hidden, but the matrix shows NO badge —
                                  no price/grouping/comparable-count change. */}
                              {isFamilyAlternative && (
                                <button
                                  type="button"
                                  className="inline-block mt-0.5 ml-1 text-[9px] font-[800] px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] hover:bg-[#FDE68A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#92400E] cursor-pointer"
                                  title={familyTooltip}
                                  aria-label={isEs ? 'Revisar variantes de familia' : 'Review family variants'}
                                  onClick={() =>
                                    setVariantReview({
                                      rowComparisonKey: row.comparisonKey,
                                      kind: 'family-variant',
                                    })
                                  }
                                >
                                  {isEs ? 'Variantes / revisar' : 'Variants / review'} ({familyVariantCount})
                                </button>
                              )}
                              {/* Phase 1A — inline "Best: X" winner
                                  line now gated on the HONEST
                                  decision so it only appears when
                                  the row is truly Best Comparable. */}
                              {isHonestBest && row.winnerLabels.length === 1 && (
                                <div className="text-[9px] font-[700] text-emerald-700 mt-0.5 truncate">
                                  {isEs ? 'Mejor:' : 'Best:'} {row.winnerLabels[0]} ${row.bestPrice.toFixed(2)}
                                </div>
                              )}
                              {isHonestTied && (
                                <div
                                  className="text-[9px] font-[700] text-amber-700 mt-0.5 truncate"
                                  title={row.winnerLabels.join(', ')}
                                >
                                  {isEs ? 'Empate:' : 'Tied:'} {row.winnerLabels.join(', ')} ${row.bestPrice.toFixed(2)}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-2 text-[#9E9890] truncate">{row.unit || '—'}</td>
                            {comparison.documents.map((doc) => {
                              const cell = cellsByDoc.get(doc.id)
                              if (!cell) {
                                return (
                                  <td key={doc.id} className="px-2 py-2 text-right text-[#9E9890]">—</td>
                                )
                              }
                              // Phase 1A — green "winner" cell
                              // styling is now gated on the honest
                              // decision. If the row isn't honestly
                              // Best Comparable or Tied, no cell
                              // gets the emerald winner highlight.
                              const showWinnerStyle =
                                (cell.isBest || cell.isTie) && (isHonestBest || isHonestTied)
                              // Phase 1B — per-cell provenance hover.
                              // Composed from documents data already
                              // fetched on this page; no new fetch.
                              const cellTooltip = buildCellProvenanceTooltip({
                                supplierName: cell.supplierName,
                                duplicateCount: cell.duplicateCount,
                                doc: docsById.get(cell.supplierQuoteId),
                                isEs,
                              })
                              return (
                                <td
                                  key={doc.id}
                                  className={`px-2 py-2 text-right whitespace-nowrap ${
                                    showWinnerStyle
                                      ? 'font-[900] text-[#047857] bg-[#ECFDF5]'
                                      : 'font-[700] text-[#1C1A17]'
                                  }`}
                                  title={cellTooltip || undefined}
                                  data-testid="quotes-compare-cell"
                                >
                                  ${cell.unitPrice.toFixed(2)}
                                  {cell.duplicateCount > 1 && (
                                    <span className="ml-1 text-[9px] font-[800] text-[#9E9890]">
                                      ({cell.duplicateCount}×)
                                    </span>
                                  )}
                                </td>
                              )
                            })}
                            <td
                              className={`px-2 py-2 text-right whitespace-nowrap bg-[#FAFAF8] ${
                                isHonestBest ? 'font-[900] text-[#047857]' : 'font-[800] text-[#1C1A17]'
                              }`}
                              data-testid="quotes-compare-best-cell"
                            >
                              ${row.bestPrice.toFixed(2)}
                              {meaningfulDelta && (
                                <div className="text-[9px] font-[700] text-[#9E9890]">
                                  −${(row.worstPrice - row.bestPrice).toFixed(2)} ({Math.round(((row.worstPrice - row.bestPrice) / row.worstPrice) * 100)}%)
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-2 text-right whitespace-nowrap">
                              {/* Phase 1A — single honest pill that
                                  covers all 6 statuses.
                                  Phase 1B — desktop pill now uses
                                  the short label
                                  (honestStatusShortLabel) so the
                                  same chip ("Only Available Price")
                                  doesn't repeat 40+ times across
                                  dense matrices; the hover tooltip
                                  carries the full bilingual label +
                                  the first reason (composed by
                                  honestStatusTooltip). Mobile cards
                                  keep the full label because they
                                  have row-level space for it. */}
                              <span
                                className={`text-[9px] font-[800] px-1.5 py-0.5 rounded-full ${honestStatusPillClass(decision.status)}`}
                                title={pillTooltip}
                                data-testid="quotes-compare-status-pill"
                              >
                                {honestStatusShortLabel(decision.status, isEs)}
                              </span>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Production-bug fix: non-material quote-level
                  charges (tax, freight, fees, discounts, totals,
                  deposit, payment) are routed to a secondary
                  section so they never pollute the product
                  comparison grid. Hidden when there are no
                  excluded charges to surface. */}
              {Array.isArray(comparison.excludedCharges) && comparison.excludedCharges.length > 0 && (
                <div className="bg-white border border-[#ECEAE4] rounded-xl">
                  <div className="px-4 py-2 border-b border-[#ECEAE4] flex items-center justify-between">
                    <div className="text-[11px] font-[800] text-[#1C1A17]">
                      {isEs
                        ? 'Cargos de la cotización excluidos de la comparación de productos'
                        : 'Quote-level charges excluded from product comparison'}
                    </div>
                    <span className="text-[10px] font-[700] text-[#9E9890]">
                      {comparison.excludedCharges.length}
                    </span>
                  </div>
                  <table className="w-full text-[11px]">
                    <thead className="bg-[#FAFAF8] border-b border-[#ECEAE4]">
                      <tr>
                        <th className="text-left px-3 py-2 font-[800] text-[#9E9890]">
                          {isEs ? 'Descripción' : 'Description'}
                        </th>
                        <th className="text-left px-2 py-2 font-[800] text-[#9E9890]">
                          {isEs ? 'Proveedor' : 'Supplier'}
                        </th>
                        <th className="text-left px-2 py-2 font-[800] text-[#9E9890]">
                          {isEs ? 'Tipo' : 'Type'}
                        </th>
                        <th className="text-right px-3 py-2 font-[800] text-[#9E9890]">
                          {isEs ? 'Monto' : 'Amount'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F2F0EB]">
                      {comparison.excludedCharges.map((charge, idx) => (
                        <tr key={`${charge.supplierQuoteId}-charge-${idx}`}>
                          <td
                            className="px-3 py-2 font-[700] text-[#1C1A17] truncate max-w-[260px]"
                            title={charge.description}
                          >
                            {charge.description || '—'}
                          </td>
                          <td
                            className="px-2 py-2 text-[#1C1A17] truncate max-w-[200px]"
                            title={charge.supplierName}
                          >
                            {charge.supplierName}
                          </td>
                          <td className="px-2 py-2 text-[#6B7280]">{charge.reason}</td>
                          <td className="px-3 py-2 text-right font-[800] text-[#1C1A17] whitespace-nowrap">
                            ${charge.amount.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <p className="text-[10px] font-[600] text-[#9E9890] px-1">
                {isEs
                  ? 'Cuando exista un takeoff confirmado del proyecto, la base de comparación cambiará a "Base: Takeoff del proyecto."'
                  : 'When a confirmed project takeoff exists, the comparison basis will switch to "Basis: Project takeoff baseline."'}
              </p>
            </>
          )}
        </div>
      )}

      {/* RFQ */}
      {activeTab === 'rfq' && (
        <MaterialRequestDraftModule projectId={projectId!} isEs={isEs} onReviewInCompare={() => setActiveTab('compare')} />
      )}

      {/* Variant Review drawer (PR #71 follow-up). Opened by the
          "Grouped variants" or "Variants / review" badges on the
          Compare-Prices matrix. READ-ONLY — no decisions are
          persisted yet. A follow-up PR can add user-confirmed
          merge/split/ignore overrides once a safe persistence
          model exists. */}
      {variantReview && comparison && (
        <VariantReviewDrawer
          isEs={isEs}
          kind={variantReview.kind}
          projectId={projectId!}
          row={
            comparison.rows.find(
              (r) => r.comparisonKey === variantReview.rowComparisonKey,
            ) ?? null
          }
          lineIdsByComparisonKey={comparison.lineIdsByComparisonKey ?? {}}
          overrides={comparison.overrides ?? []}
          onClose={() => setVariantReview(null)}
        />
      )}
    </div>
  )
}

// ── VARIANT REVIEW DRAWER ─────────────────────────
//
// Read-only review panel for the Compare-Prices matrix variant
// badges. When `kind === 'grouped-exact'` we explain that several
// raw supplier descriptions collapsed into the same exact-match
// row. When `kind === 'family-variant'` we list the SIBLING rows
// in the same product family so the user can see "same family,
// spec differs" before trusting the best-price column.
//
// No decisions are persisted from this drawer yet. The "No
// decision saved yet" notice (EN + ES) sets that expectation and
// will be removed by the future user-confirmed-merge/split PR.
function VariantReviewDrawer({
  isEs,
  kind,
  projectId,
  row,
  lineIdsByComparisonKey,
  overrides,
  onClose,
}: {
  isEs: boolean
  kind: 'grouped-exact' | 'family-variant'
  projectId: string
  row: ComparisonMatrixRow | null
  lineIdsByComparisonKey: Record<string, string[]>
  overrides: QuoteComparisonOverride[]
  onClose: () => void
}) {
  const { getToken } = useAuth()
  const qc = useQueryClient()

  // Saved-override lookup for this specific row (matches by either
  // targetKey or by any overlapping lineId). Used to render the
  // "Decision saved" / "Decisión guardada" affordance.
  const savedForRow = row
    ? overrides.find((o) => {
        if (o.targetKey && (o.targetKey === row.comparisonKey || o.targetKey === row.familyKey)) {
          return true
        }
        // Match by runtime row lineIds (the runtime row identity) —
        // fall back to the legacy keyed map for old server
        // responses that pre-date the PR #73 follow-up fix.
        const rowLines =
          row.lineIds && row.lineIds.length > 0
            ? row.lineIds
            : lineIdsByComparisonKey[row.comparisonKey] ?? []
        return o.lineIds.some((id) => rowLines.includes(id))
      })
    : undefined

  // Saving / warning state. Family CONFIRM_SAME_PRODUCT requires
  // an extra acknowledgement before the POST fires.
  const [saving, setSaving] = useState<VariantDecisionType | null>(null)
  const [pendingConfirm, setPendingConfirm] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')

  const saveOverride = async (decision: VariantDecisionType) => {
    if (!row) return
    setSaving(decision)
    try {
      const token = await getToken()
      // Phase A targets the EXACT_GROUP / FAMILY_GROUP scope when
      // the drawer is opened from a badge — the simplest mapping
      // from drawer mode to override sourceType.
      const sourceType: VariantDecisionSourceType =
        kind === 'grouped-exact' ? 'EXACT_GROUP' : 'FAMILY_GROUP'
      const targetKey = kind === 'grouped-exact' ? row.comparisonKey : row.familyKey ?? null
      // PR #73 follow-up: prefer the runtime row's own `lineIds`
      // over the keyed map — they are derived from the same
      // bucket that produced the row, so they line up even when
      // existing lines were re-grouped at deploy time. The keyed
      // map is still a safe fallback for old server responses
      // that pre-date this fix.
      const lineIds =
        row.lineIds && row.lineIds.length > 0
          ? row.lineIds
          : lineIdsByComparisonKey[row.comparisonKey] ?? []
      const res = await apiFetch(`/api/projects/${projectId}/quotes/comparison/overrides`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          decisionType: decision,
          sourceType,
          targetKey,
          lineIds,
          note: noteDraft.trim() ? noteDraft.trim() : null,
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      await qc.invalidateQueries({ queryKey: ['quote-comparison', projectId] })
    } finally {
      setSaving(null)
      setPendingConfirm(false)
    }
  }

  if (!row) return null

  // PR-B: clearer, relationship-aware titles (the prior wording was unclear).
  // grouped-exact = same-spec descriptions already in one comparable row;
  // family = same family but the spec differs (needs review before treating as
  // the same price).
  const title =
    kind === 'grouped-exact'
      ? isEs
        ? 'Misma especificación — descripciones agrupadas'
        : 'Same spec — grouped descriptions'
      : isEs
        ? 'Misma familia, especificación distinta — revisar'
        : 'Same family, spec differs — review'

  const explainer =
    kind === 'grouped-exact'
      ? isEs
        ? 'Estas descripciones son la misma especificación y se comparan juntas en la misma fila.'
        : 'These descriptions are the same spec and are compared together in the same row.'
      : isEs
        ? 'Misma familia de producto, pero la especificación puede variar (sustituto / mejora / menor especificación). Revisa antes de escoger el precio más bajo.'
        : 'Same product family, but the spec may differ (substitute / upgrade / downgrade). Review before choosing the lowest price.'

  const headerLabel = isEs ? 'Revisar relación de materiales' : 'Review material relationship'

  return (
    <div
      className="fixed inset-0 z-40 flex items-stretch justify-end bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-label={headerLabel}
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#ECEAE4] px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">
              {headerLabel}
            </div>
            <div className="text-[14px] font-[900] text-[#1C1A17]">{title}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[12px] font-[700] text-[#1C1A17] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1D4ED8]"
            aria-label={isEs ? 'Cerrar' : 'Close'}
          >
            {isEs ? 'Cerrar' : 'Close'} ✕
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Pre-decision notice. Only shown when this row has no
              saved override yet — once the user clicks one of the
              decision buttons below, the green "Decision saved"
              pill replaces this. */}
          {!savedForRow && (
            <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-[11px] font-[700] text-[#92400E]">
              {isEs ? 'Aún no se guarda ninguna decisión' : 'No decision saved yet'}
            </div>
          )}

          {/* Match-type explainer. */}
          <p className="text-[12px] font-[600] text-[#4B5563]">{explainer}</p>

          {/* Selected row details. */}
          <div>
            <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890] mb-1">
              {isEs ? 'Fila seleccionada' : 'Selected row'}
            </div>
            <div className="rounded-lg border border-[#ECEAE4] bg-[#FAFAF8] px-3 py-2">
              <div className="text-[13px] font-[800] text-[#1C1A17]">{row.displayName}</div>
              <div className="mt-1 text-[11px] font-[700] text-[#4B5563]">
                {isEs ? 'Unidad' : 'Unit'}: {row.unit || '—'} · {isEs ? 'Mejor precio' : 'Best price'}: $
                {row.bestPrice.toFixed(2)} · {isEs ? 'Estado' : 'Status'}: {row.status}
              </div>
              {row.winnerLabels.length > 0 && (
                <div className="mt-1 text-[11px] font-[700] text-emerald-700">
                  {isEs ? 'Proveedor:' : 'Supplier:'} {row.winnerLabels.join(', ')}
                </div>
              )}
            </div>
          </div>

          {/* Same-spec group — show the raw supplier descriptions. */}
          {kind === 'grouped-exact' && (
            <div>
              <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890] mb-1">
                {isEs
                  ? `Descripciones agrupadas (${row.variantCount ?? 0})`
                  : `Grouped descriptions (${row.variantCount ?? 0})`}
              </div>
              <ul className="rounded-lg border border-[#ECEAE4] bg-white divide-y divide-[#F2F0EB]">
                {(row.variantNames ?? []).map((name, idx) => (
                  <li
                    key={`${row.comparisonKey}-vn-${idx}`}
                    className="px-3 py-2 text-[12px] font-[600] text-[#1C1A17]"
                  >
                    {name}
                  </li>
                ))}
                {(row.variantNames ?? []).length === 0 && (
                  <li className="px-3 py-2 text-[11px] font-[600] text-[#9E9890]">
                    {isEs ? 'Sin descripciones disponibles' : 'No descriptions available'}
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Family variants — show sibling rows in a compact table. */}
          {kind === 'family-variant' && (
            <div>
              <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890] mb-1">
                {isEs
                  ? `Variantes de la familia (${(row.familyRowsPreview ?? []).length})`
                  : `Family variants (${(row.familyRowsPreview ?? []).length})`}
              </div>
              <p className="text-[11px] font-[600] text-[#4B5563] mb-2">
                {isEs
                  ? 'Revisa antes de escoger el precio más bajo'
                  : 'Review before choosing the lowest price'}
              </p>
              <table className="w-full text-[11px] border border-[#ECEAE4] rounded-lg overflow-hidden">
                <thead className="bg-[#FAFAF8]">
                  <tr>
                    <th className="text-left px-2 py-2 font-[800] text-[#1C1A17]">
                      {isEs ? 'Producto' : 'Product'}
                    </th>
                    <th className="text-left px-2 py-2 font-[800] text-[#9E9890]">
                      {isEs ? 'Unidad' : 'Unit'}
                    </th>
                    <th className="text-right px-2 py-2 font-[800] text-[#1C1A17]">
                      {isEs ? 'Mejor precio' : 'Best price'}
                    </th>
                    <th className="text-left px-2 py-2 font-[800] text-[#9E9890]">
                      {isEs ? 'Proveedor' : 'Supplier'}
                    </th>
                    <th className="text-left px-2 py-2 font-[800] text-[#9E9890]">
                      {isEs ? 'Estado' : 'Status'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F2F0EB]">
                  {(row.familyRowsPreview ?? []).map((sibling) => (
                    <tr key={sibling.comparisonKey}>
                      <td className="px-2 py-2 font-[700] text-[#1C1A17]" title={sibling.displayName}>
                        <div className="truncate max-w-[260px]">{sibling.displayName}</div>
                        {sibling.variantNames.length > 0 && (
                          <div
                            className="text-[9px] font-[600] text-[#9E9890] truncate max-w-[260px]"
                            title={sibling.variantNames.join('\n')}
                          >
                            {sibling.variantNames[0]}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-[#9E9890]">{sibling.unit || '—'}</td>
                      <td className="px-2 py-2 text-right font-[900] text-[#047857] whitespace-nowrap">
                        ${sibling.bestPrice.toFixed(2)}
                      </td>
                      <td className="px-2 py-2 text-[#1C1A17] truncate" title={sibling.winnerLabels.join(', ')}>
                        {sibling.winnerLabels.join(', ') || '—'}
                      </td>
                      <td className="px-2 py-2 text-[#6B7280]">{sibling.status}</td>
                    </tr>
                  ))}
                  {(row.familyRowsPreview ?? []).length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-2 py-3 text-center text-[10px] font-[700] text-[#9E9890]"
                      >
                        {isEs ? 'Sin variantes de familia' : 'No family variants'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* PR-NEW (variant decisions / Phase A): action panel. The
              buttons POST a QuoteComparisonOverride so the user
              decision is persisted; Phase B will apply it to the
              matrix. */}
          <div className="border-t border-[#ECEAE4] pt-4">
            <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890] mb-2">
              {isEs ? 'Tu decisión' : 'Your decision'}
            </div>

            {savedForRow ? (
              <div data-testid="drawer-decision-saved" className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] font-[700] text-emerald-800">
                {isEs ? 'Decisión guardada' : 'Decision saved'}: <span className="font-[800]">{variantDecisionSavedLabel(savedForRow.decisionType, isEs)}</span>
                {savedForRow.note ? (
                  <div className="mt-1 font-[600] text-emerald-700">{savedForRow.note}</div>
                ) : null}
              </div>
            ) : null}

            {/* Family-variant confirmation warning — only shown when
                the user is about to CONFIRM_SAME_PRODUCT on a
                family-variant pair (different specs). */}
            {pendingConfirm && kind === 'family-variant' && (
              <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] font-[700] text-amber-900">
                {isEs
                  ? 'Pueden variar por especificación. Confirma solo si son intercambiables para tu compra.'
                  : 'These may differ by specification. Confirm only if they are interchangeable for your purchase.'}
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => saveOverride('USE_AS_SAME_SPEC')}
                    disabled={saving !== null}
                    className="text-[11px] font-[800] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-700"
                  >
                    {isEs ? 'Usar como misma especificación' : 'Use as same spec'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingConfirm(false)}
                    disabled={saving !== null}
                    className="text-[11px] font-[800] px-2 py-1 rounded border border-amber-300 text-amber-900 hover:bg-amber-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-700"
                  >
                    {isEs ? 'Cancelar' : 'Cancel'}
                  </button>
                </div>
              </div>
            )}

            {/* Optional decision note. */}
            <div className="mt-2">
              <label className="block text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890] mb-1">
                {isEs ? 'Nota (opcional)' : 'Note (optional)'}
              </label>
              <input
                type="text"
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder={isEs ? 'Agrega contexto opcional' : 'Add optional context'}
                className="w-full text-[12px] font-[600] text-[#1C1A17] px-2 py-1.5 rounded border border-[#ECEAE4] focus:outline-none focus:border-[#1D4ED8]"
              />
            </div>

            {/* Relationship-aware decision buttons (PR-B). For grouped-exact,
                "Use as same spec" merges; for family-variant it asks to confirm
                first (the spec differs). "Mark as substitute" saves an
                alternative without merging same-spec pricing. */}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  if (kind === 'family-variant') {
                    setPendingConfirm(true)
                  } else {
                    saveOverride('USE_AS_SAME_SPEC')
                  }
                }}
                disabled={saving !== null || pendingConfirm}
                aria-label={isEs ? 'Usar como misma especificación' : 'Use as same spec'}
                className="text-[11px] font-[800] px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-700"
              >
                {isEs ? 'Usar como misma especificación' : 'Use as same spec'}
              </button>
              <button
                type="button"
                onClick={() => saveOverride('KEEP_SEPARATE')}
                disabled={saving !== null}
                aria-label={isEs ? 'Mantener separado' : 'Keep separate'}
                className="text-[11px] font-[800] px-3 py-1.5 rounded-lg border border-[#1D4ED8] text-[#1D4ED8] hover:bg-[#EFF6FF] disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1D4ED8]"
              >
                {isEs ? 'Mantener separado' : 'Keep separate'}
              </button>
              <button
                type="button"
                onClick={() => saveOverride('MARK_AS_SUBSTITUTE')}
                disabled={saving !== null}
                aria-label={isEs ? 'Marcar como sustituto' : 'Mark as substitute'}
                className="text-[11px] font-[800] px-3 py-1.5 rounded-lg border border-amber-500 text-amber-700 hover:bg-amber-50 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-700"
              >
                {isEs ? 'Marcar como sustituto' : 'Mark as substitute'}
              </button>
              <button
                type="button"
                onClick={() => saveOverride('IGNORE')}
                disabled={saving !== null}
                aria-label={isEs ? 'Ignorar' : 'Ignore'}
                className="text-[11px] font-[800] px-3 py-1.5 rounded-lg border border-[#9CA3AF] text-[#4B5563] hover:bg-[#F3F4F6] disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4B5563]"
              >
                {isEs ? 'Ignorar' : 'Ignore'}
              </button>
            </div>

            <p className="mt-2 text-[10px] font-[600] text-[#9E9890]">
              {isEs
                ? 'La decisión se aplicará a la matriz en una actualización futura.'
                : 'The decision will be applied to the matrix in a future update.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── RFQ FORM ──────────────────────────────────────

// ── COMPARE-MATRIX SUMMARY CARD ───────────────────

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-[#ECEAE4] bg-white px-3 py-2">
      <div className="text-[9px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">{label}</div>
      <div className="text-[14px] font-[900] text-[#1C1A17]">{value}</div>
    </div>
  )
}

// ── MATERIAL REQUEST DRAFT MODULE ──────────────────
//
// Phase 1: generates and edits a saveable "Material Request Draft"
// (a.k.a. "Best Price Buy List Draft" / "Recommended Purchase
// Package Draft") from the current Compare-Prices matrix. The
// draft is intentionally NOT a Purchase Order, NOT a Bill of
// Quantities, NOT an automatic purchase. Sending is deferred to
// Phase 2.
//
// This module replaces the legacy RFQForm that asked for raw
// material/supplier IDs — that flow was unusable for the customer.

type DraftBasis = 'UPLOADED_QUOTES_ONLY' | 'PROJECT_TAKEOFF_BASELINE'

type DraftLineSourceType =
  | 'BEST_PRICE'
  | 'TIE_REVIEW'
  | 'ONLY_QUOTE'
  | 'MANUAL'
  | 'MISSING_FROM_BASELINE'
  | 'ACCEPTABLE_ALTERNATIVE'

type DraftLineStatus = 'INCLUDED' | 'REMOVED' | 'NEEDS_REVIEW'

interface MaterialRequestDraftLine {
  id: string
  supplierId: string | null
  supplierQuoteId: string | null
  supplierQuoteLineId: string | null
  comparisonKey: string | null
  familyKey: string | null
  takeoffItemId: string | null
  description: string
  quantity: number | null
  unit: string | null
  unitPrice: number | null
  totalPrice: number | null
  sourceType: DraftLineSourceType
  status: DraftLineStatus
  reason: string | null
  notes: string | null
  lineOrder: number
  // PR-NEW (UX fix): server-side relations so the UI can render
  // supplier display names instead of raw IDs.
  supplier?: { id: string; name: string } | null
  supplierQuote?: {
    id: string
    supplierName: string | null
    quoteNumber: string | null
    quoteDate: string | null
  } | null
  // PR-NEW (supplier-group key override): when a manual line is
  // added inside a free-text-named supplier group, the server
  // stores the canonical supplier-group key here so the UI's
  // `supplierKeyFor()` (and the PDF's `computeSupplierKey()`)
  // can route the line into the correct group without a Supplier
  // row.
  supplierGroupKey?: string | null
}

// Persisted per-supplier approval state (PR-NEW). One row per
// (request, supplierKey). The UI reads `status` to render the
// Approve / Approved badge + the "Download approved supplier
// request" button, and reads `neededBy` / `deliveryLocation` /
// `notes` to seed the per-supplier request-details editor.
interface MaterialRequestSupplierGroup {
  id: string
  supplierKey: string
  supplierId: string | null
  supplierName: string | null
  status: 'DRAFT' | 'APPROVED' | 'ARCHIVED'
  // PR-NEW: per-supplier overrides that win over the global
  // draft defaults on the supplier-specific PDF.
  neededBy: string | null
  deliveryLocation: string | null
  notes: string | null
  approvedAt: string | null
  approvedById: string | null
}

interface MaterialRequestDraft {
  id: string
  projectId: string
  userId: string
  title: string | null
  basis: DraftBasis
  status: 'DRAFT' | 'ARCHIVED'
  neededBy: string | null
  deliveryLocation: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
  lines: MaterialRequestDraftLine[]
  supplierGroups?: MaterialRequestSupplierGroup[]
}

function MaterialRequestDraftModule({
  projectId,
  isEs,
  onReviewInCompare,
}: {
  projectId: string
  isEs: boolean
  onReviewInCompare?: () => void
}) {
  const { getToken } = useAuth()
  const qc = useQueryClient()

  // List of drafts for this project — we pick the most recent
  // DRAFT and let the user generate a new one explicitly.
  const { data: drafts } = useQuery<Array<{ id: string; status: string; updatedAt: string }>>({
    queryKey: ['quote-requests', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/quotes/requests`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    retry: false,
  })

  // Phase 1 — defensively filter to status === 'DRAFT' before
  // picking the active draft. The list endpoint currently returns
  // all draft rows (any status) ordered by updatedAt DESC, and the
  // `generate-from-best-prices` route does not yet de-dupe on the
  // server. Without this filter, an archived/sent draft sorted on
  // top would silently become the "active" one. Backend behavior
  // is unchanged — this is purely FE hygiene.
  const activeDrafts = (drafts ?? []).filter((d) => d.status === 'DRAFT')
  const latestDraftId = activeDrafts.length > 0 ? activeDrafts[0].id : null
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null)
  const selectedId = activeDraftId ?? latestDraftId

  const { data: draft } = useQuery<MaterialRequestDraft | null>({
    queryKey: ['quote-request', projectId, selectedId],
    enabled: !!selectedId,
    queryFn: async () => {
      if (!selectedId) return null
      const token = await getToken()
      const res = await apiFetch(
        `/api/projects/${projectId}/quotes/requests/${selectedId}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    retry: false,
  })

  const generateMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const res = await apiFetch(
        `/api/projects/${projectId}/quotes/requests/generate-from-best-prices`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({}),
        },
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json() as Promise<MaterialRequestDraft>
    },
    onSuccess: (newDraft) => {
      setActiveDraftId(newDraft.id)
      qc.invalidateQueries({ queryKey: ['quote-requests', projectId] })
      qc.invalidateQueries({ queryKey: ['quote-request', projectId] })
    },
  })

  // A draft already exists → "Generate" becomes a destructive "Regenerate"
  // that must be confirmed (QA: never silently reset edits/notes/decisions).
  const draftExists = activeDrafts.length > 0 || !!draft
  const [showRegenConfirm, setShowRegenConfirm] = useState(false)

  return (
    <div className="space-y-3">
      {/* Header card with primary CTA. */}
      <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
        <h3 className="text-[13px] font-[800] text-[#1C1A17]">
          {isEs ? 'Solicitud de materiales' : 'Material Request'}
        </h3>

        {/* Phase-1 "Draft only" banner. */}
        <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-[11px] font-[700] text-[#92400E]">
          {isEs
            ? 'Borrador solamente — el envío estará disponible después'
            : 'Draft only — sending available later'}
        </div>

        {/* Scope note (QA #4): this request is NOT the full project material list. */}
        <div data-testid="draft-scope-note" className="rounded-lg border border-[#D8E6F4] bg-[#F2F7FC] px-3 py-2 text-[11px] font-[600] text-[#4B5563]">
          {isEs
            ? 'Esta solicitud incluye únicamente los materiales seleccionados para pedir cotización a proveedores, basados en las cotizaciones subidas y mejores precios detectados. No es la lista completa de materiales del proyecto.'
            : 'This request includes only the selected materials used to request pricing from suppliers, based on uploaded quotes and detected best prices. It is not the complete project material list.'}
        </div>

        <p className="text-[11px] font-[600] text-[#4B5563]">
          {isEs
            ? 'Genera un borrador editable con los mejores precios de tus cotizaciones subidas. Podrás revisar, agregar líneas manuales y guardar antes de cualquier envío.'
            : 'Generate an editable draft from the best prices in your uploaded quotes. You can review, add manual lines, and save before any send.'}
        </p>

        {/* Workflow step copy. Approval persistence + a separate
            Approved Purchase Request / PO Draft renderer are a
            future PR — this PR clarifies the user-facing flow but
            does NOT add a Send button. */}
        <ol className="text-[11px] font-[600] text-[#4B5563] list-decimal pl-5 space-y-0.5">
          <li>
            {isEs
              ? 'Genera el borrador desde la mejor comparación disponible.'
              : 'Generate the draft from the best available comparison.'}
          </li>
          <li>
            {isEs ? 'Revisa y edita el borrador.' : 'Review and edit the draft.'}
          </li>
          <li>
            {isEs
              ? 'Descarga la solicitud PDF para enviarla manualmente al proveedor.'
              : 'Download the supplier request PDF to send it manually to the supplier.'}
          </li>
        </ol>
        <p className="text-[10px] font-[600] text-[#9E9890]">
          {isEs
            ? 'Revisa el borrador y luego descarga la solicitud PDF para proveedor.'
            : 'Review the draft, then download the supplier request PDF.'}
        </p>

        {/* First creation vs. destructive regeneration are different
            actions (QA): once a draft exists, the button becomes
            "Regenerate" and requires explicit confirmation so manual
            edits / notes / review decisions are never silently reset. */}
        {!draftExists ? (
          <button
            type="button"
            data-testid="draft-generate-btn"
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
            className="w-full flex items-center justify-center gap-2 bg-[#06539B] text-white text-[13px] font-[800] py-2.5 rounded-full disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#06539B]"
          >
            <Send size={14} />
            {generateMutation.isPending
              ? (isEs ? 'Generando…' : 'Generating…')
              : (isEs ? 'Generar solicitud con mejores precios' : 'Generate request from best prices')}
          </button>
        ) : !showRegenConfirm ? (
          <button
            type="button"
            data-testid="draft-regenerate-btn"
            onClick={() => setShowRegenConfirm(true)}
            disabled={generateMutation.isPending}
            className="w-full flex items-center justify-center gap-2 border border-[#CECAC4] text-[#6B6660] text-[13px] font-[800] py-2.5 rounded-full disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#9E9890]"
          >
            {isEs ? 'Regenerar borrador con mejores precios' : 'Regenerate draft from best prices'}
          </button>
        ) : (
          <div data-testid="draft-regenerate-confirm" className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2">
            <div className="text-[11px] font-[700] text-red-800">
              {isEs
                ? 'Regenerar reemplazará las líneas actuales del borrador, ediciones manuales, notas y decisiones de revisión. ¿Continuar?'
                : 'Regenerating will replace current draft lines, manual edits, notes, and review decisions. Continue?'}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="draft-regenerate-confirm-yes"
                onClick={() => { setShowRegenConfirm(false); generateMutation.mutate() }}
                disabled={generateMutation.isPending}
                className="flex-1 bg-red-600 text-white text-[12px] font-[800] py-2 rounded-full disabled:opacity-40"
              >
                {generateMutation.isPending ? (isEs ? 'Regenerando…' : 'Regenerating…') : (isEs ? 'Sí, regenerar' : 'Yes, regenerate')}
              </button>
              <button
                type="button"
                data-testid="draft-regenerate-confirm-cancel"
                onClick={() => setShowRegenConfirm(false)}
                className="flex-1 border border-[#CECAC4] text-[#6B6660] text-[12px] font-[800] py-2 rounded-full"
              >
                {isEs ? 'Cancelar' : 'Cancel'}
              </button>
            </div>
          </div>
        )}
        {generateMutation.isError && (
          <div className="text-[11px] font-[700] text-red-600 text-center">
            {isEs
              ? 'No se pudo generar el borrador'
              : 'Could not generate draft'}
          </div>
        )}
      </div>

      {/* Draft view. */}
      {draft && <MaterialRequestDraftEditor draft={draft} isEs={isEs} projectId={projectId} onReviewInCompare={onReviewInCompare} />}
    </div>
  )
}

function basisLabel(basis: DraftBasis, isEs: boolean): string {
  if (basis === 'PROJECT_TAKEOFF_BASELINE') {
    return isEs ? 'Base: Takeoff confirmado del proyecto' : 'Basis: Project takeoff baseline'
  }
  return isEs ? 'Base: Solo cotizaciones subidas' : 'Basis: Uploaded quotes only'
}

function MaterialRequestDraftEditor({
  draft,
  isEs,
  projectId,
  onReviewInCompare,
}: {
  draft: MaterialRequestDraft
  isEs: boolean
  projectId: string
  onReviewInCompare?: () => void
}) {
  const { getToken } = useAuth()
  const qc = useQueryClient()

  // Per-supplier-group inline "Add manual line" form state.
  // Key is the supplierKey; value is null when the form is
  // closed, or the in-progress draft when the user clicked
  // "Add manual line" for that group.
  type ManualLineDraft = {
    description: string
    quantity: string
    unit: string
    notes: string
  }
  const [manualLineForms, setManualLineForms] = useState<
    Record<string, ManualLineDraft | null>
  >({})
  // Session map of review-row decisions → keeps a decided row visible with an
  // Included/Excluded pill + Undo until reload (persistence is the status field).
  const [reviewDecisions, setReviewDecisions] = useState<Record<string, 'INCLUDED' | 'EXCLUDED'>>({})

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ['quote-request', projectId, draft.id] })
    await qc.invalidateQueries({ queryKey: ['quote-requests', projectId] })
  }

  // ── Saved-state / autosave indicator (PR-RQ-AUTOSAVE) ───────────────────────
  // FE-only, no schema. Every draft mutation runs through runSave() so the user
  // sees Saving… → Saved (auto, with elapsed minutes) or a visible Save failed.
  // lastSavedAt is tracked locally (no reliable per-mutation updatedAt return).
  type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error'
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null)
  const [nowTick, setNowTick] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30000)
    return () => clearInterval(t)
  }, [])
  // Mark unsaved edits (e.g. while typing in a notes field before blur saves it).
  const markDirty = () => setSaveState((s) => (s === 'saving' ? s : 'dirty'))
  async function runSave<T>(fn: () => Promise<T>): Promise<T> {
    setSaveState('saving')
    const startedAt = Date.now()
    try {
      const r = await fn()
      // Keep "Saving…" visible long enough to perceive even on fast saves (QA #1).
      const elapsed = Date.now() - startedAt
      if (elapsed < 500) await new Promise((resolve) => setTimeout(resolve, 500 - elapsed))
      setLastSavedAt(Date.now())
      setSaveState('saved')
      return r
    } catch (e) {
      // No silent failure — surface a visible "Save failed" state.
      setSaveState('error')
      throw e
    }
  }
  const saveStateLabel = (): string => {
    switch (saveState) {
      case 'saving': return isEs ? 'Guardando…' : 'Saving…'
      case 'error': return isEs ? 'No se pudo guardar el cambio. Intenta de nuevo.' : 'Could not save the change. Please try again.'
      case 'dirty': return isEs ? 'Cambios sin guardar' : 'Unsaved changes'
      case 'saved': {
        if (lastSavedAt == null) return isEs ? 'Guardado' : 'Saved'
        const mins = Math.floor((nowTick - lastSavedAt) / 60000)
        if (mins <= 0) return isEs ? 'Guardado automáticamente hace un momento' : 'Saved automatically just now'
        return isEs
          ? `Guardado automáticamente hace ${mins} ${mins === 1 ? 'minuto' : 'minutos'}`
          : `Saved automatically ${mins} ${mins === 1 ? 'minute' : 'minutes'} ago`
      }
      default: return isEs ? 'Guardado' : 'Saved'
    }
  }

  const patchLine = async (lineId: string, body: Record<string, unknown>) => runSave(async () => {
    const token = await getToken()
    const res = await apiFetch(`/api/quotes/requests/${draft.id}/lines/${lineId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    await invalidate()
  })

  const removeLine = async (lineId: string) => runSave(async () => {
    const token = await getToken()
    const res = await apiFetch(`/api/quotes/requests/${draft.id}/lines/${lineId}`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    await invalidate()
  })

  // PR-NEW (UX fix): download the combined Material Request Draft
  // PDF. The server route is read-only — generating the PDF never
  // sends mail, never changes draft.status. The Blob is delivered
  // via a hidden anchor click so the browser triggers a normal
  // download experience.
  const downloadPdf = async () => {
    const token = await getToken()
    const lang = isEs ? 'es' : 'en'
    const res = await apiFetch(
      `/api/quotes/requests/${draft.id}/pdf?lang=${lang}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    )
    if (!res.ok) return
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `material-request-draft-${draft.id}.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  // Open/preview the current draft PDF in a new tab (read-only, no regenerate).
  // From there the browser's own viewer offers Print — we do not fake a Print.
  const previewPdf = async () => {
    const token = await getToken()
    const lang = isEs ? 'es' : 'en'
    const res = await apiFetch(
      `/api/quotes/requests/${draft.id}/pdf?lang=${lang}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    )
    if (!res.ok) return
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank', 'noopener,noreferrer')
    // Revoke after a delay so the new tab has time to load the blob.
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  }

  // Per-supplier PDF download (DRAFT or APPROVED — the server
  // decides which variant based on persisted group status).
  const downloadSupplierPdf = async (supplierKey: string, supplierName: string | null) => {
    const token = await getToken()
    const lang = isEs ? 'es' : 'en'
    const res = await apiFetch(
      `/api/quotes/requests/${draft.id}/suppliers/${encodeURIComponent(supplierKey)}/pdf?lang=${lang}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    )
    if (!res.ok) return
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const safeName = (supplierName || supplierKey)
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .slice(0, 60)
    a.download = `material-request-${safeName}-${draft.id}.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  // Per-supplier approval state mutations. After mutation we
  // invalidate the draft query so the UI re-renders with the
  // updated MaterialRequestSupplierGroup.status badge.
  const approveSupplierGroup = async (supplierKey: string) => runSave(async () => {
    const token = await getToken()
    const res = await apiFetch(
      `/api/quotes/requests/${draft.id}/suppliers/${encodeURIComponent(supplierKey)}/approve`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({}),
      },
    )
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    await invalidate()
  })
  const unapproveSupplierGroup = async (supplierKey: string) => runSave(async () => {
    const token = await getToken()
    const res = await apiFetch(
      `/api/quotes/requests/${draft.id}/suppliers/${encodeURIComponent(supplierKey)}/unapprove`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({}),
      },
    )
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    await invalidate()
  })

  // PR-NEW: per-supplier-group request details. Lets the user
  // override neededBy / deliveryLocation / notes for one supplier
  // without affecting the global draft defaults. Used as
  // onBlur-save on the per-group inline fields.
  const patchSupplierGroup = async (
    supplierKey: string,
    body: { neededBy?: string | null; deliveryLocation?: string | null; notes?: string | null },
  ) => runSave(async () => {
    const token = await getToken()
    const res = await apiFetch(
      `/api/quotes/requests/${draft.id}/suppliers/${encodeURIComponent(supplierKey)}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      },
    )
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    await invalidate()
  })

  // PR-NEW: per-supplier "Add manual line". Differs from the
  // top-level addManualLine in two ways:
  //   * supplierGroupKey is passed so the new line lands in the
  //     right group even when the group is free-text-named.
  //   * supplierId is forwarded when the catalog Supplier id is
  //     known.
  // The new line goes to the server with sourceType=MANUAL +
  // status=INCLUDED (defaults on the route).
  const addManualLineToGroup = async (
    supplierKey: string,
    supplierId: string | null,
    line: { description: string; quantity: number | null; unit: string | null; notes: string | null },
  ) => runSave(async () => {
    const token = await getToken()
    const res = await apiFetch(`/api/quotes/requests/${draft.id}/lines`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        supplierId: supplierId || undefined,
        supplierGroupKey: supplierKey,
        description: line.description,
        quantity: line.quantity,
        unit: line.unit,
        notes: line.notes,
      }),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    await invalidate()
  })

  // Filter out soft-removed lines from the default view; user can
  // restore by setting status back to INCLUDED via the API.
  const activeLines = draft.lines.filter((l) => l.status !== 'REMOVED')
  const needsReviewLines = activeLines.filter((l) => l.status === 'NEEDS_REVIEW')
  const includedLines = activeLines.filter((l) => l.status === 'INCLUDED')
  // Review-row decisions visible state (QA): after Include/Exclude the row stays
  // visible in "Review before sending" with an Included/Excluded pill + Undo, so
  // the user sees their choice took effect. Persistence is the line status field
  // (INCLUDED → included groups + PDF; REMOVED/EXCLUDED → out of draft + PDF);
  // this session map only keeps the decided row on-screen until reload.
  const allLineById = new Map(draft.lines.map((l) => [l.id, l]))
  const reviewLineEntries = [
    ...needsReviewLines.map((l) => ({ line: l, decision: null as 'INCLUDED' | 'EXCLUDED' | null })),
    ...Object.entries(reviewDecisions)
      .filter(([id]) => allLineById.has(id) && allLineById.get(id)!.status !== 'NEEDS_REVIEW')
      .map(([id, decision]) => ({ line: allLineById.get(id)!, decision })),
  ]
  const setReviewDecision = (id: string, decision: 'INCLUDED' | 'EXCLUDED') =>
    setReviewDecisions((prev) => ({ ...prev, [id]: decision }))
  const includeReviewLine = async (id: string) => { await patchLine(id, { status: 'INCLUDED' }); setReviewDecision(id, 'INCLUDED') }
  const excludeReviewLine = async (id: string) => { await removeLine(id); setReviewDecision(id, 'EXCLUDED') }
  const undoReviewLine = async (id: string) => {
    await patchLine(id, { status: 'NEEDS_REVIEW' })
    setReviewDecisions((prev) => { const n = { ...prev }; delete n[id]; return n })
  }
  const missingFromBaseline = activeLines.filter(
    (l) => l.sourceType === 'MISSING_FROM_BASELINE',
  )
  // Soft-removed lines (QA): shown in a "Deleted lines" section with Restore so
  // the user never loses a line silently. Restore flips status back to INCLUDED
  // (persisted; reappears in the supplier group + PDF). No schema change.
  const removedLines = draft.lines.filter((l) => l.status === 'REMOVED')
  const restoreLine = async (id: string) => { await patchLine(id, { status: 'INCLUDED' }) }

  // Group INCLUDED lines by supplier display name (PR-NEW UX fix).
  // We pick the best available name for the group key + label:
  //   1. line.supplier?.name    (Supplier row linked → catalog name)
  //   2. line.supplierQuote?.supplierName  (linked SupplierQuote
  //      document's free-text name when no Supplier row is set)
  //   3. null                    (unassigned — shown with i18n
  //      fallback string; raw supplierId is NEVER displayed).
  const supplierLabelFor = (l: MaterialRequestDraftLine): string | null => {
    if (l.supplier?.name) return l.supplier.name
    if (l.supplierQuote?.supplierName) return l.supplierQuote.supplierName
    return null
  }
  // Canonical supplier identity used by the server's per-supplier
  // PDF / approve routes. MUST stay in sync with the server-side
  // `computeSupplierKey()` helper (src/server/utils/
  // materialRequestDraftPdf.ts). Resolution order:
  //   1. linked Supplier.id (catalog)
  //   2. explicit supplierGroupKey (PR-NEW: lets a manual line
  //      attach to a free-text-named supplier group even when no
  //      catalog Supplier row exists)
  //   3. normalized free-text SupplierQuote.supplierName
  //   4. literal 'unassigned'
  // Mirroring the server is load-bearing: when the UI and server
  // disagree, multiple buckets can collapse to the same fallback
  // key ('unassigned') and a single approve flips them all
  // because they share a groupStatusByKey lookup.
  const supplierKeyFor = (l: MaterialRequestDraftLine): string => {
    if (l.supplier?.id) return l.supplier.id
    const explicit = l.supplierGroupKey?.trim()
    if (explicit) return explicit
    const free = l.supplierQuote?.supplierName?.trim() ?? ''
    if (free) {
      const slug = free
        .toLowerCase()
        .normalize('NFD')
        // eslint-disable-next-line no-misleading-character-class
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
      return slug || 'unassigned'
    }
    return 'unassigned'
  }
  // Map supplierKey → persisted approval state (DRAFT | APPROVED).
  const groupStatusByKey = new Map<string, 'DRAFT' | 'APPROVED' | 'ARCHIVED'>()
  // Map supplierKey → full MaterialRequestSupplierGroup, used to
  // seed per-supplier neededBy / deliveryLocation / notes inputs.
  const groupByKey = new Map<string, MaterialRequestSupplierGroup>()
  for (const g of draft.supplierGroups ?? []) {
    groupStatusByKey.set(g.supplierKey, g.status)
    groupByKey.set(g.supplierKey, g)
  }
  // Group included lines by their canonical supplierKey (NOT by
  // display label). Keying by display label allowed two different
  // supplierKeys with identical labels (or null labels) to merge,
  // which let one approve flip multiple visual buckets. With the
  // map keyed on supplierKey, each canonical supplier owns exactly
  // one bucket, and the bucket carries its own display label
  // (first non-null we see, falling back to the persisted
  // SupplierGroup.supplierName, falling back to null).
  const grouped = new Map<
    string,
    { lines: MaterialRequestDraftLine[]; supplierKey: string; supplierName: string | null }
  >()
  for (const l of includedLines) {
    const supplierKey = supplierKeyFor(l)
    const label = supplierLabelFor(l)
    const existing = grouped.get(supplierKey)
    if (existing) {
      existing.lines.push(l)
      if (existing.supplierName == null && label) existing.supplierName = label
    } else {
      grouped.set(supplierKey, {
        lines: [l],
        supplierKey,
        supplierName: label ?? groupByKey.get(supplierKey)?.supplierName ?? null,
      })
    }
  }

  return (
    <div className="space-y-3">
      {/* Saved-state / autosave indicator (PR-RQ-AUTOSAVE). */}
      <div
        data-testid="draft-save-state"
        aria-live="polite"
        className={`flex items-center gap-1.5 text-[10px] font-[800] ${
          saveState === 'error' ? 'text-red-600'
            : saveState === 'saving' ? 'text-[#06539B]'
              : saveState === 'dirty' ? 'text-[#92400E]'
                : 'text-emerald-700'
        }`}
      >
        <span aria-hidden="true">
          {saveState === 'error' ? '⚠' : saveState === 'saving' ? '…' : saveState === 'dirty' ? '•' : '✓'}
        </span>
        {saveStateLabel()}
      </div>

      {/* Basis label. */}
      <div className="rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#06539B]">
            {isEs ? 'Base' : 'Basis'}
          </div>
          <p className="mt-1 text-[12px] font-[700] text-[#1C1A17]">
            {basisLabel(draft.basis, isEs)}
          </p>
        </div>
        <span className="text-[10px] font-[700] text-[#9E9890]">
          {draft.lines.length} {isEs ? 'líneas' : 'lines'}
        </span>
      </div>

      {/* The old global "Default request details" / "Datos
          generales por defecto" card was removed: the primary
          workflow now drives users straight to the per-supplier
          editor on each supplier group. The underlying
          MaterialRequestDraft.title / neededBy / deliveryLocation
          / notes columns stay on the row so the supplier-PDF
          route can still fall back to them when a supplier group
          has not set its own override. */}

      {/* Review-before-sending section (QA: explain why + offer actions, show decision state). */}
      {reviewLineEntries.length > 0 && (
        <div data-testid="draft-review-before-sending" className="bg-white border border-[#FDE68A] rounded-xl">
          <div className="px-4 py-2 border-b border-[#FDE68A] flex items-center justify-between gap-2">
            <div className="text-[11px] font-[800] text-[#92400E]">
              {isEs ? 'Revisar antes de enviar' : 'Review before sending'}
            </div>
            <span className="text-[10px] font-[700] text-[#9E9890]">
              {needsReviewLines.length}
            </span>
          </div>
          <div className="px-4 pt-2 text-[10px] font-[600] text-[#6B7280]">
            {isEs
              ? 'Elige qué partidas revisadas deben incluirse antes de descargar o aprobar la solicitud al proveedor.'
              : 'Choose which reviewed items should be included before downloading or approving the supplier request.'}
          </div>
          <div className="px-4 pt-1 text-[10px] font-[700] text-[#6B7280]">
            {isEs ? 'Tus decisiones de incluir/excluir se guardan en este borrador.' : 'Your include/exclude choices are saved to this draft.'}
          </div>
          <DraftLineTable
            lines={reviewLineEntries.map((e) => e.line)}
            isEs={isEs}
            onEdit={patchLine}
            onRemove={excludeReviewLine}
            reviewMode
            reviewDecisions={reviewDecisions}
            onInclude={includeReviewLine}
            onExclude={excludeReviewLine}
            onUndoDecision={undoReviewLine}
            onReviewInCompare={onReviewInCompare}
            onDirty={markDirty}
          />
        </div>
      )}

      {/* Included lines, grouped by supplier. */}
      <div className="bg-white border border-[#ECEAE4] rounded-xl">
        <div className="px-4 py-2 border-b border-[#ECEAE4] flex items-center justify-between gap-2">
          <div className="text-[11px] font-[800] text-[#1C1A17]">
            {isEs ? 'Borrador editable agrupado por proveedor' : 'Editable draft grouped by supplier'}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              data-testid="draft-preview-current-pdf"
              onClick={previewPdf}
              className="text-[11px] font-[800] px-2.5 py-1 rounded-full border border-[#9E9890] text-[#4B5563] hover:bg-[#FAFAF8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#9E9890]"
              aria-label={isEs ? 'Abrir / Vista previa del PDF' : 'Open / Preview PDF'}
              title={
                isEs
                  ? 'Abre el PDF del borrador actual en una pestaña nueva (no regenera). Desde ahí puedes imprimir.'
                  : 'Opens the current draft PDF in a new tab (does not regenerate). Print from there.'
              }
            >
              {isEs ? 'Abrir / Vista previa del PDF' : 'Open / Preview PDF'}
            </button>
            <button
              type="button"
              data-testid="draft-download-current-pdf"
              onClick={downloadPdf}
              className="text-[11px] font-[800] px-2.5 py-1 rounded-full border border-[#9E9890] text-[#4B5563] hover:bg-[#FAFAF8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#9E9890]"
              aria-label={
                isEs
                  ? 'Descargar PDF del borrador actual'
                  : 'Download current draft PDF'
              }
              title={
                isEs
                  ? 'Descarga el borrador actual tal como se muestra (no regenera). Para un solo proveedor usa el botón en su sección.'
                  : 'Downloads the current draft exactly as shown (does not regenerate). For a single supplier, use that supplier section button.'
              }
            >
              {isEs
                ? 'Descargar PDF del borrador actual'
                : 'Download current draft PDF'}
            </button>
          </div>
        </div>
        {grouped.size === 0 ? (
          <div className="px-4 py-6 text-center text-[11px] font-[700] text-[#9E9890]">
            {isEs ? 'Sin líneas incluidas todavía' : 'No included lines yet'}
          </div>
        ) : (
          Array.from(grouped.values()).map((bucket, idx) => {
            const supplierKey = bucket.supplierKey
            const supplierName = bucket.supplierName
            const groupStatus = groupStatusByKey.get(supplierKey) ?? 'DRAFT'
            const isApproved = groupStatus === 'APPROVED'
            // Pick the first concrete catalog Supplier.id we can
            // see in this group's lines so a per-group "Add manual
            // line" inherits it. Free-text-named groups have no
            // catalog id; supplierGroupKey alone keeps the new
            // line in the correct bucket.
            const supplierIdForGroup =
              bucket.lines.find((l) => l.supplier?.id)?.supplier?.id ?? null
            const persistedGroup = groupByKey.get(supplierKey)
            const manualForm = manualLineForms[supplierKey] ?? null
            const updateManualForm = (patch: Partial<ManualLineDraft>) => {
              markDirty() // typing a manual line shows "Unsaved changes" until it saves
              setManualLineForms((prev) => ({
                ...prev,
                [supplierKey]: { ...(prev[supplierKey] ?? { description: '', quantity: '', unit: '', notes: '' }), ...patch },
              }))
            }
            const openManualForm = () =>
              setManualLineForms((prev) => ({
                ...prev,
                [supplierKey]: prev[supplierKey] ?? { description: '', quantity: '', unit: '', notes: '' },
              }))
            const closeManualForm = () =>
              setManualLineForms((prev) => ({ ...prev, [supplierKey]: null }))
            const submitManualForm = async () => {
              if (!manualForm || !manualForm.description.trim()) return
              await addManualLineToGroup(supplierKey, supplierIdForGroup, {
                description: manualForm.description.trim(),
                quantity:
                  manualForm.quantity.trim() === '' ? null : Number(manualForm.quantity),
                unit: manualForm.unit.trim() || null,
                notes: manualForm.notes.trim() || null,
              })
              closeManualForm()
            }
            return (
              <div
                key={supplierKey || `unassigned-${idx}`}
                className="border-t first:border-t-0 border-[#F2F0EB]"
              >
                <div className="px-4 py-2 bg-[#FAFAF8] flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890] flex items-center gap-2">
                    <span>
                      {supplierName
                        ? `${isEs ? 'Proveedor' : 'Supplier'}: ${supplierName}`
                        : isEs
                          ? 'Proveedor sin asignar'
                          : 'Unassigned supplier'}
                    </span>
                    <span className="text-[9px] font-[700] text-[#9E9890]">
                      ({bucket.lines.length})
                    </span>
                    {isApproved ? (
                      <span className="text-[9px] font-[800] px-1.5 py-0.5 rounded-full bg-[#ECFDF5] text-[#047857]">
                        {isEs ? 'Aprobada para enviar' : 'Approved to send'}
                      </span>
                    ) : (
                      <span className="text-[9px] font-[800] px-1.5 py-0.5 rounded-full bg-[#FFFBEB] text-[#92400E]">
                        {isEs ? 'Borrador' : 'Draft'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => downloadSupplierPdf(supplierKey, supplierName)}
                      className="text-[10px] font-[800] px-2 py-0.5 rounded-full border border-[#06539B] text-[#06539B] hover:bg-[#EFF6FF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#06539B]"
                    >
                      {isEs
                        ? 'Descargar solicitud del proveedor (borrador PDF)'
                        : 'Download supplier request (draft PDF)'}
                    </button>
                    {!isApproved && (
                      <button
                        type="button"
                        onClick={() => approveSupplierGroup(supplierKey)}
                        className="text-[10px] font-[800] px-2 py-0.5 rounded-full border border-[#047857] text-[#047857] hover:bg-[#ECFDF5] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#047857]"
                      >
                        {isEs
                          ? 'Aprobar solicitud del proveedor'
                          : 'Approve supplier request'}
                      </button>
                    )}
                    {isApproved && (
                      <>
                        <button
                          type="button"
                          onClick={() => downloadSupplierPdf(supplierKey, supplierName)}
                          className="text-[10px] font-[800] px-2 py-0.5 rounded-full bg-[#047857] text-white hover:bg-[#065F46] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#047857]"
                        >
                          {isEs
                            ? 'Descargar solicitud aprobada del proveedor'
                            : 'Download approved supplier request'}
                        </button>
                        <button
                          type="button"
                          onClick={() => unapproveSupplierGroup(supplierKey)}
                          className="text-[10px] font-[700] text-[#9E9890] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#9E9890]"
                        >
                          {isEs ? 'Revertir a borrador' : 'Revert to draft'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {/* Per-supplier request details. Empty values fall
                    back to the draft-level defaults (still stored
                    on MaterialRequestDraft) on the supplier-
                    specific PDF, but the global editor card is no
                    longer rendered: users edit per supplier here. */}
                <details className="px-4 py-2 border-t border-[#F2F0EB] bg-white">
                  <summary className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#06539B] cursor-pointer">
                    {isEs
                      ? 'Datos de solicitud para este proveedor'
                      : 'Request details for this supplier'}
                  </summary>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">
                        {isEs ? 'Requerido para' : 'Needed by'}
                      </span>
                      <input
                        type="date"
                        defaultValue={
                          persistedGroup?.neededBy
                            ? persistedGroup.neededBy.slice(0, 10)
                            : ''
                        }
                        onBlur={(e) =>
                          patchSupplierGroup(supplierKey, {
                            neededBy: e.target.value ? e.target.value : null,
                          })
                        }
                        className="mt-1 w-full text-[12px] font-[600] text-[#1C1A17] px-2 py-1.5 rounded border border-[#ECEAE4] focus:outline-none focus:border-[#1D4ED8]"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">
                        {isEs ? 'Ubicación de entrega' : 'Delivery location'}
                      </span>
                      <input
                        type="text"
                        defaultValue={persistedGroup?.deliveryLocation ?? ''}
                        onBlur={(e) =>
                          patchSupplierGroup(supplierKey, {
                            deliveryLocation: e.target.value || null,
                          })
                        }
                        className="mt-1 w-full text-[12px] font-[600] text-[#1C1A17] px-2 py-1.5 rounded border border-[#ECEAE4] focus:outline-none focus:border-[#1D4ED8]"
                        placeholder={isEs ? 'Dirección de entrega' : 'Delivery address'}
                      />
                    </label>
                    <label className="block md:col-span-2">
                      <span className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">
                        {isEs ? 'Notas para este proveedor' : 'Notes for this supplier'}
                      </span>
                      <textarea
                        defaultValue={persistedGroup?.notes ?? ''}
                        onBlur={(e) =>
                          patchSupplierGroup(supplierKey, {
                            notes: e.target.value || null,
                          })
                        }
                        className="mt-1 w-full text-[12px] font-[600] text-[#1C1A17] px-2 py-1.5 rounded border border-[#ECEAE4] focus:outline-none focus:border-[#1D4ED8] h-16 resize-none"
                        placeholder={
                          isEs
                            ? 'Sobrescribe las notas por defecto'
                            : 'Overrides the default notes'
                        }
                      />
                    </label>
                  </div>
                </details>
                <DraftLineTable
                  lines={bucket.lines}
                  isEs={isEs}
                  onEdit={patchLine}
                  onRemove={removeLine}
                  onDirty={markDirty}
                  includedState
                />
                {/* Per-group manual line form. Replaces the old
                    top-level button: now every line we create has
                    an explicit supplierGroupKey so it lands in the
                    right bucket even when the group has no catalog
                    Supplier row. */}
                <div className="px-4 py-2 border-t border-[#F2F0EB] bg-white">
                  {manualForm === null ? (
                    <button
                      type="button"
                      onClick={openManualForm}
                      className="text-[10px] font-[800] px-2 py-0.5 rounded-full border border-[#1D4ED8] text-[#1D4ED8] hover:bg-[#EFF6FF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1D4ED8]"
                    >
                      + {isEs ? 'Agregar línea manual' : 'Add manual line'}
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                        <label className="block md:col-span-2">
                          <span className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">
                            {isEs ? 'Descripción' : 'Description'}
                          </span>
                          <input
                            type="text"
                            value={manualForm.description}
                            onChange={(e) => updateManualForm({ description: e.target.value })}
                            className="mt-1 w-full text-[12px] font-[600] text-[#1C1A17] px-2 py-1.5 rounded border border-[#ECEAE4] focus:outline-none focus:border-[#1D4ED8]"
                            autoFocus
                          />
                        </label>
                        <label className="block">
                          <span className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">
                            {isEs ? 'Cantidad' : 'Qty'}
                          </span>
                          <input
                            type="number"
                            step="any"
                            value={manualForm.quantity}
                            onChange={(e) => updateManualForm({ quantity: e.target.value })}
                            className="mt-1 w-full text-[12px] font-[600] text-[#1C1A17] px-2 py-1.5 rounded border border-[#ECEAE4] focus:outline-none focus:border-[#1D4ED8]"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">
                            {isEs ? 'Unidad' : 'Unit'}
                          </span>
                          <input
                            type="text"
                            value={manualForm.unit}
                            onChange={(e) => updateManualForm({ unit: e.target.value })}
                            className="mt-1 w-full text-[12px] font-[600] text-[#1C1A17] px-2 py-1.5 rounded border border-[#ECEAE4] focus:outline-none focus:border-[#1D4ED8]"
                          />
                        </label>
                        <label className="block md:col-span-4">
                          <span className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">
                            {isEs ? 'Notas' : 'Notes'}
                          </span>
                          <input
                            type="text"
                            value={manualForm.notes}
                            onChange={(e) => updateManualForm({ notes: e.target.value })}
                            className="mt-1 w-full text-[12px] font-[600] text-[#1C1A17] px-2 py-1.5 rounded border border-[#ECEAE4] focus:outline-none focus:border-[#1D4ED8]"
                            placeholder={isEs ? 'Opcional' : 'Optional'}
                          />
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={submitManualForm}
                          disabled={!manualForm.description.trim()}
                          className="text-[10px] font-[800] px-2 py-0.5 rounded-full bg-[#1D4ED8] text-white hover:bg-[#1E40AF] disabled:bg-[#9E9890] disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1D4ED8]"
                        >
                          {isEs ? 'Guardar línea' : 'Save line'}
                        </button>
                        <button
                          type="button"
                          onClick={closeManualForm}
                          className="text-[10px] font-[700] text-[#9E9890] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#9E9890]"
                        >
                          {isEs ? 'Cancelar' : 'Cancel'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Missing from baseline. */}
      {missingFromBaseline.length > 0 && draft.basis === 'PROJECT_TAKEOFF_BASELINE' && (
        <div className="bg-white border border-[#ECEAE4] rounded-xl">
          <div className="px-4 py-2 border-b border-[#ECEAE4] flex items-center justify-between">
            <div className="text-[11px] font-[800] text-[#1C1A17]">
              {isEs ? 'Falta cotizar' : 'Needs quote'}
            </div>
            <span className="text-[10px] font-[700] text-[#9E9890]">
              {missingFromBaseline.length}
            </span>
          </div>
          <DraftLineTable
            lines={missingFromBaseline}
            isEs={isEs}
            onEdit={patchLine}
            onRemove={removeLine}
            onDirty={markDirty}
          />
        </div>
      )}

      {/* Deleted lines (QA): soft-removed lines, recoverable via Restore. They
          never appear in the supplier request PDF until restored. */}
      {removedLines.length > 0 && (
        <div data-testid="draft-deleted-lines" className="bg-white border border-[#ECEAE4] rounded-xl">
          <div className="px-4 py-2 border-b border-[#ECEAE4] flex items-center justify-between">
            <div className="text-[11px] font-[800] text-[#9E9890]">
              {isEs ? 'Líneas eliminadas' : 'Deleted lines'}
            </div>
            <span className="text-[10px] font-[700] text-[#9E9890]">{removedLines.length}</span>
          </div>
          <div className="divide-y divide-[#F2F0EB]">
            {removedLines.map((l) => (
              <div key={l.id} className="px-4 py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] font-[700] text-[#6B6660] line-through truncate">{l.description}</div>
                  <div className="text-[9.5px] font-[600] text-[#9E9890]">
                    {l.quantity != null ? `${l.quantity} ` : ''}{displayUnit(l.unit, isEs)}
                    {typeof l.unitPrice === 'number' ? ` · $${l.unitPrice.toFixed(2)}` : ''}
                    {(l.supplier?.name || l.supplierQuote?.supplierName) ? ` · ${l.supplier?.name || l.supplierQuote?.supplierName}` : ''}
                    {l.reason ? ` · ${draftReasonLabel(l.reason, isEs)}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="draft-line-restore"
                  onClick={() => restoreLine(l.id)}
                  className="shrink-0 text-[10px] font-[800] text-[#06539B] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#06539B]"
                >
                  {isEs ? 'Restaurar' : 'Restore'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function DraftLineTable({
  lines,
  isEs,
  onEdit,
  onRemove,
  reviewMode,
  reviewDecisions,
  onInclude,
  onExclude,
  onUndoDecision,
  onReviewInCompare,
  onDirty,
  includedState,
}: {
  lines: MaterialRequestDraftLine[]
  isEs: boolean
  onEdit: (lineId: string, body: Record<string, unknown>) => Promise<void>
  onRemove: (lineId: string) => Promise<void>
  // Review-before-sending mode (QA): show recommended action + Include /
  // Exclude / Review-in-Compare-Prices actions, and a decision pill + Undo
  // once the user has chosen.
  reviewMode?: boolean
  reviewDecisions?: Record<string, 'INCLUDED' | 'EXCLUDED'>
  onInclude?: (lineId: string) => Promise<void>
  onExclude?: (lineId: string) => Promise<void>
  onUndoDecision?: (lineId: string) => Promise<void>
  onReviewInCompare?: () => void
  // Fired while the user is editing a field (before onBlur saves) so the
  // parent can show an "Unsaved changes" state until the save completes.
  onDirty?: () => void
  // Included supplier-group rows (QA #2): show an "Included" status chip next
  // to Remove so the user can tell these are already in the request.
  includedState?: boolean
}) {
  return (
    <table className="w-full text-[11px]">
      <thead className="bg-[#FAFAF8] border-b border-[#ECEAE4]">
        <tr>
          <th className="text-left px-3 py-2 font-[800] text-[#9E9890]">
            {isEs ? 'Descripción' : 'Description'}
          </th>
          <th className="text-left px-2 py-2 font-[800] text-[#9E9890]">
            {isEs ? 'Cantidad' : 'Qty'}
          </th>
          <th className="text-left px-2 py-2 font-[800] text-[#9E9890]">
            {isEs ? 'Unidad' : 'Unit'}
          </th>
          <th className="text-right px-2 py-2 font-[800] text-[#9E9890]">
            {isEs ? 'Precio' : 'Unit price'}
          </th>
          <th className="text-left px-2 py-2 font-[800] text-[#9E9890]">
            {isEs ? 'Notas' : 'Notes'}
          </th>
          <th className="px-2 py-2"></th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#F2F0EB]">
        {lines.map((l) => (
          <tr key={l.id}>
            <td className="px-3 py-2 font-[700] text-[#1C1A17]">
              <input
                type="text"
                defaultValue={l.description}
                onChange={() => onDirty?.()}
                onBlur={(e) =>
                  e.target.value.trim() && onEdit(l.id, { description: e.target.value.trim() })
                }
                className="w-full text-[11px] font-[700] text-[#1C1A17] px-1 py-0.5 rounded border border-transparent hover:border-[#ECEAE4] focus:outline-none focus:border-[#1D4ED8]"
              />
              {l.reason && (
                <div data-testid="draft-line-reason" className="text-[9px] font-[600] text-[#9E9890] mt-0.5">{draftReasonLabel(l.reason, isEs)}</div>
              )}
              {reviewMode && l.reason && (
                <div data-testid="draft-line-recommended" className="text-[9px] font-[800] text-[#92400E] mt-0.5">
                  {(isEs ? 'Recomendación: ' : 'Recommended: ')}{draftRecommendedAction(l.reason, isEs)}
                </div>
              )}
            </td>
            <td className="px-2 py-2">
              <input
                type="number"
                step="any"
                defaultValue={l.quantity ?? ''}
                onChange={() => onDirty?.()}
                onBlur={(e) =>
                  onEdit(l.id, {
                    quantity: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                className="w-20 text-[11px] font-[700] text-[#1C1A17] px-1 py-0.5 rounded border border-transparent hover:border-[#ECEAE4] focus:outline-none focus:border-[#1D4ED8]"
              />
            </td>
            <td className="px-2 py-2">
              {/* Spanish shows "pza" for each-like units (display-only). The
                  guarded onBlur only saves when the user actually changes the
                  shown value, so the raw stored unit (e.g. "ea") is never
                  mutated by the localized display. */}
              <input
                type="text"
                defaultValue={displayUnit(l.unit, isEs)}
                onChange={() => onDirty?.()}
                onBlur={(e) => {
                  const v = e.target.value
                  if (v !== displayUnit(l.unit, isEs)) onEdit(l.id, { unit: v || null })
                }}
                className="w-16 text-[11px] font-[700] text-[#1C1A17] px-1 py-0.5 rounded border border-transparent hover:border-[#ECEAE4] focus:outline-none focus:border-[#1D4ED8]"
              />
            </td>
            <td className="px-2 py-2 text-right">
              <input
                type="number"
                step="any"
                defaultValue={l.unitPrice ?? ''}
                onChange={() => onDirty?.()}
                onBlur={(e) =>
                  onEdit(l.id, {
                    unitPrice: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                className="w-24 text-right text-[11px] font-[700] text-[#1C1A17] px-1 py-0.5 rounded border border-transparent hover:border-[#ECEAE4] focus:outline-none focus:border-[#1D4ED8]"
              />
            </td>
            <td className="px-2 py-2">
              <input
                type="text"
                defaultValue={l.notes ?? ''}
                onChange={() => onDirty?.()}
                onBlur={(e) => onEdit(l.id, { notes: e.target.value || null })}
                className="w-full text-[11px] font-[600] text-[#4B5563] px-1 py-0.5 rounded border border-transparent hover:border-[#ECEAE4] focus:outline-none focus:border-[#1D4ED8]"
                placeholder={isEs ? 'Opcional' : 'Optional'}
              />
            </td>
            <td className="px-2 py-2 text-right">
              {reviewMode ? (
                reviewDecisions?.[l.id] ? (
                  // Decided: show the visible Included/Excluded state + Undo.
                  <div className="flex flex-col items-end gap-1">
                    {reviewDecisions[l.id] === 'INCLUDED' ? (
                      <span data-testid="draft-line-state-included" className="text-[10px] font-[800] text-emerald-700">
                        ✓ {isEs ? 'Incluido' : 'Included'}
                      </span>
                    ) : (
                      <span data-testid="draft-line-state-excluded" className="text-[10px] font-[800] text-[#9E9890]">
                        ✕ {isEs ? 'Excluido' : 'Excluded'}
                      </span>
                    )}
                    {onUndoDecision && (
                      <button
                        type="button"
                        data-testid="draft-line-undo"
                        onClick={() => onUndoDecision(l.id)}
                        className="text-[10px] font-[800] text-[#06539B] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#06539B]"
                      >
                        {isEs ? 'Deshacer' : 'Undo'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-end gap-1">
                    {onInclude && (
                      <button
                        type="button"
                        data-testid="draft-line-include"
                        onClick={() => onInclude(l.id)}
                        className="text-[10px] font-[800] text-emerald-700 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-700"
                      >
                        {isEs ? 'Incluir en solicitud' : 'Include in request'}
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid="draft-line-exclude"
                      onClick={() => (onExclude ? onExclude(l.id) : onRemove(l.id))}
                      className="text-[10px] font-[800] text-red-600 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600"
                    >
                      {isEs ? 'Excluir de solicitud' : 'Exclude from request'}
                    </button>
                    {onReviewInCompare && (
                      <button
                        type="button"
                        data-testid="draft-line-review-compare"
                        onClick={onReviewInCompare}
                        className="text-[10px] font-[800] text-[#06539B] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#06539B]"
                      >
                        {isEs ? 'Revisar en Comparar precios' : 'Review in Compare Prices'}
                      </button>
                    )}
                  </div>
                )
              ) : (
                <div className="flex flex-col items-end gap-1">
                  {includedState && (
                    <span data-testid="draft-line-included-chip" className="text-[10px] font-[800] text-emerald-700">
                      ✓ {isEs ? 'Incluido' : 'Included'}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => onRemove(l.id)}
                    aria-label={isEs ? 'Eliminar línea' : 'Remove line'}
                    className="text-[10px] font-[800] text-red-600 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600"
                  >
                    {isEs ? 'Eliminar' : 'Remove'}
                  </button>
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
